import { Fragment, useState, useEffect, useCallback, useRef } from 'react';
import useToolbarResponsive from '../../hooks/useToolbarResponsive';
import useAlertHighlight from '../../hooks/useAlertHighlight';
import AlertHighlightChip from '../../components/AlertHighlightChip';
import MonthRangePicker from '../../components/MonthRangePicker';
import {
  FileText, Search, Filter, Upload, Plus, Eye, Delete,
  DollarSign, AlertTriangle, CheckCircle, Clock, X, Check,
  ChevronLeft, ChevronRight, Calendar, XCircle, RefreshCw, Loader,
  GripVertical, Settings2, Settings, ArrowUp, ArrowDown, ArrowUpDown,
  Download, ChevronDown, CheckSquare, Square, Tag, Tags,
  AlertCircle, FileWarning, FileSpreadsheet, Send, Sparkles, FileInput,
  Receipt, Wallet, Building2
} from 'lucide-react';
import dayjs from 'dayjs';
import toast from 'react-hot-toast';
import clsx from 'clsx';
import { gastosApi, parametrosApi } from '../../utils/api';
import useAuthStore from '../../store/authStore';
import usePageMetaStore from '../../store/pageMetaStore';
import ConfirmModal from '../../components/ConfirmModal';
import ProcesoMinimizado, { BotonMinimizar, HUECO_PROCESOS } from '../../components/ProcesoMinimizado';
import PagadoPorModal from '../../components/gastos/PagadoPorModal';
import CorreoProveedorModal from '../../components/gastos/CorreoProveedorModal';
import DraggableModal from '../../components/DraggableModal';
import NotificarPagoProveedorModal from '../../components/NotificarPagoProveedorModal';

import useFiltroPeriodo from '../../hooks/useFiltroPeriodo';
import ImportacionMultipleModal from '../../components/gastos/ImportacionMultipleModal';
// Toolbar button component (same style as CrearFactura)
function ToolbarButton({ icon: Icon, label, onClick, disabled, active, variant = 'default', badge }) {
  const baseClasses = "flex flex-col items-center justify-center px-3 py-2 rounded-lg transition-all text-xs font-medium min-w-[70px]";
  const variants = {
    default: "border border-gray-300 text-gray-700 hover:bg-gray-50 hover:border-gray-400",
    primary: "border border-blue-400 text-blue-600 hover:bg-blue-50 hover:border-blue-500",
    warning: "border border-amber-400 text-amber-600 hover:bg-amber-50 hover:border-amber-500",
    success: "border border-green-400 text-green-600 hover:bg-green-50 hover:border-green-500",
    danger: "border border-red-400 text-red-500 hover:bg-red-50 hover:border-red-500",
    dangerOutline: "border border-gray-400 text-red-500 hover:bg-red-50 hover:border-gray-500",
    successOutline: "border border-gray-400 text-green-700 hover:bg-green-50 hover:border-gray-500",
    indigoOutline: "border border-gray-400 text-indigo-600 hover:bg-indigo-50 hover:border-gray-500",
    purple: "border border-purple-400 text-purple-600 hover:bg-purple-50 hover:border-purple-500",
    indigo: "border border-indigo-400 text-indigo-600 hover:bg-indigo-50 hover:border-indigo-500",
    cyan: "border border-cyan-400 text-cyan-600 hover:bg-cyan-50 hover:border-cyan-500",
    teal: "border border-teal-400 text-teal-600 hover:bg-teal-50 hover:border-teal-500",
    slate: "border border-slate-400 text-slate-600 hover:bg-slate-50 hover:border-slate-500",
    rose: "border border-rose-400 text-rose-600 hover:bg-rose-50 hover:border-rose-500",
    emerald: "border border-emerald-400 text-emerald-600 hover:bg-emerald-50 hover:border-emerald-500",
    orange: "border border-orange-400 text-orange-600 hover:bg-orange-50 hover:border-orange-500",
  };

  const activeStyles = {
    primary: 'bg-blue-50 border-blue-500 border-2',
    success: 'bg-green-50 border-green-500 border-2',
    danger: 'bg-red-50 border-red-500 border-2',
    default: 'bg-gray-100 border-gray-400',
  };

  return (
    <button
      onClick={onClick}
      disabled={disabled}
      className={`${baseClasses} ${variants[variant]} ${active ? activeStyles[variant] || activeStyles.default : ''} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} relative`}
    >
      <Icon className="h-5 w-5 mb-1" />
      <span className="whitespace-nowrap">{label}</span>
      {badge && (
        <span className="absolute -top-1 -right-1 bg-blue-600 text-white text-xs rounded-full w-5 h-5 flex items-center justify-center">
          {badge}
        </span>
      )}
    </button>
  );
}

// Separator for toolbar
function ToolbarSeparator() {
  return <div className="w-px h-12 bg-gray-300 mx-1" />;
}

// Configuración de columnas disponibles con visibilidad toggle
// Orden: Fecha, Tipo, Proveedor, No.Doc, Término, Divisa, Subtotal, Descuento, Impuesto, Total, TC, Base CRC, IVA CRC, ...
const COLUMN_CONFIG = {
  fecha: { label: 'Fecha', default: true, fixed: true },
  tipoDoc: { label: 'Tipo Doc.', default: true },
  proveedor: { label: 'Proveedor', default: true, fixed: true },
  numero: { label: 'No. Documento', default: true, fixed: true },
  termino: { label: 'Término', default: true },
  divisa: { label: 'Divisa', default: true },
  subtotal: { label: 'Subtotal', default: true },
  descuento: { label: 'Descuento', default: false },
  impuesto: { label: 'Impuesto', default: true },
  total: { label: 'Total', default: true, fixed: true },
  tipoCambio: { label: 'T/C', default: false },
  baseCRC: { label: 'Base CRC', default: false },
  ivaCRC: { label: 'IVA CRC', default: false },
  tarifa: { label: '% Imp.', default: false },
  pagado: { label: 'Pagado', default: true, porPagarOnly: true },
  saldo: { label: 'Saldo', default: true, porPagarOnly: true },
  estadoPago: { label: 'Estado Pago', default: true, porPagarOnly: true },
  estado: { label: 'Estado Hacienda', default: true, notPorPagar: true },
  cumplimiento: { label: 'Cumplimiento', default: true, porConfirmarOnly: true },
  cabys: { label: 'CABYS', default: false },
  confirmadoPor: { label: 'Confirmado por', default: false, aceptadaOnly: true },
  confirmadoAt: { label: 'Fecha Confirmación', default: false, aceptadaOnly: true },
};

// Obtener columnas visibles de localStorage o usar defaults
// Merge con defaults para asegurar que nuevas columnas siempre aparezcan
const getDefaultColumns = (estado) => {
  const storageKey = `facturasRecibidas_columns_${estado}`;
  const defaults = Object.fromEntries(
    Object.entries(COLUMN_CONFIG).map(([key, config]) => [key, config.default])
  );

  const saved = localStorage.getItem(storageKey);
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      // Merge: usar saved values pero agregar nuevas columnas con su default
      return { ...defaults, ...parsed };
    } catch {
      return defaults;
    }
  }
  return defaults;
};

// Para compatibilidad con drag-and-drop existente
const ALL_COLUMNS = Object.entries(COLUMN_CONFIG).map(([id, config]) => ({ id, label: config.label }));

// Calcular estado de vencimiento para facturas por pagar
const getEstadoVencimiento = (factura) => {
  // Si ya está pagado
  if (factura.estado === 'pagado') {
    return { texto: 'Pagado', clase: 'bg-green-100 text-green-800', icon: CheckCircle };
  }
  // Si tiene pago parcial
  if (factura.estado === 'parcial') {
    const porcentaje = factura.totalPagado && factura.totalComprobante
      ? Math.round((factura.totalPagado / factura.totalComprobante) * 100)
      : 0;
    return { texto: `Parcial ${porcentaje}%`, clase: 'bg-purple-100 text-purple-800', icon: DollarSign };
  }
  // Si está anulado
  if (factura.estado === 'anulado') {
    return { texto: 'Anulado', clase: 'bg-gray-100 text-gray-800', icon: X };
  }

  // Calcular días para vencimiento
  const fechaVencimiento = factura.fechaVencimiento
    ? dayjs(factura.fechaVencimiento)
    : dayjs(factura.fechaEmision).add(30, 'day'); // Default 30 días

  const hoy = dayjs().startOf('day');
  const vence = fechaVencimiento.startOf('day');
  const dias = vence.diff(hoy, 'day');

  if (dias > 10) {
    return { texto: `Vence ${dias}d`, clase: 'border border-green-300 text-green-700' };
  } else if (dias > 1) {
    return { texto: `Vence ${dias}d`, clase: 'border border-yellow-300 text-yellow-700' };
  } else if (dias === 1) {
    return { texto: 'Mañana', clase: 'border border-orange-300 text-orange-700' };
  } else if (dias === 0) {
    return { texto: 'Hoy', clase: 'font-medium border border-red-300 text-red-700' };
  } else {
    return { texto: `Venció ${Math.abs(dias)}d`, clase: 'font-medium border border-red-400 text-red-700' };
  }
};

const estadoConfig = {
  por_confirmar: { label: 'Por Confirmar', color: 'bg-orange-100 text-orange-800', icon: Clock },
  // Nuestro mensaje ya está en Hacienda esperando respuesta: no se vuelve a confirmar (2-nov-2026)
  en_hacienda: { label: 'En Hacienda', color: 'bg-blue-100 text-blue-800 animate-pulse', icon: Clock },
  procesando_hacienda: { label: 'Procesando', color: 'bg-blue-100 text-blue-800', icon: Clock },
  procesando_rechazo: { label: 'Rechazando', color: 'bg-red-100 text-red-800', icon: Clock },
  aceptada: { label: 'Aceptada', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  rechazada: { label: 'Rechazada', color: 'bg-red-100 text-red-800', icon: XCircle },
  // La FE del PROVEEDOR la rechazó Hacienda: no existe para Hacienda y no se puede confirmar (31-oct-2026)
  rechazada_hacienda: { label: 'Rechazada por Hacienda', color: 'bg-red-100 text-red-800', icon: XCircle },
  por_pagar: { label: 'Por Pagar', color: 'bg-yellow-100 text-yellow-800', icon: Clock },
  parcial: { label: 'Parcial', color: 'bg-purple-100 text-purple-800', icon: DollarSign },
  pagado: { label: 'Pagado', color: 'bg-green-100 text-green-800', icon: CheckCircle },
  cancelada_nc: { label: 'Cancelada NC', color: 'bg-indigo-100 text-indigo-800', icon: FileInput },
  anulado: { label: 'Anulado', color: 'bg-gray-100 text-gray-800', icon: X }
};

const viewConfig = {
  por_confirmar: {
    title: 'Facturas Por Confirmar',
    subtitle: 'Facturas recibidas pendientes de aceptación',
    showUploadXML: true,   // FE requiere confirmación con Hacienda
    showUploadPDF: false,
    showAcceptReject: true,
    showPago: false
  },
  aceptada: {
    title: 'Documentos Aceptados',
    subtitle: 'Documentos confirmados - listos para pago',
    showUploadXML: false,
    showUploadPDF: false,
    showAcceptReject: false,
    showPago: false
  },
  por_pagar: {
    title: 'Facturas por Pagar',
    subtitle: 'Gestión de cuentas por pagar',
    showUploadXML: false,
    showUploadPDF: true,   // PDF no requiere Hacienda
    showAcceptReject: false,
    showPago: true
  }
};

export default function FacturasRecibidas({ estado = 'por_pagar' }) {
  const config = viewConfig[estado] || viewConfig.por_pagar;

  // Permissions
  //
  // `currentEmisor` lo pide el modal de notificación de pago al proveedor, abajo.
  // Faltaba, y no era un detalle: leer un identificador NO DECLARADO en JS lanza
  // `ReferenceError` —no devuelve `undefined`—, así que al aplicar un pago en
  // Cuentas por Pagar se caía el render entero y la pantalla quedaba en blanco.
  // Las otras dos pantallas que abren el mismo modal (PendientesPorProveedor e
  // HistoricoPagos) sí lo sacaban de acá; a esta le copiaron el bloque JSX sin
  // la línea del hook.
  const { user, currentEmisor } = useAuthStore();
  const isAdmin = user?.role === 'admin';

  const hasPermission = (modulo, accion) => {
    if (isAdmin) return true;
    return user?.permisos?.some(p => p.modulo === modulo && p.accion === accion);
  };

  const canCreate = hasPermission('cxp', 'crear');
  const canEdit = hasPermission('cxp', 'editar');
  const canDelete = hasPermission('cxp', 'eliminar');

  const [facturas, setFacturas] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastUpdated, setLastUpdated] = useState(null);
  // Publicar "Actualizado: HH:MM" en el header global (al lado del título de la página)
  const setPageLastUpdated = usePageMetaStore(s => s.setLastUpdated);
  // Ref del header de la tabla (para anclar verticalmente el chip de alerta flotante)
  const tableHeaderRef = useRef(null);

  // Highlight desde alertas inteligentes del dashboard (?highlight=vencidas|por_vencer_7d).
  // Aplica solo en estado=por_pagar (las CxP activas).
  const { isHighlighted, animOn, highlightedRef, hasMatches, matchedIds, clearHighlight, highlightKey } = useAlertHighlight({
    items: facturas,
    getId: (f) => f.id,
    matcher: {
      vencidas: (f) => f.fechaVencimiento && new Date(f.fechaVencimiento) < new Date() && ['por_pagar', 'parcial'].includes(f.estadoPago),
      por_vencer_7d: (f) => {
        if (!f.fechaVencimiento || !['por_pagar', 'parcial'].includes(f.estadoPago)) return false;
        const venc = new Date(f.fechaVencimiento);
        const hoy = new Date(); hoy.setHours(0,0,0,0);
        const en7 = new Date(hoy.getTime() + 7 * 86400000);
        return venc >= hoy && venc <= en7;
      }
    }
  });
  // En Gastos → Aceptadas pedimos todas las FE (sin paginación) porque la lista
  // usa scroll interno y los totales ya están en las Summary Cards de arriba.
  // Sin paginación: la tabla tiene scroll interno (sticky thead/tfoot). El filtro de
  // fecha controla cuántas FE se traen. 10000 cubre cualquier período razonable.
  const [pagination, setPagination] = useState({ page: 1, limit: 10000, total: 0, pages: 0 });
  const [autoRefresh, setAutoRefresh] = useState(() => {
    const saved = localStorage.getItem('facturasRecibidas_autoRefresh');
    return saved !== null ? JSON.parse(saved) : true;
  });
  const [exporting, setExporting] = useState(false);

  // Column order (draggable)
  const [columns, setColumns] = useState(() => {
    const defaultOrder = ALL_COLUMNS.map(c => c.id);
    const saved = localStorage.getItem('facturasRecibidas_columns');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        // Si hay columnas nuevas, usar orden default para mantener posición correcta
        const newCols = defaultOrder.filter(id => !parsed.includes(id));
        if (newCols.length > 0) {
          // Usar orden default para asegurar posición correcta de nuevas columnas
          return defaultOrder;
        }
        return parsed;
      } catch {
        return defaultOrder;
      }
    }
    return defaultOrder;
  });
  const [draggedColumn, setDraggedColumn] = useState(null);

  // Toolbar responsive: hook que devuelve refs para search & buttons + maneja overlap.

  // Column visibility toggle (like HistorialFacturas)
  const [columnVisibility, setColumnVisibility] = useState(() => getDefaultColumns(estado));
  const [showColumnSelector, setShowColumnSelector] = useState(false);

  // Toggle column visibility
  const toggleColumn = (columnKey) => {
    if (COLUMN_CONFIG[columnKey]?.fixed) return;
    setColumnVisibility(prev => {
      const updated = { ...prev, [columnKey]: !prev[columnKey] };
      localStorage.setItem(`facturasRecibidas_columns_${estado}`, JSON.stringify(updated));
      return updated;
    });
  };

  // Filter columns based on view and visibility
  const visibleColumns = columns.filter(colId => {
    const config = COLUMN_CONFIG[colId];
    if (!config) return false;

    // Check visibility toggle
    if (!columnVisibility[colId]) return false;

    // View-specific filters
    if (estado === 'por_pagar') {
      return !config.notPorPagar && !config.porConfirmarOnly; // Hide Estado Hacienda in por_pagar view
    } else {
      if (config.porPagarOnly) return false;
      if (config.porConfirmarOnly && estado !== 'por_confirmar') return false;
      if (config.aceptadaOnly && estado !== 'aceptada') return false;
      return true;
    }
  });

  // Sorting
  const [sortColumn, setSortColumn] = useState('fecha');
  const [sortDirection, setSortDirection] = useState('desc'); // 'asc' | 'desc'

  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [estadoFilter, setEstadoFilter] = useState('');
  // Filtro de período unificado (MonthRangePicker). Default: vacío (todas) excepto en "aceptada" que arranca con mes actual.
  const buildMesActualFiltro = () => {
    const n = new Date();
    const y = n.getFullYear();
    const m = String(n.getMonth() + 1).padStart(2, '0');
    const last = new Date(y, n.getMonth() + 1, 0).getDate();
    return { modo: 'mes', periodo: `${y}-${m}`, desde: `${y}-${m}-01`, hasta: `${y}-${m}-${String(last).padStart(2, '0')}` };
  };
  const buildSinFiltro = () => ({ modo: 'rango', desde: '', hasta: '' });
  const [filtroFecha, setFiltroFecha] = useFiltroPeriodo(`FacturasRecibidas-${estado}`, () => estado === 'aceptada' ? buildMesActualFiltro() : buildSinFiltro());
  const [showFilters, setShowFilters] = useState(false);

  // Modals
  const [showUploadXMLModal, setShowUploadXMLModal] = useState(false);
  const [showImportacionMultiple, setShowImportacionMultiple] = useState(false);
  const [showUploadPDFModal, setShowUploadPDFModal] = useState(false);
  const [showPagoModal, setShowPagoModal] = useState(false);
  // Notificación de pago al proveedor (se abre automáticamente tras aplicar pago en CxP)
  const [notifPagoData, setNotifPagoData] = useState(null);
  const [showAceptarModal, setShowAceptarModal] = useState(false);
  const [showRechazarModal, setShowRechazarModal] = useState(false);
  const [showBatchAceptarModal, setShowBatchAceptarModal] = useState(false);
  const [cumplimientoFactura, setCumplimientoFactura] = useState(null); // detalle de la revisión con las reglas
  const [showAplicarNCModal, setShowAplicarNCModal] = useState(false);
  const [showPreviewModal, setShowPreviewModal] = useState(false);
  const [selectedFactura, setSelectedFactura] = useState(null);
  const [mensajeHaciendaModal, setMensajeHaciendaModal] = useState(null);
  const [confirmModal, setConfirmModal] = useState(null); // { title, message, onConfirm, confirmText, confirmColor }
  const [facturaDetalle, setFacturaDetalle] = useState(null);
  const [loadingDetalle, setLoadingDetalle] = useState(false);

  // Selection for por_pagar view (simplified)
  const [selectedFacturas, setSelectedFacturas] = useState([]);

  // Batch selection mode (for por_confirmar)
  const [batchSelected, setBatchSelected] = useState([]);
  const [batchProcessing, setBatchProcessing] = useState(false);
  const [showBatchRechazarModal, setShowBatchRechazarModal] = useState(false);
  const [showDownloadMenu, setShowDownloadMenu] = useState(false);
  const downloadMenuRef = useRef(null);

  // Corregir condición modal
  const [corregirModal, setCorregirModal] = useState(null); // { factura, nueva }
  const [corrigiendo, setCorrigiendo] = useState(false);
  // Quién pagó una compra de contado: la empresa (banco) o un empleado (se reintegra en planilla)
  const [pagadoPorModal, setPagadoPorModal] = useState(null); // { factura, pagadoPor, empleadoNombre, porcentajeEmpresa }

  // Batch selection for aceptada (categorization)
  const [categorizacionSelected, setCategorizacionSelected] = useState([]);
  const [showCategorizacionModal, setShowCategorizacionModal] = useState(false); // Modal múltiples
  const [showCategorizacionDetalleModal, setShowCategorizacionDetalleModal] = useState(false); // Modal individual
  const [categorizacionLoading, setCategorizacionLoading] = useState(false);
  const [categorias, setCategorias] = useState(null);
  const [categorizacionForm, setCategorizacionForm] = useState({
    tipo_movimiento: '01',
    clase: 'S',
    identificacion: 'E',
    tarifa_iva_categoria: '13'
  });
  // Single invoice categorization
  const [facturaCategorizacion, setFacturaCategorizacion] = useState(null);
  const [lineasCategorizacion, setLineasCategorizacion] = useState([]);
  const [lineasLoading, setLineasLoading] = useState(false);
  const [editingLineaId, setEditingLineaId] = useState(null);

  // Bancos para modal de pago
  const [bancos, setBancos] = useState([]);

  // Cargar bancos
  useEffect(() => {
    const loadBancos = async () => {
      try {
        const response = await parametrosApi.listBancosActivos();
        setBancos(response.data.data || []);
      } catch (error) {
        console.error('Error loading bancos:', error);
      }
    };
    loadBancos();
  }, []);

  const { searchRef: searchToolbarRef, buttonsRef: toolbarButtonsRef } = useToolbarResponsive(
    [estado, filtroFecha, categorizacionSelected.length, batchSelected.length, selectedFacturas.length]
  );

  // Close download menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (downloadMenuRef.current && !downloadMenuRef.current.contains(event.target)) {
        setShowDownloadMenu(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const fetchFacturas = useCallback(async (opciones) => {
    try {
      if (!opciones?.silencioso) setLoading(true);

      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...(searchTerm && { search: searchTerm }),
        estado: estadoFilter || estado, // Use prop estado if no filter selected
        ...(filtroFecha?.desde && { fechaDesde: filtroFecha.desde }),
        ...(filtroFecha?.hasta && { fechaHasta: filtroFecha.hasta })
      };

      const [facturasRes, summaryRes] = await Promise.all([
        gastosApi.listFacturas(params),
        gastosApi.getFacturasSummary()
      ]);

      setFacturas(facturasRes.data.data || []);
      setPagination(prev => ({ ...prev, ...facturasRes.data.pagination }));
      setSummary(summaryRes.data.data);
      const now = new Date();
      setLastUpdated(now);
      setPageLastUpdated(now);  // publicar en el header global
    } catch (error) {
      console.error('Error fetching facturas:', error);
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  }, [pagination.page, pagination.limit, searchTerm, estadoFilter, estado, filtroFecha]);

  useEffect(() => {
    fetchFacturas();
  }, [fetchFacturas]);

  // Facturas con nuestro mensaje ya en Hacienda: aunque se haya refrescado la pantalla, se ven en la
  // píldora y la lista se pone al día sola cada 15 s hasta que Hacienda conteste.
  const esperandoHacienda = estado === 'por_confirmar' ? facturas.filter(enHacienda) : [];
  const [verEnHacienda, setVerEnHacienda] = useState(false);
  const cuantosEnHacienda = useRef(0);
  useEffect(() => {
    if (cuantosEnHacienda.current > esperandoHacienda.length && esperandoHacienda.length === 0) toast.success('Hacienda ya contestó las facturas que estaban en proceso');
    cuantosEnHacienda.current = esperandoHacienda.length;
    if (!esperandoHacienda.length) { setVerEnHacienda(false); return undefined; }
    const t = setInterval(() => fetchFacturas({ silencioso: true }), 15000);
    return () => clearInterval(t);
  }, [esperandoHacienda.length, fetchFacturas]);

  // Reset state and refetch when estado prop changes (navigation between submodules).
  // Al montar (o recargar con F5) no se toca el período: se conserva el que el usuario tenía.
  const estadoAnteriorRef = useRef(estado);
  useEffect(() => {
    if (estadoAnteriorRef.current === estado) return;
    estadoAnteriorRef.current = estado;
    setFacturas([]);
    setEstadoFilter('');
    setFiltroFecha(estado === 'aceptada' ? buildMesActualFiltro() : buildSinFiltro());
    setPagination(prev => ({ ...prev, page: 1 }));
  }, [estado]);

  // Auto-refresh every 30 seconds (only when there are invoices in "Procesando" state)
  const hasProcessingInvoices = facturas.some(f =>
    ['procesando_hacienda', 'procesando_rechazo'].includes(f.estado)
  );

  useEffect(() => {
    // Only auto-refresh if enabled AND there are invoices being processed
    if (!autoRefresh || !hasProcessingInvoices) return;

    const interval = setInterval(() => {
      fetchFacturas();
    }, 30000);

    return () => clearInterval(interval);
  }, [fetchFacturas, autoRefresh, hasProcessingInvoices]);

  const toggleAutoRefresh = () => {
    const newValue = !autoRefresh;
    setAutoRefresh(newValue);
    localStorage.setItem('facturasRecibidas_autoRefresh', JSON.stringify(newValue));
  };

  // Drag & Drop handlers for columns
  const handleDragStart = (e, columnId) => {
    setDraggedColumn(columnId);
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e, columnId) => {
    e.preventDefault();
    if (draggedColumn === columnId) return;

    const newColumns = [...columns];
    const draggedIndex = newColumns.indexOf(draggedColumn);
    const targetIndex = newColumns.indexOf(columnId);

    newColumns.splice(draggedIndex, 1);
    newColumns.splice(targetIndex, 0, draggedColumn);

    setColumns(newColumns);
  };

  const handleDragEnd = () => {
    setDraggedColumn(null);
    localStorage.setItem('facturasRecibidas_columns', JSON.stringify(columns));
  };

  const resetColumns = () => {
    const defaultOrder = ALL_COLUMNS.map(c => c.id);
    setColumns(defaultOrder);
    localStorage.setItem('facturasRecibidas_columns', JSON.stringify(defaultOrder));
  };

  // Handle column sort click
  const handleSort = (columnId) => {
    if (sortColumn === columnId) {
      setSortDirection(prev => prev === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(columnId);
      setSortDirection('asc');
    }
  };

  // Sort facturas based on current sort settings
  const sortedFacturas = [...facturas].sort((a, b) => {
    let valA, valB;

    switch (sortColumn) {
      case 'fecha':
        valA = new Date(a.fechaEmision);
        valB = new Date(b.fechaEmision);
        break;
      case 'tipoDoc':
        valA = a.tipoDocumento || '';
        valB = b.tipoDocumento || '';
        break;
      case 'proveedor':
        valA = a.proveedorNombre || '';
        valB = b.proveedorNombre || '';
        break;
      case 'numero':
        valA = a.numeroConsecutivo || '';
        valB = b.numeroConsecutivo || '';
        break;
      case 'termino':
        valA = a.condicionVenta || '';
        valB = b.condicionVenta || '';
        break;
      case 'divisa':
        valA = a.moneda || '';
        valB = b.moneda || '';
        break;
      case 'tipoCambio':
        valA = a.tipoCambio || 0;
        valB = b.tipoCambio || 0;
        break;
      case 'subtotal':
        valA = a.totalVenta || (a.totalGravado || 0) + (a.totalExento || 0) + (a.totalExonerado || 0) + (a.totalNoSujeto || 0);
        valB = b.totalVenta || (b.totalGravado || 0) + (b.totalExento || 0) + (b.totalExonerado || 0) + (b.totalNoSujeto || 0);
        break;
      case 'impuesto':
        valA = a.totalImpuesto || 0;
        valB = b.totalImpuesto || 0;
        break;
      case 'total':
        valA = a.totalComprobante || 0;
        valB = b.totalComprobante || 0;
        break;
      case 'pagado':
        valA = parseFloat(a.totalPagado) || 0;
        valB = parseFloat(b.totalPagado) || 0;
        break;
      case 'saldo':
        valA = parseFloat(a.saldoPendiente ?? a.totalComprobante) || 0;
        valB = parseFloat(b.saldoPendiente ?? b.totalComprobante) || 0;
        break;
      case 'baseCRC':
        valA = parseFloat(a.totalBaseCrc) || 0;
        valB = parseFloat(b.totalBaseCrc) || 0;
        break;
      case 'ivaCRC':
        valA = parseFloat(a.totalImpuestoCrc) || 0;
        valB = parseFloat(b.totalImpuestoCrc) || 0;
        break;
      case 'descuento':
        valA = parseFloat(a.totalDescuentos) || 0;
        valB = parseFloat(b.totalDescuentos) || 0;
        break;
      case 'fechaVenc':
      case 'vencimiento': {
        valA = a.fechaVencimiento ? new Date(a.fechaVencimiento).getTime() : Infinity;
        valB = b.fechaVencimiento ? new Date(b.fechaVencimiento).getTime() : Infinity;
        break;
      }
      case 'estadoPago':
      case 'estado': {
        // Ambas columnas muestran badge basado en días hasta vencimiento (getEstadoVencimiento).
        // Ordenar por la misma lógica visual: vencidas más antiguas → recientes → al día → por vencer.
        const today = new Date(); today.setHours(0, 0, 0, 0);
        valA = a.fechaVencimiento ? (new Date(a.fechaVencimiento).getTime() - today.getTime()) : Infinity;
        valB = b.fechaVencimiento ? (new Date(b.fechaVencimiento).getTime() - today.getTime()) : Infinity;
        break;
      }
      default:
        return 0;
    }

    if (valA < valB) return sortDirection === 'asc' ? -1 : 1;
    if (valA > valB) return sortDirection === 'asc' ? 1 : -1;
    return 0;
  });

  // Calculate totals grouped by currency
  const calculateTotals = () => {
    const totals = {};

    sortedFacturas.forEach(factura => {
      const moneda = factura.moneda || 'CRC';

      if (!totals[moneda]) {
        totals[moneda] = {
          subtotal: 0, impuestos: 0, total: 0, count: 0,
          baseCRC: 0, ivaCRC: 0
        };
      }

      totals[moneda].count += 1;

      // Excluir facturas anuladas o pagadas del cálculo de pendientes
      if (factura.estado === 'anulado') {
        return;
      }

      const subtotal = factura.totalVenta || (factura.totalGravado || 0) + (factura.totalExento || 0) + (factura.totalExonerado || 0) + (factura.totalNoSujeto || 0);
      const impuestos = factura.totalImpuesto || 0;
      const total = factura.totalComprobante || 0;

      // CRC values — leen directo de BD (backend SIEMPRE pobla estos campos al insertar).
      const baseCRC = Number(factura.totalBaseCrc) || 0;
      const ivaCRC = Number(factura.totalImpuestoCrc) || 0;

      totals[moneda].subtotal += subtotal;
      totals[moneda].impuestos += impuestos;
      totals[moneda].total += total;
      totals[moneda].baseCRC += baseCRC;
      totals[moneda].ivaCRC += ivaCRC;
    });

    return totals;
  };

  const totalesPorMoneda = calculateTotals();

  // Render cell value based on column id
  const renderCell = (factura, columnId) => {
    // haciendaEstadoOriginal es la respuesta de Hacienda al PROVEEDOR: no la vuelve «aceptada» mientras
    // nuestra respuesta esté pendiente, en proceso o rechazada
    const estadoKey = rechazadaPorHacienda(factura) ? 'rechazada_hacienda'
      : enHacienda(factura) ? 'en_hacienda'
      : factura.haciendaEstadoOriginal === 'aceptado' && !['pendiente', 'procesando', 'rechazado'].includes(factura.estadoHacienda) ? 'aceptada' : factura.estado;
    const estadoCfg = estadoConfig[estadoKey] || estadoConfig.por_pagar;

    const isNC = esNC(factura);
    const moneyClass = isNC ? 'text-orange-700 font-semibold' : '';
    switch (columnId) {
      case 'fecha':
        return <span className="text-xs">{dayjs(factura.fechaEmision).format('DD/MM/YYYY')}</span>;
      case 'tipoDoc':
        return (
          <span className={clsx(
            "text-xs font-medium px-2 py-1 rounded",
            isNC ? "bg-orange-200 text-orange-800 font-bold" : "bg-gray-100"
          )}>
            {factura.tipoDocumento}
          </span>
        );
      case 'proveedor':
        return <span className="text-xs font-medium text-gray-900">{factura.proveedorNombre}</span>;
      case 'numero':
        return <span className="text-xs font-mono">{factura.fecNumeroFacturaProveedor || factura.numeroConsecutivo}</span>;
      case 'termino': {
        const esAceptada = estado === 'aceptada';
        const yaCorregida = factura.condicionCorregida || factura.condicion_corregida;
        const puedeCorregir = esAceptada && !yaCorregida;
        const nueva = factura.condicionVenta === '01' ? 'credito' : 'contado';
        const esContado = factura.condicionVenta === '01';
        const esNC = ['NC', '03'].includes(String(factura.tipoDocumento));
        // Contado aceptada: abre «¿Quién la pagó?» (empresa o empleado), que también lleva a pasar a crédito
        const abrePagadoPor = esAceptada && esContado && !esNC;
        const clickable = abrePagadoPor || puedeCorregir;
        return (
          <span
            className={`text-xs font-medium px-2 py-1 rounded whitespace-nowrap ${
              esContado ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'
            } ${clickable ? 'cursor-pointer hover:ring-2 hover:ring-amber-400' : ''}`}
            title={factura.pagadaPorEmpleado
              ? `Pagada por ${factura.empleadoNombre} (empresa asume ${factura.porcentajeEmpresa}%) · se reintegra en planilla`
              : abrePagadoPor ? 'Click: quién la pagó / corregir condición' : puedeCorregir ? 'Click para corregir condición' : yaCorregida ? 'Condición ya fue corregida' : ''}
            onClick={abrePagadoPor
              ? (e) => { e.stopPropagation(); setPagadoPorModal({ factura, pagadoPor: factura.pagadaPorEmpleado ? 'empleado' : 'empresa', empleadoNombre: factura.empleadoNombre || '', porcentajeEmpresa: factura.porcentajeEmpresa ?? 100 }); }
              : puedeCorregir ? (e) => { e.stopPropagation(); setCorregirModal({ factura, nueva }); } : undefined}
          >
            {esContado ? 'Contado' : 'Crédito'}
            {factura.pagadaPorEmpleado && ' · Empleado'}
            {yaCorregida && ' ✓'}
          </span>
        );
      }
      case 'divisa':
        return <span className="text-xs font-medium">{factura.moneda}</span>;
      case 'tipoCambio':
        return <span className="text-sm">₡{factura.tipoCambio?.toFixed(2)}</span>;
      case 'tarifa': {
        // Si la factura tiene MÁS DE UNA tarifa en líneas (caso real Walmart: 1% + 13%),
        // mostrar las tarifas distintas como "1%+13%" en vez del promedio engañoso
        // (impuesto/gravado)*100 que daba 8% para esa mezcla.
        const tarifas = Array.isArray(factura.tarifasLineas) ? factura.tarifasLineas : [];
        const gravado = parseFloat(factura.totalGravado) || 0;
        const impuesto = parseFloat(factura.totalImpuesto) || 0;
        const exonerado = parseFloat(factura.totalExonerado) || 0;
        const exento = parseFloat(factura.totalExento) || 0;
        const noSujeto = parseFloat(factura.totalNoSujeto) || 0;

        if (tarifas.length > 1) {
          // Mezcla de tarifas (ej. [1, 13])
          const label = tarifas.map(t => `${t}%`).join('+');
          return (
            <span className="px-2 py-0.5 rounded text-xs font-medium bg-orange-100 text-orange-700"
              title={`Factura con tarifas mezcladas: ${label}`}>
              {label}
            </span>
          );
        }

        let pct = null;
        let label = '';
        let color = 'bg-gray-100 text-gray-600';
        if (tarifas.length === 1) {
          pct = tarifas[0];
          label = `${pct}%`;
          if (pct === 13) color = 'bg-emerald-100 text-emerald-700';
          else if (pct === 1 || pct === 2 || pct === 4) color = 'bg-amber-100 text-amber-700';
          else color = 'bg-emerald-100 text-emerald-700';
        } else if (gravado > 0 && impuesto > 0) {
          pct = Math.round((impuesto / gravado) * 100);
          label = `${pct}%`;
          if (pct === 13) color = 'bg-emerald-100 text-emerald-700';
          else if (pct === 1 || pct === 2 || pct === 4) color = 'bg-amber-100 text-amber-700';
          else color = 'bg-emerald-100 text-emerald-700';
        } else if (exonerado > 0) {
          label = 'Exo';
          color = 'bg-blue-100 text-blue-700';
        } else if (noSujeto > 0) {
          label = '0% s/c';
          color = 'bg-purple-100 text-purple-700';
        } else if (exento > 0) {
          label = 'Exento';
          color = 'bg-gray-100 text-gray-600';
        } else {
          return <span className="text-gray-400 text-sm">-</span>;
        }
        return (
          <span className={clsx("px-2 py-0.5 rounded text-xs font-medium", color)}>{label}</span>
        );
      }
      case 'subtotal': {
        const raw = factura.totalVenta || (factura.totalGravado || 0) + (factura.totalExento || 0) + (factura.totalExonerado || 0) + (factura.totalNoSujeto || 0);
        return <span className={clsx("text-sm", moneyClass)}>{formatMoney(monto(factura, raw), factura.moneda)}</span>;
      }
      case 'descuento':
        return <span className="text-sm text-gray-500">-</span>;
      case 'impuesto':
        return <span className={clsx("text-sm", moneyClass)}>{formatMoney(monto(factura, factura.totalImpuesto), factura.moneda)}</span>;
      case 'total':
        return <span className={clsx("text-sm font-bold", moneyClass)}>{formatMoney(monto(factura, factura.totalComprobante), factura.moneda)}</span>;
      case 'pagado':
        return (factura.totalPagado || 0) > 0 ? (
          <span className="inline-flex items-center px-2 py-0.5 bg-green-100 text-green-700 text-sm font-semibold rounded-full">
            {formatMoney(factura.totalPagado, factura.moneda)}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        );
      case 'saldo':
        return (
          <span className={`text-sm font-bold ${
            factura.moneda === 'USD' ? 'text-blue-600' : 'text-gray-600'
          }`}>
            {formatMoney(factura.saldoPendiente || factura.totalComprobante, factura.moneda)}
          </span>
        );
      case 'cumplimiento': {
        // Resultado GUARDADO de la revisión con las reglas de la empresa (Configuración → Facturas recibidas)
        const c = factura.cumplimientoEstado;
        if (!c) return <span className="text-xs text-gray-400">—</span>;
        const est = {
          cumple: ['✓ Cumple', 'border-green-400 text-green-700'],
          advierte: ['! Advertencia', 'border-amber-400 text-amber-700'],
          incumple: ['✕ Incumple', 'border-red-400 text-red-700'],
        }[c];
        return (
          <button type="button" onClick={(e) => { e.stopPropagation(); setCumplimientoFactura(factura); }}
            className={`px-2 py-0.5 rounded-full border text-xs font-medium whitespace-nowrap hover:bg-gray-50 ${est[1]}`}
            title="Ver lo que trae y lo que no trae la factura">
            {est[0]}
          </button>
        );
      }
      case 'estado': {
        // Get the REAL Hacienda message from mensaje receptor response
        // Rechazada al proveedor: se muestra por qué Hacienda le rechazó SU factura, no la respuesta a nuestro
        // mensaje (que solo dice -29 «clave no registrada», consecuencia de lo mismo)
        const alProveedor = rechazadaPorHacienda(factura);
        const mensajeHacienda = alProveedor ? factura.haciendaMensaje : (factura.mrRespuestaHacienda || factura.haciendaMensaje || null);
        const hasMensaje = mensajeHacienda && (rechazadaPorHacienda(factura) || ['rechazada', 'procesando_hacienda', 'procesando_rechazo', 'aceptada', 'por_pagar'].includes(factura.estado));
        const isProcessing = ['procesando_hacienda', 'procesando_rechazo'].includes(factura.estado);

        return (
          <button
            type="button"
            onClick={() => hasMensaje && setMensajeHaciendaModal({ factura, mensaje: mensajeHacienda, alProveedor })}
            className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${estadoCfg.color} ${hasMensaje ? 'cursor-pointer hover:ring-2 hover:ring-offset-1 hover:ring-gray-400' : 'cursor-default'} ${isProcessing ? 'animate-pulse' : ''}`}
            title={hasMensaje ? 'Click para ver mensaje de Hacienda' : (isProcessing ? 'Esperando respuesta de Hacienda...' : '')}
          >
            <estadoCfg.icon size={12} className={isProcessing ? 'animate-spin' : ''} />
            {estadoCfg.label}
          </button>
        );
      }
      case 'estadoPago': {
        const estadoVenc = getEstadoVencimiento(factura);
        return (
          <span className={`whitespace-nowrap px-2 py-0.5 text-xs rounded ${estadoVenc.clase}`}>
            {estadoVenc.texto}
          </span>
        );
      }
      case 'baseCRC': {
        // Base imponible total en CRC — backend (FacturaCompra.create línea 167)
        // calcula y guarda esto SIEMPRE en INSERT. Solo lee de BD acá.
        const baseAbs = Number(factura.totalBaseCrc) || 0;
        const baseSigned = isNC ? -baseAbs : baseAbs;
        return baseAbs > 0 ? (
          <span className={clsx("text-sm", moneyClass)}>
            {baseSigned < 0 ? '-' : ''}₡{Math.abs(baseSigned).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        );
      }
      case 'ivaCRC': {
        // IVA en CRC (total_impuesto_crc o calculado)
        const ivaAbs = factura.totalImpuestoCrc || (factura.totalImpuesto * (factura.tipoCambio || 1)) || 0;
        const ivaSigned = isNC ? -ivaAbs : ivaAbs;
        return ivaAbs > 0 ? (
          <span className={clsx("text-sm", moneyClass)}>
            {ivaSigned < 0 ? '-' : ''}₡{Math.abs(ivaSigned).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
          </span>
        ) : (
          <span className="text-gray-400 text-sm">-</span>
        );
      }
      case 'cabys':
        return <span className="text-xs font-mono text-gray-500 max-w-[120px] truncate block" title={factura.cabysCodes || '-'}>{factura.cabysCodes || '-'}</span>;
      case 'confirmadoPor':
        return <span className="text-sm text-gray-600">{factura.confirmadoNombre || '-'}</span>;
      case 'confirmadoAt':
        return factura.confirmadoAt ? (
          <span className="text-sm text-gray-600">
            {new Date(factura.confirmadoAt).toLocaleDateString('es-CR')}
          </span>
        ) : <span className="text-gray-400 text-sm">-</span>;
      default:
        return null;
    }
  };

  const handleSearch = (e) => {
    e.preventDefault();
    setPagination(prev => ({ ...prev, page: 1 }));
    fetchFacturas();
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
  };

  const handleViewFactura = async (factura) => {
    try {
      setLoadingDetalle(true);
      setSelectedFactura(factura);
      setShowPreviewModal(true);
      const response = await gastosApi.getFactura(factura.id);
      setFacturaDetalle(response.data.data);
    } catch (error) {
      console.error('Error fetching factura details:', error);
      toast.error('Error al cargar detalle');
    } finally {
      setLoadingDetalle(false);
    }
  };

  const handleDelete = (factura) => {
    setConfirmModal({
      title: '¿Eliminar factura?',
      message: `Se eliminará la factura ${factura.numeroConsecutivo} de ${factura.proveedorNombre}`,
      confirmText: 'Eliminar',
      variant: 'danger',
      onConfirm: async () => {
        try {
          await gastosApi.deleteFactura(factura.id);
          toast.success('Factura eliminada');
          fetchFacturas();
        } catch (error) {
          console.error('Error deleting factura:', error);
          toast.error('Error al eliminar factura');
        }
      }
    });
  };

  // ========================================
  // SELECTION FOR POR_PAGAR VIEW
  // ========================================

  const toggleFacturaSelection = (factura) => {
    setSelectedFacturas(prev => {
      const isSelected = prev.some(f => f.id === factura.id);
      if (isSelected) {
        return prev.filter(f => f.id !== factura.id);
      } else {
        return [...prev, factura];
      }
    });
  };

  const toggleSelectAllPorPagar = () => {
    const eligibleFacturas = sortedFacturas.filter(f =>
      f.estado !== 'pagado' && f.estado !== 'anulado'
    );
    if (selectedFacturas.length === eligibleFacturas.length && eligibleFacturas.length > 0) {
      setSelectedFacturas([]);
    } else {
      setSelectedFacturas(eligibleFacturas);
    }
  };

  const clearSelection = () => {
    setSelectedFacturas([]);
  };

  // Check if selected invoices can be paid together (same provider and currency)
  const canPaySelected = () => {
    if (selectedFacturas.length === 0) return false;
    const first = selectedFacturas[0];
    return selectedFacturas.every(f =>
      f.proveedorId === first.proveedorId &&
      f.moneda === first.moneda &&
      f.estado !== 'pagado' &&
      f.estado !== 'anulado' &&
      // Una NC no se paga, se APLICA a una FE → no debe habilitar "Pagar"
      !['NC', '03'].includes(f.tipoDocumento)
    );
  };

  const getSelectedTotal = () => {
    return selectedFacturas.reduce((sum, f) => sum + (f.saldoPendiente || f.totalComprobante || 0), 0);
  };

  const getSelectedMoneda = () => {
    return selectedFacturas.length > 0 ? selectedFacturas[0].moneda : 'CRC';
  };

  const handleVerSelected = () => {
    if (selectedFacturas.length === 1) {
      handleViewFactura(selectedFacturas[0]);
    }
  };

  const handlePagarSelected = () => {
    if (!canPaySelected()) {
      toast.error('Las facturas deben ser del mismo proveedor y moneda');
      return;
    }
    if (selectedFacturas.length === 1) {
      setSelectedFactura(selectedFacturas[0]);
    }
    setShowPagoModal(true);
  };

  const handleEliminarSelected = () => {
    if (selectedFacturas.length === 1) {
      handleDelete(selectedFacturas[0]);
    } else {
      // Multiple delete confirmation
      setConfirmModal({
        title: `¿Eliminar ${selectedFacturas.length} facturas?`,
        message: `Se eliminarán ${selectedFacturas.length} facturas seleccionadas. Esta acción no se puede deshacer.`,
        confirmText: 'Eliminar',
        variant: 'danger',
        onConfirm: async () => {
          try {
            for (const factura of selectedFacturas) {
              await gastosApi.deleteFactura(factura.id);
            }
            toast.success(`${selectedFacturas.length} facturas eliminadas`);
            setSelectedFacturas([]);
            fetchFacturas();
          } catch (error) {
            console.error('Error deleting facturas:', error);
            toast.error('Error al eliminar facturas');
          }
          setConfirmModal(null);
        }
      });
    }
  };

  const formatMoney = (amount, currency = 'CRC') => {
    const num = parseFloat(amount) || 0;
    const abs = Math.abs(num);
    const symbol = currency === 'USD' ? '$' : '₡';
    const locale = currency === 'USD' ? 'en-US' : 'es-CR';
    const formatted = abs.toLocaleString(locale, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    return num < 0 ? `-${symbol}${formatted}` : `${symbol}${formatted}`;
  };

  // NC = nota de crédito (resta contablemente). Se identifica visualmente con
  // fondo naranja claro + badge naranja + montos negativos en rojo.
  const esNC = (f) => ['NC', '03'].includes(f?.tipoDocumento);
  // Aplica signo negativo si la factura es NC. Para mostrar montos contablemente
  // como crédito en la UI aunque BD los guarde en positivo.
  const monto = (f, valor) => esNC(f) ? -(parseFloat(valor) || 0) : (parseFloat(valor) || 0);

  const handleAccept = (factura) => {
    if (rechazadaPorHacienda(factura)) {
      toast.error('Hacienda rechazó esta factura del proveedor: no se puede confirmar. Pídale una nueva.');
      return;
    }
    // Usar siempre el batch modal (mejor UX con animaciones y polling)
    setBatchSelected([factura.id]);
    setShowBatchAceptarModal(true);
  };

  const handleReject = (factura) => {
    setSelectedFactura(factura);
    setShowRechazarModal(true);
  };

  const handleRefreshHaciendaStatus = async (factura) => {
    try {
      const response = await gastosApi.checkMensajeReceptorStatus(factura.id);
      const status = response.data.data;

      if (status.estadoHacienda === 'accepted') {
        toast.success('Hacienda confirmó la aceptación. Factura lista para pago.');
      } else if (status.estadoHacienda === 'rejected') {
        toast.error(`Hacienda rechazó: ${status.mensaje || 'Sin detalle'}`);
      } else {
        toast(`Estado: ${status.estadoHacienda}. Intente de nuevo en unos momentos.`);
      }
      fetchFacturas();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al verificar estado');
    }
  };

  // ========================================
  // EXPORT FUNCTIONS
  // ========================================
  const getExportParams = () => {
    const params = { estado };
    // Calculate dates using same logic as fetchFacturas
    if (filtroFecha?.desde) params.fechaDesde = filtroFecha.desde;
    if (filtroFecha?.hasta) params.fechaHasta = filtroFecha.hasta;
    params.columns = visibleColumns.join(',');
    return params;
  };

  const exportarExcel = async () => {
    setExporting(true);
    try {
      const response = await gastosApi.exportExcel(getExportParams());
      const blob = new Blob([response.data], {
        type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
      });
      const filename = `Facturas_${estado}_${new Date().toISOString().split('T')[0]}.xlsx`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('Excel exportado');
    } catch (err) {
      console.error('Error exportando Excel:', err);
      toast.error('Error al exportar Excel');
    } finally {
      setExporting(false);
    }
  };

  const exportarPdf = async () => {
    setExporting(true);
    try {
      const response = await gastosApi.exportPdf(getExportParams());
      const blob = new Blob([response.data], { type: 'application/pdf' });
      const filename = `Facturas_${estado}_${new Date().toISOString().split('T')[0]}.pdf`;
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
      toast.success('PDF exportado');
    } catch (err) {
      console.error('Error exportando PDF:', err);
      toast.error('Error al exportar PDF');
    } finally {
      setExporting(false);
    }
  };

  // ========================================
  // BATCH OPERATIONS (Por Confirmar)
  // ========================================

  const toggleBatchSelect = (factura) => {
    setBatchSelected(prev =>
      prev.includes(factura.id)
        ? prev.filter(id => id !== factura.id)
        : [...prev, factura.id]
    );
  };

  const toggleSelectAll = () => {
    // Incluir por_confirmar y rechazada (rechazada se puede eliminar)
    const eligibleFacturas = sortedFacturas.filter(f => ['por_confirmar', 'rechazada'].includes(f.estado));
    if (batchSelected.length === eligibleFacturas.length) {
      setBatchSelected([]);
    } else {
      setBatchSelected(eligibleFacturas.map(f => f.id));
    }
  };

  const handleBatchAceptar = () => {
    // Filtrar solo las facturas por_confirmar (no las rechazadas)
    const porConfirmarIds = batchSelected.filter(id => {
      const f = facturas.find(x => x.id === id);
      return f?.estado === 'por_confirmar' && !rechazadaPorHacienda(f);
    });
    const deHacienda = batchSelected.filter(id => rechazadaPorHacienda(facturas.find(x => x.id === id))).length;
    if (deHacienda) toast.error(`${deHacienda} factura${deHacienda === 1 ? '' : 's'} rechazada${deHacienda === 1 ? '' : 's'} por Hacienda al proveedor: no se confirma${deHacienda === 1 ? '' : 'n'}`);

    if (porConfirmarIds.length === 0) {
      toast.error('Selecciona al menos una factura por confirmar');
      return;
    }

    // Siempre usar modal batch (tiene mejor UX con animaciones y polling)
    setBatchSelected(porConfirmarIds);
    setShowBatchAceptarModal(true);
  };

  const handleBatchRechazar = () => {
    // Filtrar solo las facturas por_confirmar (no las rechazadas)
    const porConfirmarIds = batchSelected.filter(id =>
      facturas.find(f => f.id === id)?.estado === 'por_confirmar'
    );

    if (porConfirmarIds.length === 0) {
      toast.error('Selecciona al menos una factura por confirmar');
      return;
    }

    // Usar solo las por_confirmar
    setBatchSelected(porConfirmarIds);
    setShowBatchRechazarModal(true);
  };

  const handleBatchEliminar = () => {
    if (batchSelected.length === 0) {
      toast.error('Selecciona al menos una factura');
      return;
    }
    setConfirmModal({
      title: '¿Eliminar facturas?',
      message: `Se eliminarán ${batchSelected.length} facturas. Esta acción no se puede deshacer.`,
      confirmText: 'Eliminar',
      variant: 'danger',
      onConfirm: async () => {
        try {
          setBatchProcessing(true);
          const response = await gastosApi.batchEliminar(batchSelected);
          toast.success(response.data.message);
          setBatchSelected([]);
          fetchFacturas();
        } catch (error) {
          toast.error(error.response?.data?.error || 'Error al procesar');
        } finally {
          setBatchProcessing(false);
        }
      }
    });
  };

  const handleBatchDescargarXML = async (tipo = 'documento') => {
    if (batchSelected.length === 0) {
      toast.error('Selecciona al menos una factura');
      return;
    }

    try {
      setBatchProcessing(true);
      const response = await gastosApi.batchDescargarXML(batchSelected, tipo);

      // Get filename from Content-Disposition header or generate one
      const contentDisposition = response.headers['content-disposition'];
      let filename;
      if (contentDisposition) {
        const match = contentDisposition.match(/filename="?([^";\n]+)"?/);
        filename = match ? match[1] : null;
      }
      if (!filename) {
        const ext = batchSelected.length === 1 ? 'xml' : 'zip';
        const prefix = tipo === 'respuesta' ? 'respuesta_hacienda' : 'documento';
        filename = `${prefix}_${Date.now()}.${ext}`;
      }

      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast.success('Descarga iniciada');
      setShowDownloadMenu(false);
    } catch (error) {
      // La respuesta pedida como blob trae el error del servidor adentro: leerlo para decir qué faltó
      let msg = error.response?.data?.error;
      if (!msg && error.response?.data instanceof Blob) {
        try { msg = JSON.parse(await error.response.data.text()).error; } catch { /* sin detalle */ }
      }
      toast.error(msg || 'Error al descargar');
    } finally {
      setBatchProcessing(false);
    }
  };

  // ============================================
  // CATEGORIZACIÓN DE GASTOS
  // ============================================

  // Cargar catálogos de categorización
  const loadCategorias = async () => {
    if (categorias) return; // Ya están cargadas
    try {
      const response = await gastosApi.getCategorias();
      setCategorias(response.data.data);
    } catch (error) {
      console.error('Error cargando categorías:', error);
    }
  };

  // Toggle selección para categorización
  const toggleCategorizacionSelect = (id) => {
    setCategorizacionSelected(prev =>
      prev.includes(id)
        ? prev.filter(x => x !== id)
        : [...prev, id]
    );
  };

  // Seleccionar/deseleccionar todas para categorización
  const toggleSelectAllCategorizacion = () => {
    if (categorizacionSelected.length === sortedFacturas.length && categorizacionSelected.length > 0) {
      setCategorizacionSelected([]);
    } else {
      setCategorizacionSelected(sortedFacturas.map(f => f.id));
    }
  };

  // Abrir modal de categorización
  const openCategorizacionModal = async () => {
    if (categorizacionSelected.length === 0) {
      toast.error('Selecciona al menos una factura');
      return;
    }
    await loadCategorias();
    setShowCategorizacionModal(true);
  };

  // Abrir modal de categorización individual (con líneas)
  const openCategorizacionDetalleModal = async (factura) => {
    try {
      setLineasLoading(true);
      await loadCategorias();

      // Get full factura details with lines
      const [facturaRes, lineasRes] = await Promise.all([
        gastosApi.getFactura(factura.id),
        gastosApi.getFacturaLineas(factura.id)
      ]);

      let lineas = lineasRes.data.data;

      // Si alguna línea no tiene código CABYS, re-parsear desde el XML
      const faltaCabys = lineas.some(l => !l.codigo_cabys);
      if (faltaCabys) {
        try {
          await gastosApi.reparseCabys(factura.id);
          // Recargar líneas con CABYS actualizado
          const lineasActualizadas = await gastosApi.getFacturaLineas(factura.id);
          lineas = lineasActualizadas.data.data;
        } catch (e) {
          console.warn('No se pudo re-parsear CABYS:', e);
        }
      }

      setFacturaCategorizacion(facturaRes.data.data);
      setLineasCategorizacion(lineas.map(linea => {
        // Auto-detectar Clase basado en CABYS (primer dígito)
        // 0-4 = Bien (B), 5-9 = Servicio (S)
        const cabysCode = linea.codigo_cabys || linea.codigo || '';
        const firstDigit = String(cabysCode).charAt(0);
        const autoClase = ['5', '6', '7', '8', '9'].includes(firstDigit) ? 'S' : 'B';

        return {
          ...linea,
          tipo_movimiento: linea.tipo_movimiento || '01',
          clase: linea.clase || autoClase,
          identificacion: linea.identificacion || 'E',
          tarifa_iva_categoria: linea.tarifa_iva_categoria || '13',
          autoClase // guardar para referencia
        };
      }));
      setShowCategorizacionDetalleModal(true);
    } catch (error) {
      toast.error('Error al cargar factura');
      console.error(error);
    } finally {
      setLineasLoading(false);
    }
  };

  // Guardar categorización de una línea individual
  const handleSaveLineaCategorizacion = async (linea) => {
    try {
      await gastosApi.categorizarLinea(facturaCategorizacion.id, linea.id, {
        tipo_movimiento: linea.tipo_movimiento,
        clase: linea.clase,
        identificacion: linea.identificacion,
        tarifa_iva_categoria: linea.tarifa_iva_categoria
      });
      toast.success('Línea categorizada');
      setEditingLineaId(null);
      // Update local state
      setLineasCategorizacion(prev => prev.map(l =>
        l.id === linea.id ? { ...l, categorizado: true } : l
      ));
    } catch (error) {
      toast.error('Error al categorizar línea');
    }
  };

  // Update linea field locally
  const updateLineaField = (lineaId, field, value) => {
    setLineasCategorizacion(prev => prev.map(l =>
      l.id === lineaId ? { ...l, [field]: value } : l
    ));
  };

  // Aplicar categorización
  const handleCategorizarBatch = async () => {
    if (categorizacionSelected.length === 0) return;

    try {
      setCategorizacionLoading(true);
      const response = await gastosApi.batchCategorizar(categorizacionSelected, categorizacionForm);
      toast.success(response.data.message);
      setCategorizacionSelected([]);
      setShowCategorizacionModal(false);
      fetchFacturas();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al categorizar');
    } finally {
      setCategorizacionLoading(false);
    }
  };

  return (
    <div className="flex flex-col gap-6 min-w-0 h-[var(--alto-contenido)]">
      {/* Batch Toolbar - Solo para Por Confirmar */}
      {estado === 'por_confirmar' && (() => {
        // Calcular cuántas facturas por_confirmar hay seleccionadas (para Confirmar/Rechazar)
        const selectedPorConfirmar = batchSelected.filter(id => {
          const f = facturas.find(x => x.id === id);
          return f?.estado === 'por_confirmar' && !enHacienda(f);
        });
        const hasPorConfirmar = selectedPorConfirmar.length > 0;

        return (
        <div className="bg-white rounded-lg border p-3 shrink-0 relative">
          {/* Buscador absolute izquierda — no ocupa espacio del flow para que botones queden centrados */}
          <div ref={searchToolbarRef} className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar proveedor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm w-52"
              />
            </div>
            <button type="submit" onClick={handleSearch} className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm">Buscar</button>
            {/* Procesos con Hacienda minimizados (siguen corriendo): aquí quedan a la vista */}
            <span id={HUECO_PROCESOS} className="flex items-center gap-2 empty:hidden" />
          </div>
          {/* Botones centrados al toolbar — useLayoutEffect aplica padding-left solo si hay traslape */}
          <div ref={toolbarButtonsRef} className="flex items-center justify-center gap-2 flex-wrap">
          {/* Contador de seleccionadas */}
          {batchSelected.length > 0 && (
            <span className="text-sm font-medium text-blue-600 mr-2">
              {batchSelected.length} seleccionada{batchSelected.length > 1 ? 's' : ''}
            </span>
          )}

          {/* Confirmar - solo para facturas por_confirmar */}
          {canEdit && (
            <ToolbarButton
              icon={Check}
              label="Confirmar"
              onClick={handleBatchAceptar}
              disabled={!hasPorConfirmar || batchProcessing}
              variant="success"
            />
          )}

          {/* Rechazar - solo para facturas por_confirmar */}
          {canEdit && (
            <ToolbarButton
              icon={XCircle}
              label="Rechazar"
              onClick={handleBatchRechazar}
              disabled={!hasPorConfirmar || batchProcessing}
              variant="danger"
            />
          )}

          <ToolbarSeparator />

          {/* Eliminar - funciona para por_confirmar y rechazada */}
          {canDelete && (
            <ToolbarButton
              icon={Delete}
              label="Eliminar"
              onClick={handleBatchEliminar}
              disabled={batchSelected.length === 0 || batchProcessing}
              variant="rose"
            />
          )}

          {/* Descargar dropdown */}
          <div className="relative" ref={downloadMenuRef}>
            <ToolbarButton
              icon={Download}
              label="Descargar"
              onClick={() => setShowDownloadMenu(!showDownloadMenu)}
              disabled={batchSelected.length === 0 || batchProcessing}
              variant="cyan"
            />
            {showDownloadMenu && (() => {
              // Flotante (fixed) bajo el botón: la barra corta lo que se sale de ella y el menú quedaba detrás
              const r = downloadMenuRef.current?.getBoundingClientRect();
              return (
              <div className="fixed bg-white border rounded-lg shadow-lg z-[60] py-1 min-w-[200px] -translate-x-1/2"
                style={r ? { top: r.bottom + 4, left: r.left + r.width / 2 } : undefined}>
                <button
                  onClick={() => handleBatchDescargarXML('documento')}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                >
                  <FileText size={14} className="text-blue-600" />
                  Documento Electrónico
                </button>
                <button
                  onClick={() => handleBatchDescargarXML('respuesta')}
                  className="w-full px-4 py-2 text-left text-sm hover:bg-gray-100 flex items-center gap-2"
                >
                  <FileText size={14} className="text-green-600" />
                  Respuesta Hacienda
                </button>
              </div>
              );
            })()}
          </div>

          <ToolbarSeparator />

          <ToolbarButton
            icon={FileSpreadsheet}
            label="Excel"
            variant="success"
            onClick={exportarExcel}
            disabled={exporting || facturas.length === 0}
          />
          <ToolbarButton
            icon={FileText}
            label="PDF"
            variant="danger"
            onClick={exportarPdf}
            disabled={exporting || facturas.length === 0}
          />
          {config.showUploadXML && canCreate && (
            <ToolbarButton
              icon={Upload}
              label="Importar XML"
              variant="successOutline"
              onClick={() => setShowUploadXMLModal(true)}
            />
          )}

          {batchProcessing && (
            <div className="ml-2 flex items-center gap-2 text-sm text-gray-500">
              <Loader size={16} className="animate-spin" />
              Procesando...
            </div>
          )}
          </div>
        </div>
        );
      })()}

      {/* Summary Cards - Gastos → Aceptadas */}
      {summary && estado === 'aceptada' && (
        (() => {
          // Totales por moneda + extras (exonerado, proveedores únicos, categorización)
          const totales = {
            CRC: { compras: 0, iva: 0, total: 0, cant: 0, exonerado: 0 },
            USD: { compras: 0, iva: 0, total: 0, cant: 0, exonerado: 0 }
          };
          const proveedoresSet = new Set();
          let categorizadas = 0;
          sortedFacturas.forEach(f => {
            const mon = f.moneda || 'CRC';
            if (!totales[mon]) totales[mon] = { compras: 0, iva: 0, total: 0, cant: 0, exonerado: 0 };
            totales[mon].compras   += f.totalVenta || ((f.totalGravado || 0) + (f.totalExento || 0) + (f.totalExonerado || 0) + (f.totalNoSujeto || 0));
            totales[mon].exonerado += (f.totalExonerado || 0);
            totales[mon].iva       += (f.totalImpuesto || 0);
            totales[mon].total     += (f.totalComprobante || 0);
            totales[mon].cant++;
            if (f.proveedorId) proveedoresSet.add(f.proveedorId);
            else if (f.proveedorNombre) proveedoresSet.add(f.proveedorNombre);
            if (f.todasCategorizadas) categorizadas++;
          });
          const totalFacturas = totales.CRC.cant + totales.USD.cant;
          const proveedoresUnicos = proveedoresSet.size;
          const pendientesCat = totalFacturas - categorizadas;
          const catPct = totalFacturas > 0 ? Math.round((categorizadas / totalFacturas) * 100) : 0;
          const hayExonerado = (totales.CRC.exonerado + totales.USD.exonerado) > 0;

          return (
            <div className="grid grid-cols-2 lg:grid-cols-5 gap-3 shrink-0">

              {/* 1. Documentos Aceptados */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-3 py-1.5 bg-gradient-to-r from-green-50 to-emerald-100 border-b border-green-200 flex items-center gap-2">
                  <div className="p-1 bg-green-600 rounded"><CheckCircle className="h-3.5 w-3.5 text-white" /></div>
                  <h3 className="font-semibold text-xs text-gray-800">Documentos Aceptados</h3>
                </div>
                <div className="px-3 py-1.5">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-xl font-bold text-gray-900 leading-tight">{totalFacturas}</span>
                    <span className="text-xs text-gray-500">{totalFacturas === 1 ? 'documento' : 'documentos'}</span>
                    {totales.CRC.cant > 0 && (
                      <span className="text-[11px] text-gray-500"><strong className="text-gray-700">{totales.CRC.cant}</strong> CRC</span>
                    )}
                    {totales.CRC.cant > 0 && totales.USD.cant > 0 && <span className="text-gray-300 text-[11px]">·</span>}
                    {totales.USD.cant > 0 && (
                      <span className="text-[11px] text-gray-500"><strong className="text-gray-700">{totales.USD.cant}</strong> USD</span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-400 flex items-center gap-1">
                    <Building2 className="h-3 w-3" /> {proveedoresUnicos} {proveedoresUnicos === 1 ? 'proveedor' : 'proveedores'}
                  </div>
                </div>
              </div>

              {/* 2. Total Compras (subtotal sin IVA) */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-3 py-1.5 bg-gradient-to-r from-blue-50 to-blue-100 border-b border-blue-200 flex items-center gap-2">
                  <div className="p-1 bg-blue-600 rounded"><Receipt className="h-3.5 w-3.5 text-white" /></div>
                  <h3 className="font-semibold text-xs text-gray-800">Total Compras</h3>
                </div>
                <div className="px-3 py-1.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-base font-bold text-gray-900 leading-tight">{formatMoney(totales.CRC.compras, 'CRC')}</span>
                    {totales.USD.compras > 0 && (
                      <span className="text-base font-bold text-gray-900 leading-tight">{formatMoney(totales.USD.compras, 'USD')}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-400">Subtotal sin IVA</div>
                  {hayExonerado && (
                    <div className="text-[11px] text-amber-700">
                      + Exo: {totales.CRC.exonerado > 0 && formatMoney(totales.CRC.exonerado, 'CRC')}
                      {totales.CRC.exonerado > 0 && totales.USD.exonerado > 0 && ' · '}
                      {totales.USD.exonerado > 0 && formatMoney(totales.USD.exonerado, 'USD')}
                    </div>
                  )}
                </div>
              </div>

              {/* 3. IVA Crédito */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-3 py-1.5 bg-gradient-to-r from-purple-50 to-purple-100 border-b border-purple-200 flex items-center gap-2">
                  <div className="p-1 bg-purple-600 rounded"><DollarSign className="h-3.5 w-3.5 text-white" /></div>
                  <h3 className="font-semibold text-xs text-gray-800">IVA Crédito</h3>
                </div>
                <div className="px-3 py-1.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-base font-bold text-purple-700 leading-tight">{formatMoney(totales.CRC.iva, 'CRC')}</span>
                    {totales.USD.iva > 0 && (
                      <span className="text-base font-bold text-purple-700 leading-tight">{formatMoney(totales.USD.iva, 'USD')}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-400">Acreditable D-104</div>
                </div>
              </div>

              {/* 4. Total Pagado (con IVA) */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className="px-3 py-1.5 bg-gradient-to-r from-slate-100 to-slate-200 border-b border-slate-300 flex items-center gap-2">
                  <div className="p-1 bg-slate-700 rounded"><Wallet className="h-3.5 w-3.5 text-white" /></div>
                  <h3 className="font-semibold text-xs text-gray-800">Total Pagado</h3>
                </div>
                <div className="px-3 py-1.5">
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="text-base font-bold text-gray-900 leading-tight">{formatMoney(totales.CRC.total, 'CRC')}</span>
                    {totales.USD.total > 0 && (
                      <span className="text-base font-bold text-gray-900 leading-tight">{formatMoney(totales.USD.total, 'USD')}</span>
                    )}
                  </div>
                  <div className="text-[11px] text-gray-400">Con IVA</div>
                </div>
              </div>

              {/* 5. Categorización */}
              <div className="bg-white rounded-lg border border-gray-200 shadow-sm overflow-hidden">
                <div className={`px-3 py-1.5 border-b flex items-center gap-2 ${pendientesCat > 0 ? 'bg-gradient-to-r from-amber-50 to-amber-100 border-amber-200' : 'bg-gradient-to-r from-emerald-50 to-emerald-100 border-emerald-200'}`}>
                  <div className={`p-1 rounded ${pendientesCat > 0 ? 'bg-amber-600' : 'bg-emerald-600'}`}><Tags className="h-3.5 w-3.5 text-white" /></div>
                  <h3 className="font-semibold text-xs text-gray-800">Categorización</h3>
                </div>
                <div className="px-3 py-1.5">
                  <div className="flex items-baseline gap-2 flex-wrap">
                    <span className="text-xl font-bold text-gray-900 leading-tight">{categorizadas}</span>
                    <span className="text-xs text-gray-500">de {totalFacturas}</span>
                    <span className={`text-[11px] ${pendientesCat > 0 ? 'text-amber-700 font-medium' : 'text-emerald-700'}`}>
                      {pendientesCat > 0 ? `· ${pendientesCat} pendiente${pendientesCat !== 1 ? 's' : ''}` : '· ✓ Todas'}
                    </span>
                  </div>
                  <div className="mt-1 h-1 w-full bg-gray-100 rounded overflow-hidden">
                    <div className={`h-full ${pendientesCat > 0 ? 'bg-amber-500' : 'bg-emerald-500'}`} style={{ width: `${catPct}%` }} />
                  </div>
                </div>
              </div>

            </div>
          );
        })()
      )}

      {/* Toolbar Categorización con buscador - Solo para Aceptadas.
          Buscador absolute izquierda (NO afecta el flow). Botones centrados al toolbar
          (justify-center → centro de la pantalla). useLayoutEffect mide y aplica
          padding-left dinámico al wrapper de botones SOLO si hay traslape entre buscador
          y botones → los botones se desplazan a la derecha lo MÍNIMO necesario y vuelven
          al centro cuando hay espacio. */}
      {estado === 'aceptada' && (
        <div className="bg-white rounded-lg border p-3 shrink-0 relative">
          {/* Buscador & filtros — absolute izquierda, no ocupa espacio en el flow */}
          <div ref={searchToolbarRef} className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
              <input
                type="text"
                placeholder="Buscar proveedor..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm w-52"
              />
            </div>
            <MonthRangePicker
              value={filtroFecha}
              onChange={(v) => { setFiltroFecha(v); setPagination(prev => ({ ...prev, page: 1 })); }}
              onRefresh={fetchFacturas}
            />
            <button type="submit" className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm">Buscar</button>
          </div>
          {/* Botones — centrados; el padding-left dinámico (calculado por useLayoutEffect)
              los empuja a la derecha solo si el buscador los traslaparía. */}
          <div ref={toolbarButtonsRef} className="flex items-center justify-center gap-2 flex-wrap">
            {categorizacionSelected.length > 0 && (
              <span className="text-sm font-medium text-purple-600 mr-1">
                {categorizacionSelected.length} sel.
              </span>
            )}
            {canEdit && (
              <ToolbarButton
                icon={Tag}
                label="Categorizar Gasto"
                onClick={() => {
                  const factura = facturas.find(f => f.id === categorizacionSelected[0]);
                  if (factura) openCategorizacionDetalleModal(factura);
                }}
                disabled={categorizacionSelected.length !== 1 || categorizacionLoading}
                variant="purple"
              />
            )}
            {canEdit && (
              <ToolbarButton
                icon={Tags}
                label="Categorizar Múltiples"
                onClick={openCategorizacionModal}
                disabled={categorizacionSelected.length < 2 || categorizacionLoading}
                variant="indigo"
              />
            )}
            <ToolbarSeparator />
            <ToolbarButton
              icon={FileSpreadsheet}
              label="Excel"
              variant="success"
              onClick={exportarExcel}
              disabled={exporting || facturas.length === 0}
            />
            <ToolbarButton
              icon={FileText}
              label="PDF"
              variant="danger"
              onClick={exportarPdf}
              disabled={exporting || facturas.length === 0}
            />
            {(categorizacionLoading || lineasLoading) && (
              <div className="ml-2 flex items-center gap-2 text-sm text-gray-500">
                <Loader size={16} className="animate-spin" />
                Procesando...
              </div>
            )}
          </div>
        </div>
      )}


      {/* CxP Dashboard Cards - KPIs de Pagos */}
      {estado === 'por_pagar' && (
        (() => {
          const hoy = new Date();
          const en8Dias = new Date(hoy.getTime() + 8 * 24 * 60 * 60 * 1000);
          const en15Dias = new Date(hoy.getTime() + 15 * 24 * 60 * 60 * 1000);

          // Calcular totales pendientes por moneda
          const pendientes = { CRC: 0, USD: 0, cantCRC: 0, cantUSD: 0 };
          const vencidos = { CRC: 0, USD: 0, cantCRC: 0, cantUSD: 0 };
          const proximos8 = { CRC: 0, USD: 0, cant: 0 };
          const proximos15 = { CRC: 0, USD: 0, cant: 0 };

          facturas.forEach(f => {
            if (f.estado === 'pagado' || f.estado === 'anulado') return;

            const mon = f.moneda || 'CRC';
            const saldo = parseFloat(f.saldoPendiente || f.totalComprobante) || 0;
            const fechaVenc = f.fechaVencimiento
              ? new Date(f.fechaVencimiento)
              : new Date(new Date(f.fechaEmision).getTime() + 30 * 24 * 60 * 60 * 1000);

            // Total pendiente
            pendientes[mon] = (pendientes[mon] || 0) + saldo;
            if (mon === 'CRC') pendientes.cantCRC++;
            else pendientes.cantUSD++;

            // Vencidas
            if (fechaVenc < hoy) {
              vencidos[mon] = (vencidos[mon] || 0) + saldo;
              if (mon === 'CRC') vencidos.cantCRC++;
              else vencidos.cantUSD++;
            }

            // Próximos a vencer (no vencidas aún)
            if (fechaVenc >= hoy) {
              if (fechaVenc <= en8Dias) {
                proximos8[mon] = (proximos8[mon] || 0) + saldo;
                proximos8.cant++;
              } else if (fechaVenc <= en15Dias) {
                proximos15[mon] = (proximos15[mon] || 0) + saldo;
                proximos15.cant++;
              }
            }
          });

          const totalPendientes = pendientes.cantCRC + pendientes.cantUSD;
          const totalVencidos = vencidos.cantCRC + vencidos.cantUSD;

          return (
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-3 shrink-0">
              {/* Card 1: Total Por Pagar */}
              <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                <div className="px-4 py-2 border-b bg-gradient-to-r from-blue-50 to-blue-100/50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-blue-600 rounded">
                      <DollarSign className="h-4 w-4 text-white" />
                    </div>
                    <h3 className="font-semibold text-sm text-gray-900">Por Pagar</h3>
                  </div>
                  <span className="text-xs text-gray-500">{totalPendientes} fact.</span>
                </div>
                <div className="px-4 py-2 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">CRC</span>
                    <span className="text-sm font-bold text-blue-600">{formatMoney(pendientes.CRC, 'CRC')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">USD</span>
                    <span className="text-sm font-bold text-blue-600">{formatMoney(pendientes.USD, 'USD')}</span>
                  </div>
                </div>
              </div>

              {/* Card 2: Total Vencido */}
              <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                <div className={`px-4 py-2 border-b flex items-center justify-between ${totalVencidos > 0 ? 'bg-gradient-to-r from-red-50 to-red-100/50' : 'bg-gradient-to-r from-gray-50 to-gray-100/50'}`}>
                  <div className="flex items-center gap-2">
                    <div className={`p-1.5 rounded ${totalVencidos > 0 ? 'bg-red-600' : 'bg-gray-400'}`}>
                      <AlertTriangle className="h-4 w-4 text-white" />
                    </div>
                    <h3 className="font-semibold text-sm text-gray-900">Vencido</h3>
                  </div>
                  <span className="text-xs text-gray-500">{totalVencidos > 0 ? `${totalVencidos} fact.` : 'Al día'}</span>
                </div>
                <div className="px-4 py-2 flex items-center justify-between gap-4">
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">CRC</span>
                    <span className={`text-sm font-bold ${vencidos.CRC > 0 ? 'text-red-600' : 'text-gray-400'}`}>{formatMoney(vencidos.CRC, 'CRC')}</span>
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs text-gray-500">USD</span>
                    <span className={`text-sm font-bold ${vencidos.USD > 0 ? 'text-red-600' : 'text-gray-400'}`}>{formatMoney(vencidos.USD, 'USD')}</span>
                  </div>
                </div>
              </div>

              {/* Card 3: Próximos a Vencer */}
              <div className="bg-white rounded-lg border shadow-sm overflow-hidden">
                <div className="px-4 py-2 border-b bg-gradient-to-r from-amber-50 to-amber-100/50 flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="p-1.5 bg-amber-500 rounded">
                      <Clock className="h-4 w-4 text-white" />
                    </div>
                    <h3 className="font-semibold text-sm text-gray-900">Próximos a Vencer</h3>
                  </div>
                </div>
                <div className="px-4 py-2 flex items-center justify-between gap-2">
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">8 días</span>
                    <span className="text-xs text-gray-600">₡{(proximos8.CRC || 0).toLocaleString('es-CR', { maximumFractionDigits: 0 })} / ${(proximos8.USD || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className="text-xs font-medium text-orange-700 bg-orange-100 px-1.5 py-0.5 rounded">15 días</span>
                    <span className="text-xs text-gray-600">₡{(proximos15.CRC || 0).toLocaleString('es-CR', { maximumFractionDigits: 0 })} / ${(proximos15.USD || 0).toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
                  </div>
                </div>
              </div>
            </div>
          );
        })()
      )}

      {/* Toolbar with search, filters and actions - Only for por_pagar */}
      {estado === 'por_pagar' && (
      // pr-[100px] reserva espacio para el grupo Auto+Refresh absolute right,
      // así los botones centrados no se traslapan con él
      <div className="bg-white rounded-lg border pl-4 pr-[100px] py-3 shrink-0 relative">
        {/* Buscador absolute izquierda — no ocupa espacio del flow */}
        <div ref={searchToolbarRef} className="absolute left-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
            <input
              type="text"
              placeholder="Buscar proveedor, factura..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="pl-10 pr-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 text-sm w-52"
            />
          </div>
          <select
            value={estadoFilter}
            onChange={(e) => { setEstadoFilter(e.target.value); setPagination(prev => ({ ...prev, page: 1 })); }}
            className="px-2 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">Todos</option>
            <option value="por_pagar">Por Pagar</option>
            <option value="parcial">Parcial</option>
            <option value="pagado">Pagado</option>
            <option value="anulado">Anulado</option>
          </select>
          <button type="submit" onClick={handleSearch} className="px-3 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 text-sm">Buscar</button>
        </div>

        {/* Auto + Refresh: anclados a la derecha del toolbar */}
        <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-2 z-10">
          <label
            className="flex items-center gap-1 cursor-pointer text-xs text-gray-600 hover:text-gray-800"
            title={autoRefresh
              ? (hasProcessingInvoices ? `Auto-check activo (cada 30s)${lastUpdated ? ' · Últ: ' + dayjs(lastUpdated).format('HH:mm:ss') : ''}` : 'Auto-check habilitado (sin facturas procesando)')
              : 'Auto-check desactivado'
            }
          >
            <input
              type="checkbox"
              checked={autoRefresh}
              onChange={toggleAutoRefresh}
              className="h-3.5 w-3.5 text-primary-600 rounded cursor-pointer"
            />
            <span className={`flex items-center gap-1 ${autoRefresh && hasProcessingInvoices ? 'text-green-600 font-medium' : ''}`}>
              Auto
              {autoRefresh && hasProcessingInvoices && (
                <span className="w-1.5 h-1.5 bg-green-500 rounded-full animate-pulse"></span>
              )}
            </span>
          </label>
          <button
            onClick={() => { setRefreshing(true); fetchFacturas(); }}
            disabled={refreshing || loading}
            className="p-1.5 rounded-lg border border-gray-300 text-gray-500 hover:bg-gray-50 hover:text-gray-700 disabled:opacity-40"
            title={lastUpdated ? `Última actualización: ${dayjs(lastUpdated).format('HH:mm:ss')}` : 'Actualizar'}
          >
            <RefreshCw size={15} className={refreshing ? 'animate-spin' : ''} />
          </button>
        </div>

        {/* Action Buttons - centrados al toolbar (hook empuja a la derecha si traslapa) */}
        <div ref={toolbarButtonsRef} className="flex items-center justify-center gap-2 flex-wrap">
            <ToolbarButton
              icon={Eye}
              label="Ver"
              variant="primary"
              onClick={handleVerSelected}
              disabled={selectedFacturas.length !== 1}
            />
            <ToolbarButton
              icon={DollarSign}
              label="Pagar"
              variant="success"
              onClick={handlePagarSelected}
              disabled={!canPaySelected()}
              badge={selectedFacturas.length > 0 ? selectedFacturas.length : null}
            />
            <ToolbarButton
              icon={Delete}
              label="Eliminar"
              variant="danger"
              onClick={handleEliminarSelected}
              disabled={selectedFacturas.length === 0}
            />
            <ToolbarButton
              icon={FileInput}
              label="Aplicar NC"
              variant="indigoOutline"
              onClick={() => setShowAplicarNCModal(true)}
              disabled={selectedFacturas.length !== 1 || !(['NC', '03'].includes(selectedFacturas[0]?.tipoDocumento))}
            />

            <div className="w-px h-10 bg-gray-300 mx-2" />

            <ToolbarButton
              icon={FileSpreadsheet}
              label="Excel"
              variant="success"
              onClick={exportarExcel}
              disabled={exporting || facturas.length === 0}
            />
            <ToolbarButton
              icon={FileText}
              label="PDF"
              variant="danger"
              onClick={exportarPdf}
              disabled={exporting || facturas.length === 0}
            />
            {config.showUploadPDF && canCreate && (
              <ToolbarButton
                icon={Upload}
                label="Importar PDF"
                variant="dangerOutline"
                onClick={() => setShowUploadPDFModal(true)}
              />
            )}
          </div>

      </div>
      )}

      {/* Column Selector Modal - fuera del overflow:hidden */}
      {showColumnSelector && (
        <>
          <div className="fixed inset-0 z-40" onClick={() => setShowColumnSelector(false)} />
          <div className="fixed top-1/4 right-8 bg-white border border-gray-200 rounded-lg shadow-xl z-50 min-w-[220px]">
            <div className="px-4 py-3 border-b border-gray-100 font-medium text-sm text-gray-700 flex items-center justify-between">
              <span>Columnas visibles</span>
              <button onClick={() => setShowColumnSelector(false)} className="text-gray-400 hover:text-gray-600">
                <X size={16} />
              </button>
            </div>
            <div className="py-2 max-h-80 overflow-y-auto">
              {Object.entries(COLUMN_CONFIG).map(([key, config]) => {
                // Skip columns that don't apply to this view
                if (estado === 'por_pagar' && config.notPorPagar) return null;
                if (estado !== 'por_pagar' && config.porPagarOnly) return null;
                if (estado !== 'por_confirmar' && config.porConfirmarOnly) return null;

                return (
                  <button
                    key={key}
                    onClick={() => !config.fixed && toggleColumn(key)}
                    className={clsx(
                      "w-full flex items-center gap-3 px-4 py-2 text-sm text-left hover:bg-gray-50",
                      config.fixed && "opacity-50 cursor-not-allowed"
                    )}
                  >
                    <span className={clsx(
                      "w-4 h-4 rounded border flex items-center justify-center flex-shrink-0",
                      config.fixed ? "bg-gray-300 border-gray-300" :
                        (columnVisibility[key] ? "bg-blue-600 border-blue-600" : "border-gray-300")
                    )}>
                      {(config.fixed || columnVisibility[key]) && <Check className="h-3 w-3 text-white" />}
                    </span>
                    <span className={config.fixed ? "text-gray-500" : "text-gray-700"}>{config.label}</span>
                    {config.fixed && <span className="text-xs text-gray-400 ml-auto">(fija)</span>}
                  </button>
                );
              })}
            </div>
          </div>
        </>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border overflow-hidden flex-1 min-h-0 flex flex-col">
        {/* Column config bar — también aloja el "Selection info" centrado cuando
            hay facturas seleccionadas. Está fuera del toolbar (que tiene los
            elementos absolute centered verticalmente) así no descuadra search/Auto. */}
        <div ref={tableHeaderRef} className="flex items-center justify-between px-4 py-2 bg-gray-50 border-b shrink-0 relative">
          <span className="text-xs text-gray-500">Arrastra las columnas para reordenar</span>

          {/* Selection info centrado en esta barra (absolute para no afectar el
              espaciamiento entre "Arrastra..." y los botones de la derecha). */}
          {selectedFacturas.length > 0 && (
            <div className="absolute left-1/2 -translate-x-1/2 flex items-center gap-3 text-sm">
              <span className="text-gray-500">
                <span className="font-semibold text-gray-900">{selectedFacturas.length}</span> seleccionada{selectedFacturas.length > 1 ? 's' : ''}
              </span>
              {canPaySelected() && (
                <>
                  <span className="text-gray-300">|</span>
                  <span className={`font-bold ${getSelectedMoneda() === 'USD' ? 'text-blue-600' : 'text-gray-600'}`}>
                    {formatMoney(getSelectedTotal(), getSelectedMoneda())}
                  </span>
                </>
              )}
              <button
                onClick={clearSelection}
                className="flex items-center gap-1 px-2 py-0.5 text-xs text-gray-600 hover:text-gray-800 hover:bg-gray-100 border border-gray-300 rounded transition-colors"
              >
                <X size={12} />
                Limpiar
              </button>
            </div>
          )}

          <div className="flex items-center gap-2">
            <button
              onClick={resetColumns}
              className="text-xs text-blue-600 hover:text-blue-800 flex items-center gap-1"
            >
              <Settings2 size={12} />
              Restablecer orden
            </button>
            {/* Column Selector Button */}
            <button
              onClick={() => setShowColumnSelector(!showColumnSelector)}
              className={clsx(
                "p-1.5 border rounded-lg hover:bg-gray-100",
                showColumnSelector ? "border-blue-500 bg-blue-50" : "border-gray-300"
              )}
              title="Configurar columnas"
            >
              <Settings className="h-4 w-4 text-gray-600" />
            </button>
          </div>
        </div>

        <div className="flex-1 min-h-0 overflow-auto rounded-lg border">
          <table className="w-full">
            <thead className="bg-gray-50 border-b sticky top-0 z-10 shadow-[0_1px_0_rgba(229,231,235,1)]">
              <tr>
                {/* Checkbox column for por_pagar */}
                {estado === 'por_pagar' && (
                  <th className="px-2 py-3 w-10">
                    <button
                      onClick={toggleSelectAllPorPagar}
                      className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                      title={selectedFacturas.length === sortedFacturas.filter(f => f.estado !== 'pagado' && f.estado !== 'anulado').length && selectedFacturas.length > 0 ? 'Deseleccionar todo' : 'Seleccionar todo'}
                    >
                      {selectedFacturas.length === sortedFacturas.filter(f => f.estado !== 'pagado' && f.estado !== 'anulado').length && selectedFacturas.length > 0
                        ? <CheckSquare size={18} className="text-blue-600" />
                        : <Square size={18} />
                      }
                    </button>
                  </th>
                )}
                {/* Checkbox column for batch selection (por_confirmar) */}
                {estado === 'por_confirmar' && (
                  <th className="px-2 py-3 w-10">
                    <button
                      onClick={toggleSelectAll}
                      className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                      title={batchSelected.length === sortedFacturas.filter(f => f.estado === 'por_confirmar').length && batchSelected.length > 0 ? 'Deseleccionar todo' : 'Seleccionar todo'}
                    >
                      {batchSelected.length === sortedFacturas.filter(f => f.estado === 'por_confirmar').length && batchSelected.length > 0
                        ? <CheckSquare size={18} className="text-blue-600" />
                        : <Square size={18} />
                      }
                    </button>
                  </th>
                )}
                {/* Checkbox column for categorización (aceptada) */}
                {estado === 'aceptada' && (
                  <th className="px-2 py-3 w-10">
                    <button
                      onClick={toggleSelectAllCategorizacion}
                      className="p-1 text-gray-600 hover:bg-gray-100 rounded"
                      title={categorizacionSelected.length === sortedFacturas.length && categorizacionSelected.length > 0 ? 'Deseleccionar todo' : 'Seleccionar todo'}
                    >
                      {categorizacionSelected.length === sortedFacturas.length && categorizacionSelected.length > 0
                        ? <CheckSquare size={18} className="text-purple-600" />
                        : <Square size={18} />
                      }
                    </button>
                  </th>
                )}
                {visibleColumns.map(colId => {
                  const col = ALL_COLUMNS.find(c => c.id === colId);
                  if (!col) return null;
                  const isSorted = sortColumn === col.id;
                  return (
                    <th
                      key={col.id}
                      draggable
                      onDragStart={(e) => handleDragStart(e, col.id)}
                      onDragOver={(e) => handleDragOver(e, col.id)}
                      onDragEnd={handleDragEnd}
                      onClick={() => handleSort(col.id)}
                      className={`px-3 py-3 text-left text-xs font-medium text-gray-600 cursor-pointer select-none whitespace-nowrap hover:bg-gray-100 ${
                        draggedColumn === col.id ? 'bg-blue-100' : ''
                      } ${isSorted ? 'bg-blue-50 text-blue-700' : ''}`}
                    >
                      <div className="flex items-center gap-1">
                        <GripVertical size={10} className="text-gray-300 cursor-move" />
                        {col.label}
                        {isSorted ? (
                          sortDirection === 'asc' ? <ArrowUp size={12} /> : <ArrowDown size={12} />
                        ) : (
                          <ArrowUpDown size={12} className="text-gray-300" />
                        )}
                      </div>
                    </th>
                  );
                })}
                {(estado === 'por_confirmar' || estado === 'aceptada') && (
                  <th className="px-3 py-3 text-center text-xs font-medium text-gray-600 w-16">Acciones</th>
                )}
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {loading ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="px-4 py-8 text-center text-gray-500">
                    Cargando...
                  </td>
                </tr>
              ) : sortedFacturas.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="px-4 py-8 text-center text-gray-500">
                    No hay facturas registradas
                  </td>
                </tr>
              ) : (
                sortedFacturas.map((factura) => {
                  const isSelected = selectedFacturas.some(f => f.id === factura.id);
                  const isBatchSelected = batchSelected.includes(factura.id);
                  const isCategorizacionSelected = categorizacionSelected.includes(factura.id);
                  const canSelectPorPagar = factura.estado !== 'pagado' && factura.estado !== 'anulado';

                  // Check if this invoice can be selected for multiple payment (same provider & currency)
                  const firstSelected = selectedFacturas[0];
                  const isCompatibleForPayment = !firstSelected || isSelected || (
                    factura.proveedorId === firstSelected.proveedorId &&
                    factura.moneda === firstSelected.moneda
                  );
                  const canSelectForPayment = canSelectPorPagar && isCompatibleForPayment;

                  // Row click handler based on current view
                  const handleRowClick = () => {
                    if (estado === 'por_pagar' && canSelectForPayment) {
                      toggleFacturaSelection(factura);
                    } else if (estado === 'por_confirmar' && ['por_confirmar', 'rechazada'].includes(factura.estado)) {
                      toggleBatchSelect(factura);
                    } else if (estado === 'aceptada') {
                      toggleCategorizacionSelect(factura.id);
                    }
                  };

                  // Primera fila resaltada por alerta → recibe el ref para auto-scroll
                  const isFirstHighlighted = isHighlighted(factura.id) && matchedIds.values().next().value === factura.id;
                  const hl = isHighlighted(factura.id);
                  return (
                  <tr
                    key={factura.id}
                    ref={isFirstHighlighted ? highlightedRef : null}
                    onClick={handleRowClick}
                    className={clsx(
                      'cursor-pointer',
                      hl && animOn && 'row-highlight-info',
                      hl && !animOn && 'row-highlight-info-static',
                      !hl && (isSelected || isBatchSelected) && 'bg-blue-50 hover:bg-blue-100',
                      // NC se diferencia con fondo naranja claro + borde izquierdo naranja
                      // para que el contador la vea al instante en cualquier lista.
                      !hl && !isSelected && !isBatchSelected && esNC(factura) && 'bg-orange-50/60 border-l-4 border-l-orange-400 hover:bg-orange-100/70',
                      !hl && !isSelected && !isBatchSelected && !esNC(factura) && 'hover:bg-gray-50',
                      !hl && isCategorizacionSelected && 'bg-purple-50',
                      estado === 'por_pagar' && !canSelectForPayment && 'opacity-40 cursor-not-allowed'
                    )}
                  >
                    {/* Checkbox for por_pagar */}
                    {estado === 'por_pagar' && (
                      <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
                        {canSelectForPayment ? (
                          <input
                            type="checkbox"
                            checked={isSelected}
                            onChange={() => toggleFacturaSelection(factura)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500 cursor-pointer"
                          />
                        ) : (
                          <span className="h-4 w-4 inline-block" />
                        )}
                      </td>
                    )}
                    {/* Checkbox for batch selection (por_confirmar y rechazada) */}
                    {estado === 'por_confirmar' && (
                      <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
                        {['por_confirmar', 'rechazada'].includes(factura.estado) && (
                          <input
                            type="checkbox"
                            checked={isBatchSelected}
                            onChange={() => toggleBatchSelect(factura)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                        )}
                      </td>
                    )}
                    {/* Checkbox for categorización (aceptada) */}
                    {estado === 'aceptada' && (
                      <td className="px-2 py-2.5" onClick={(e) => e.stopPropagation()}>
                        <div className="flex items-center gap-2">
                          <input
                            type="checkbox"
                            checked={isCategorizacionSelected}
                            onChange={() => toggleCategorizacionSelect(factura.id)}
                            className="h-4 w-4 text-purple-600 rounded border-gray-300 focus:ring-purple-500"
                          />
                          {/* Categorization status indicator by class */}
                          {(() => {
                            // Determinar color según clase
                            const { todasCategorizadas, lineasBienes = 0, lineasServicios = 0, lineasCapital = 0, lineasCategorizadas = 0, totalLineas = 0 } = factura;

                            if (!todasCategorizadas) {
                              // Sin categorizar completo - amber pulsante
                              return (
                                <span
                                  className="w-2.5 h-2.5 rounded-full bg-amber-500 animate-pulse"
                                  title={`${lineasCategorizadas}/${totalLineas} líneas categorizadas`}
                                />
                              );
                            }

                            // Contar cuántas clases distintas hay
                            const clasesPresentes = [lineasBienes > 0, lineasServicios > 0, lineasCapital > 0].filter(Boolean).length;

                            if (clasesPresentes === 1) {
                              // Una sola clase
                              if (lineasBienes > 0) {
                                return <span className="w-2.5 h-2.5 rounded-full bg-blue-500" title="Bienes" />;
                              } else if (lineasServicios > 0) {
                                return <span className="w-2.5 h-2.5 rounded-full bg-green-500" title="Servicios" />;
                              } else {
                                return <span className="w-2.5 h-2.5 rounded-full bg-purple-500" title="Capital" />;
                              }
                            } else {
                              // Mixto - mostrar indicador con borde degradado
                              return (
                                <span
                                  className="w-2.5 h-2.5 rounded-full bg-gradient-to-r from-blue-500 via-green-500 to-purple-500"
                                  title={`Mixto: ${lineasBienes > 0 ? `B:${lineasBienes} ` : ''}${lineasServicios > 0 ? `S:${lineasServicios} ` : ''}${lineasCapital > 0 ? `K:${lineasCapital}` : ''}`}
                                />
                              );
                            }
                          })()}
                        </div>
                      </td>
                    )}
                    {visibleColumns.map(colId => (
                      <td key={colId} className="px-3 py-2.5 whitespace-nowrap">
                        {renderCell(factura, colId)}
                      </td>
                    ))}
                    {(estado === 'por_confirmar' || estado === 'aceptada') && (
                      <td className="px-3 py-2.5 text-center" onClick={(e) => e.stopPropagation()}>
                        {factura.hasPdf && (
                          <button
                            onClick={() => handleViewFactura(factura)}
                            className="flex items-center gap-1 px-2 py-1 text-xs text-green-600 border border-green-300 rounded hover:bg-green-50"
                          >
                            <Eye size={12} />
                            Ver
                          </button>
                        )}
                      </td>
                    )}
                  </tr>
                  );
                })
              )}
            </tbody>

            {/* Totales sticky al pie. Una fila por moneda con Subtotal/Impuesto/Total
                en moneda original. Base CRC e IVA CRC son sumas GLOBALES (todas las
                monedas convertidas) y se muestran solo en la PRIMERA fila — están en
                sus columnas correspondientes así que el contexto se entiende solo. */}
            {Object.keys(totalesPorMoneda).length > 0 && estado !== 'por_pagar' && (() => {
              const totalBaseCRC = Object.values(totalesPorMoneda).reduce((s, t) => s + (t.baseCRC || 0), 0);
              const totalIvaCRC = Object.values(totalesPorMoneda).reduce((s, t) => s + (t.ivaCRC || 0), 0);
              const monedas = Object.entries(totalesPorMoneda);
              return (
                <tfoot className="sticky bottom-0 z-10 bg-gray-100 border-t-2 border-gray-300 shadow-[0_-1px_0_rgba(156,163,175,1)]">
                  {monedas.map(([moneda, totals], idx) => {
                    const esPrimera = idx === 0;
                    return (
                      <tr key={moneda}>
                        {(estado === 'por_pagar' || estado === 'por_confirmar' || estado === 'aceptada') && (
                          <td className="px-2 py-1 w-10"></td>
                        )}
                        {visibleColumns.map(colId => {
                          switch (colId) {
                            case 'subtotal':
                              return (
                                <td key={colId} className="px-3 py-1 text-left font-semibold text-gray-700 text-sm">
                                  {formatMoney(totals.subtotal, moneda)}
                                </td>
                              );
                            case 'descuento':
                              return <td key={colId} className="px-3 py-1 text-left text-gray-400 text-sm">-</td>;
                            case 'tarifa':
                              return <td key={colId} className="px-3 py-1"></td>;
                            case 'impuesto':
                              return (
                                <td key={colId} className="px-3 py-1 text-left font-semibold text-gray-700 text-sm">
                                  {formatMoney(totals.impuestos, moneda)}
                                </td>
                              );
                            case 'total':
                              return (
                                <td key={colId} className="px-3 py-1 text-left font-bold text-gray-900 text-sm">
                                  {formatMoney(totals.total, moneda)}
                                </td>
                              );
                            case 'baseCRC':
                              // Suma global de todas las monedas. Solo en la primera fila,
                              // alineada en la misma línea que el primer "Total CRC".
                              return (
                                <td key={colId} className="px-3 py-1 text-left font-bold text-gray-900 text-sm">
                                  {esPrimera ? `₡${totalBaseCRC.toLocaleString('es-CR', { minimumFractionDigits: 2 })}` : ''}
                                </td>
                              );
                            case 'ivaCRC':
                              return (
                                <td key={colId} className="px-3 py-1 text-left font-bold text-gray-900 text-sm">
                                  {esPrimera ? `₡${totalIvaCRC.toLocaleString('es-CR', { minimumFractionDigits: 2 })}` : ''}
                                </td>
                              );
                            default:
                              return <td key={colId} className="px-3 py-1"></td>;
                          }
                        })}
                        {(estado === 'por_confirmar' || estado === 'aceptada') && (
                          <td className="px-3 py-1"></td>
                        )}
                      </tr>
                    );
                  })}
                </tfoot>
              );
            })()}
          </table>
        </div>

        {/* Paginación eliminada — la tabla tiene scroll interno sticky thead/tfoot */}
      </div>

      {/* Upload XML Modal */}
      {showUploadXMLModal && (
        <UploadXMLModal
          onAbrirMultiple={() => { setShowUploadXMLModal(false); setShowImportacionMultiple(true); }}
          onClose={() => setShowUploadXMLModal(false)}
          onSuccess={() => {
            setShowUploadXMLModal(false);
            fetchFacturas();
          }}
        />
      )}

      {/* Importación múltiple: carpeta del proveedor (XML + respuesta de Hacienda + PDF por factura) */}
      {showImportacionMultiple && (
        <ImportacionMultipleModal
          empresa={currentEmisor}
          ConfirmarTodas={BatchAceptarModal}
          onClose={() => setShowImportacionMultiple(false)}
          onSuccess={() => { setShowImportacionMultiple(false); fetchFacturas(); }}
        />
      )}

      {/* Aplicar NC Modal */}
      {showAplicarNCModal && selectedFacturas.length === 1 && (
        <AplicarNCModal
          nc={selectedFacturas[0]}
          onClose={() => setShowAplicarNCModal(false)}
          onSuccess={() => {
            setShowAplicarNCModal(false);
            setSelectedFacturas([]);
            fetchFacturas();
          }}
        />
      )}

      {/* Upload PDF Modal */}
      {showUploadPDFModal && (
        <UploadPDFModal
          onClose={() => setShowUploadPDFModal(false)}
          onSuccess={() => {
            setShowUploadPDFModal(false);
            fetchFacturas();
          }}
        />
      )}

      {/* Apply Payment Modal */}
      {showPagoModal && (
        <PagoModal
          factura={selectedFacturas.length === 1 ? selectedFacturas[0] : selectedFactura}
          facturas={selectedFacturas}
          isMultiple={selectedFacturas.length > 1}
          bancos={bancos}
          onClose={() => {
            setShowPagoModal(false);
            setSelectedFactura(null);
          }}
          onSuccess={(notifInfo) => {
            setShowPagoModal(false);
            setSelectedFactura(null);
            setSelectedFacturas([]);
            fetchFacturas();
            // Solo en CxP (por_pagar) abrimos la notificación al proveedor automáticamente
            if (estado === 'por_pagar' && notifInfo && notifInfo.proveedorId) {
              setNotifPagoData(notifInfo);
            }
          }}
        />
      )}

      {/* Notificación de pago al proveedor (CxP) */}
      {notifPagoData && (
        <NotificarPagoProveedorModal
          info={notifPagoData}
          emisor={currentEmisor}
          onClose={() => setNotifPagoData(null)}
        />
      )}

      {esperandoHacienda.length > 0 && !showBatchAceptarModal && !showBatchRechazarModal && !showAceptarModal && (
        verEnHacienda
          ? <EnHaciendaModal facturas={esperandoHacienda} onMinimizar={() => setVerEnHacienda(false)} />
          : <ProcesoMinimizado texto={`${esperandoHacienda.length} en Hacienda`} onAbrir={() => setVerEnHacienda(true)} />
      )}

      {/* Accept Invoice Modal (Credito vs Contado vs Parcial) */}
      {showAceptarModal && selectedFactura && (
        <AceptarFacturaModal
          factura={selectedFactura}
          onActualizar={() => fetchFacturas({ silencioso: true })}
          onClose={() => {
            setShowAceptarModal(false);
            setSelectedFactura(null);
          }}
          onSuccess={() => {
            setShowAceptarModal(false);
            setSelectedFactura(null);
            fetchFacturas();
          }}
        />
      )}

      {/* Quién pagó la compra de contado */}
      {pagadoPorModal && (
        <PagadoPorModal
          compraId={pagadoPorModal.factura.id}
          titulo={`${pagadoPorModal.factura.proveedorNombre} · ${pagadoPorModal.factura.fecNumeroFacturaProveedor || pagadoPorModal.factura.numeroConsecutivo}`}
          total={parseFloat(pagadoPorModal.factura.totalComprobante) || 0}
          moneda={pagadoPorModal.factura.moneda}
          inicial={pagadoPorModal}
          puedePasarACredito={!(pagadoPorModal.factura.condicionCorregida || pagadoPorModal.factura.condicion_corregida)}
          onPasarACredito={() => { const fac = pagadoPorModal.factura; setPagadoPorModal(null); setCorregirModal({ factura: fac, nueva: 'credito' }); }}
          onClose={() => setPagadoPorModal(null)}
          onGuardado={() => { setPagadoPorModal(null); fetchFacturas(); }}
        />
      )}

      {/* Corregir Condición Modal */}
      {corregirModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setCorregirModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b bg-amber-50 rounded-t-xl">
              <h3 className="text-lg font-semibold text-amber-800 flex items-center gap-2">
                <AlertTriangle className="h-5 w-5" />
                Corregir Condición de Aceptación
              </h3>
              <button onClick={() => setCorregirModal(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-5 space-y-4">
              <div className="bg-gray-50 rounded-lg p-3">
                <p className="text-sm text-gray-500">Factura:</p>
                <p className="font-mono font-medium text-gray-900">{corregirModal.factura.numeroConsecutivo}</p>
                <p className="text-sm text-gray-600">{corregirModal.factura.proveedorNombre}</p>
                <p className="text-sm font-semibold mt-1">{formatMoney(corregirModal.factura.totalComprobante, corregirModal.factura.moneda)}</p>
              </div>

              <div className="flex items-center justify-center gap-3">
                <span className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${corregirModal.factura.condicionVenta === '01' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                  {corregirModal.factura.condicionVenta === '01' ? 'Contado' : 'Crédito'}
                </span>
                <span className="text-gray-400 text-lg">→</span>
                <span className={`px-3 py-1.5 rounded-lg text-sm font-semibold ${corregirModal.nueva === 'contado' ? 'bg-green-100 text-green-700' : 'bg-blue-100 text-blue-700'}`}>
                  {corregirModal.nueva === 'contado' ? 'Contado' : 'Crédito'}
                </span>
              </div>

              <div className={`p-3 rounded-lg border text-sm ${corregirModal.nueva === 'credito' ? 'bg-blue-50 border-blue-200 text-blue-800' : 'bg-green-50 border-green-200 text-green-800'}`}>
                {corregirModal.nueva === 'credito' ? (
                  <ul className="space-y-1">
                    <li>• Se creará una <strong>Cuenta por Pagar</strong> por {formatMoney(corregirModal.factura.totalComprobante, corregirModal.factura.moneda)}</li>
                    <li>• La factura quedará con estado <strong>Por Pagar</strong></li>
                    <li>• Se eliminará el registro de pago automático (si existe)</li>
                  </ul>
                ) : (
                  <ul className="space-y-1">
                    <li>• Se eliminará la <strong>Cuenta por Pagar</strong> asociada</li>
                    <li>• La factura se marcará como <strong>Pagada</strong></li>
                    <li>• No se puede hacer si ya tiene pagos parciales aplicados</li>
                  </ul>
                )}
              </div>

              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg flex items-start gap-2 text-sm text-amber-800">
                <AlertTriangle className="h-4 w-4 flex-shrink-0 mt-0.5" />
                <p><strong>Esta corrección solo se puede realizar una vez.</strong> Verifique que la condición sea la correcta antes de continuar.</p>
              </div>
            </div>

            <div className="flex justify-end gap-3 p-4 border-t bg-gray-50 rounded-b-xl">
              <button onClick={() => setCorregirModal(null)} disabled={corrigiendo}
                className="px-4 py-2 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-100">
                Cancelar
              </button>
              <button
                onClick={async () => {
                  setCorrigiendo(true);
                  try {
                    await gastosApi.corregirCondicion(corregirModal.factura.id, corregirModal.nueva);
                    toast.success(`Condición corregida a ${corregirModal.nueva}`);
                    setCorregirModal(null);
                    fetchFacturas();
                  } catch (err) {
                    toast.error(err.response?.data?.error || 'Error al corregir');
                  } finally {
                    setCorrigiendo(false);
                  }
                }}
                disabled={corrigiendo}
                className="flex items-center gap-2 px-4 py-2 border-2 border-amber-500 text-amber-600 rounded-lg hover:bg-amber-50 disabled:opacity-50"
              >
                {corrigiendo ? <RefreshCw className="animate-spin" size={16} /> : <Check size={16} />}
                {corrigiendo ? 'Corrigiendo...' : 'Confirmar Corrección'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Rechazar Factura Modal */}
      {showRechazarModal && selectedFactura && (
        <RechazarFacturaModal
          factura={selectedFactura}
          onClose={() => {
            setShowRechazarModal(false);
            setSelectedFactura(null);
          }}
          onSuccess={() => {
            setShowRechazarModal(false);
            setSelectedFactura(null);
            fetchFacturas();
          }}
        />
      )}

      {/* Batch Rechazar Modal */}
      {showBatchRechazarModal && (
        <BatchRechazarModal
          count={batchSelected.length}
          onActualizar={() => fetchFacturas({ silencioso: true })}
          onClose={() => setShowBatchRechazarModal(false)}
          onSuccess={() => {
            setShowBatchRechazarModal(false);
            setBatchSelected([]);
            fetchFacturas();
          }}
          facturaIds={batchSelected}
          facturas={facturas}
        />
      )}

      {/* Batch Aceptar Modal */}
      {cumplimientoFactura && (
        <CumplimientoModal
          factura={cumplimientoFactura}
          onClose={() => setCumplimientoFactura(null)}
          onConfirmar={() => { const f = cumplimientoFactura; setCumplimientoFactura(null); handleAccept(f); }}
          onPasado={() => { setCumplimientoFactura(null); fetchFacturas(); }}
        />
      )}

      {showBatchAceptarModal && (
        <BatchAceptarModal
          count={batchSelected.length}
          onActualizar={() => fetchFacturas({ silencioso: true })}
          onClose={() => setShowBatchAceptarModal(false)}
          onSuccess={() => {
            setShowBatchAceptarModal(false);
            setBatchSelected([]);
            fetchFacturas();
          }}
          facturaIds={batchSelected}
          facturas={facturas}
        />
      )}

      {/* Factura Preview Modal (PDF-like view) */}
      {showPreviewModal && (
        <FacturaPreviewModal
          factura={facturaDetalle}
          loading={loadingDetalle}
          onClose={() => {
            setShowPreviewModal(false);
            setSelectedFactura(null);
            setFacturaDetalle(null);
          }}
        />
      )}

      {/* Modal Mensaje Hacienda */}
      {mensajeHaciendaModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50" onClick={() => setMensajeHaciendaModal(null)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center justify-between p-4 border-b">
              <h3 className="text-lg font-semibold text-gray-900">{mensajeHaciendaModal.alProveedor ? 'Hacienda rechazó la factura del proveedor' : 'Respuesta de Hacienda'}</h3>
              <button onClick={() => setMensajeHaciendaModal(null)} className="text-gray-400 hover:text-gray-600">
                <X size={20} />
              </button>
            </div>
            <div className="p-4">
              <div className="mb-3 text-sm text-gray-600">
                <strong>Factura:</strong> {mensajeHaciendaModal.factura?.numeroConsecutivo}
              </div>
              <div className="mb-3 text-sm text-gray-600">
                <strong>Proveedor:</strong> {mensajeHaciendaModal.factura?.proveedorNombre}
              </div>
              {/* Solo lo que dice Hacienda, un motivo por línea, separando lo que rechaza de lo que solo advierte */}
              {(() => {
                const motivos = motivosHacienda(mensajeHaciendaModal.mensaje);
                const rechazos = motivos.filter(m => !m.advertencia), avisos = motivos.filter(m => m.advertencia);
                const Lista = ({ titulo, items, color }) => items.length > 0 && (
                  <div className="mb-3">
                    <p className={`text-xs font-semibold uppercase tracking-wide mb-1.5 ${color}`}>{titulo}</p>
                    <ul className="border border-gray-200 rounded-lg p-3 space-y-2 list-disc pl-7 text-sm text-gray-800 select-text">
                      {items.map((m, i) => <li key={i}>{m.texto}{m.codigo && <span className="text-gray-400 text-xs"> ({m.codigo})</span>}</li>)}
                    </ul>
                  </div>
                );
                return (
                  <div className="max-h-72 overflow-y-auto">
                    <Lista titulo={mensajeHaciendaModal.alProveedor ? 'Por esto la rechazó' : 'Errores'} items={rechazos} color="text-red-700" />
                    <Lista titulo="Advertencias (no rechazan la factura)" items={avisos} color="text-amber-700" />
                  </div>
                );
              })()}
              {mensajeHaciendaModal.alProveedor && (
                <p className="mt-2 text-xs text-gray-600">La factura no es válida para Hacienda: pídale al proveedor una nueva.</p>
              )}
            </div>
            <div className="flex justify-end gap-2 p-4 border-t bg-gray-50 rounded-b-xl">
              <button
                onClick={() => {
                  navigator.clipboard.writeText(motivosHacienda(mensajeHaciendaModal.mensaje).map(m => `${m.codigo ? m.codigo + ' ' : ''}${m.texto}`).join('\n'));
                  toast.success('Mensaje copiado');
                }}
                className="px-4 py-2 text-sm border border-blue-500 text-blue-600 rounded-lg hover:bg-blue-50"
              >
                Copiar Mensaje
              </button>
              <button
                onClick={() => setMensajeHaciendaModal(null)}
                className="px-4 py-2 text-sm border border-red-500 text-red-600 rounded-lg hover:bg-red-50"
              >
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Confirm Modal */}
      <ConfirmModal
        isOpen={!!confirmModal}
        onClose={() => setConfirmModal(null)}
        onConfirm={confirmModal?.onConfirm}
        title={confirmModal?.title}
        message={confirmModal?.message}
        confirmText={confirmModal?.confirmText}
        variant={confirmModal?.variant}
      />


      {/* Modal Categorización de Gastos */}
      {showCategorizacionModal && categorias && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-lg shadow-xl w-full max-w-lg">
            {/* Header */}
            <div className="flex items-center justify-between px-6 py-4 border-b">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Categorizar Múltiples Gastos</h2>
                <p className="text-sm text-gray-500">
                  Aplicar misma categorización a {categorizacionSelected.length} factura{categorizacionSelected.length > 1 ? 's' : ''}
                </p>
              </div>
              <button
                onClick={() => setShowCategorizacionModal(false)}
                className="text-gray-400 hover:text-gray-600"
              >
                <X size={24} />
              </button>
            </div>

            {/* Form */}
            <div className="p-6 space-y-4">
              {/* Tipo de Movimiento */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tipo de Movimiento
                </label>
                <select
                  value={categorizacionForm.tipo_movimiento}
                  onChange={(e) => setCategorizacionForm(prev => ({ ...prev, tipo_movimiento: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  {categorias.tiposMovimiento.map(t => (
                    <option key={t.codigo} value={t.codigo}>{t.nombre}</option>
                  ))}
                </select>
              </div>

              {/* Clase */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Clase
                </label>
                <select
                  value={categorizacionForm.clase}
                  onChange={(e) => setCategorizacionForm(prev => ({ ...prev, clase: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  {categorias.clases.map(c => (
                    <option key={c.codigo} value={c.codigo}>{c.nombre}</option>
                  ))}
                </select>
              </div>

              {/* Tarifa IVA */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Tarifa IVA
                </label>
                <select
                  value={categorizacionForm.tarifa_iva_categoria}
                  onChange={(e) => setCategorizacionForm(prev => ({ ...prev, tarifa_iva_categoria: e.target.value }))}
                  className="w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-purple-500 focus:border-purple-500"
                >
                  {categorias.tarifasIva.map(t => (
                    <option key={t.codigo} value={t.codigo}>{t.nombre}</option>
                  ))}
                </select>
              </div>

              {/* Identificación */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Identificación
                </label>
                <div className="space-y-2">
                  {categorias.identificaciones.map(i => (
                    <label key={i.codigo} className="flex items-center gap-3 cursor-pointer">
                      <input
                        type="radio"
                        name="identificacion"
                        value={i.codigo}
                        checked={categorizacionForm.identificacion === i.codigo}
                        onChange={(e) => setCategorizacionForm(prev => ({ ...prev, identificacion: e.target.value }))}
                        className="h-4 w-4 text-purple-600 border-gray-300 focus:ring-purple-500"
                      />
                      <span className="text-sm text-gray-700">{i.nombre}</span>
                    </label>
                  ))}
                </div>
              </div>

              {/* Info box */}
              <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                <p className="text-sm text-purple-800">
                  {categorizacionForm.identificacion === 'E' ? (
                    <><strong>Crédito Total:</strong> El 100% del IVA pagado será acreditable.</>
                  ) : (
                    <><strong>Según Proporcionalidad:</strong> El crédito fiscal se calculará según la prorrata de la empresa.</>
                  )}
                </p>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => setShowCategorizacionModal(false)}
                className="px-4 py-2 border border-red-500 text-red-600 rounded-lg hover:bg-red-50 transition-colors flex items-center justify-center gap-2 font-medium"
              >
                <X size={16} />
                Cancelar
              </button>
              <button
                onClick={handleCategorizarBatch}
                disabled={categorizacionLoading}
                className="px-4 py-2 border border-purple-500 text-purple-600 rounded-lg hover:bg-purple-50 transition-colors disabled:opacity-50 flex items-center gap-2 font-medium"
              >
                {categorizacionLoading ? (
                  <>
                    <Loader size={16} className="animate-spin" />
                    Procesando...
                  </>
                ) : (
                  <>
                    <Tags size={16} />
                    Aplicar Categorización
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Categorización Individual con Líneas */}
      {showCategorizacionDetalleModal && facturaCategorizacion && categorias && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-7xl max-h-[90vh] overflow-hidden flex flex-col">
            {/* Header */}
            <div className="px-6 py-4 border-b flex justify-between items-center bg-gradient-to-r from-purple-50 to-indigo-50">
              <div>
                <h2 className="text-lg font-bold text-gray-900">Categorizar Gasto</h2>
                <p className="text-sm text-gray-500">Factura {facturaCategorizacion.numeroConsecutivo}</p>
              </div>
              <button
                onClick={() => {
                  setShowCategorizacionDetalleModal(false);
                  setFacturaCategorizacion(null);
                  setLineasCategorizacion([]);
                  setEditingLineaId(null);
                  setCategorizacionSelected([]); // Limpiar selección
                  fetchFacturas(); // Refresh para ver cambios
                }}
                className="p-2 hover:bg-gray-100 rounded-lg"
              >
                <X size={20} />
              </button>
            </div>

            {/* Content */}
            <div className="flex-1 overflow-auto p-6 space-y-6">
              {/* Info de la Factura */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4 bg-gray-50 rounded-lg p-4">
                <div>
                  <p className="text-xs text-gray-500 mb-1">Fecha</p>
                  <p className="text-sm font-medium">{dayjs(facturaCategorizacion.fechaEmision).format('DD/MM/YYYY HH:mm')}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Tipo Documento</p>
                  <p className="text-sm font-medium">{facturaCategorizacion.tipoDocumento === 'FE' ? 'Factura Electrónica' : facturaCategorizacion.tipoDocumento}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500 mb-1">Proveedor</p>
                  <p className="text-sm font-medium">{facturaCategorizacion.proveedorNombre}</p>
                </div>
                <div className="col-span-2">
                  <p className="text-xs text-gray-500 mb-1">Clave</p>
                  <p className="text-xs font-mono text-gray-600 break-all">{facturaCategorizacion.clave || '-'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Condición Venta</p>
                  <p className="text-sm font-medium">{facturaCategorizacion.condicionVenta === '01' ? 'Contado' : 'Crédito'}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-1">Divisa</p>
                  <p className="text-sm font-medium">{facturaCategorizacion.moneda} {facturaCategorizacion.moneda !== 'CRC' && `(T/C: ${facturaCategorizacion.tipoCambio})`}</p>
                </div>
              </div>

              {/* Totales */}
              <div className="grid grid-cols-4 gap-4 text-right">
                <div className="bg-white border rounded-lg p-3">
                  <p className="text-xs text-gray-500">Subtotal</p>
                  <p className="text-lg font-bold">{formatMoney(facturaCategorizacion.totalVenta || (facturaCategorizacion.totalGravado || 0) + (facturaCategorizacion.totalExento || 0) + (facturaCategorizacion.totalExonerado || 0) + (facturaCategorizacion.totalNoSujeto || 0), facturaCategorizacion.moneda)}</p>
                </div>
                <div className="bg-white border rounded-lg p-3">
                  <p className="text-xs text-gray-500">Descuento</p>
                  <p className="text-lg font-bold text-gray-400">-</p>
                </div>
                <div className="bg-white border rounded-lg p-3">
                  <p className="text-xs text-gray-500">Impuestos</p>
                  <p className="text-lg font-bold text-amber-600">{formatMoney(facturaCategorizacion.totalImpuesto, facturaCategorizacion.moneda)}</p>
                </div>
                <div className="bg-purple-50 border border-purple-200 rounded-lg p-3">
                  <p className="text-xs text-purple-600">Total</p>
                  <p className="text-lg font-bold text-purple-700">{formatMoney(facturaCategorizacion.totalComprobante, facturaCategorizacion.moneda)}</p>
                </div>
              </div>

              {/* Detalle de Líneas */}
              <div>
                <h3 className="text-sm font-semibold text-gray-700 mb-3">Detalle de Gastos</h3>
                <div className="border rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">CABYS</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Descripción</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Cant.</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">P. Unit.</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">Subtotal</th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">Tarifa</th>
                        <th className="px-3 py-2 text-right text-xs font-semibold text-gray-600">IVA</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Tipo Mov.</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Clase</th>
                        <th className="px-3 py-2 text-left text-xs font-semibold text-gray-600">Identificación</th>
                        <th className="px-3 py-2 text-center text-xs font-semibold text-gray-600">Acción</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200 bg-white">
                      {lineasCategorizacion.map((linea) => {
                        const cabysCode = linea.codigo_cabys || linea.codigo || '';
                        const firstDigit = String(cabysCode).charAt(0);
                        const esBien = ['0', '1', '2', '3', '4'].includes(firstDigit);
                        return (
                        <tr key={linea.id}>
                          <td className="px-3 py-2 text-xs">
                            <div className="font-mono text-gray-600 text-[11px]">{cabysCode || '—'}</div>
                            <span className={`text-[10px] px-1 rounded ${esBien ? 'bg-blue-100 text-blue-600' : 'bg-green-100 text-green-600'}`}>
                              {esBien ? 'Bien' : 'Servicio'}
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-900 max-w-[200px] truncate" title={linea.detalle}>{linea.detalle}</td>
                          <td className="px-3 py-2 text-xs text-right">{linea.cantidad}</td>
                          <td className="px-3 py-2 text-xs text-right">{formatMoney(linea.precio_unitario, facturaCategorizacion.moneda)}</td>
                          <td className="px-3 py-2 text-xs text-right">{formatMoney(linea.monto_total, facturaCategorizacion.moneda)}</td>
                          <td className="px-3 py-2 text-xs text-center">
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-700 rounded text-xs">
                              {linea.impuesto_tarifa || 0}%
                            </span>
                          </td>
                          <td className="px-3 py-2 text-xs text-right font-medium text-amber-600">{formatMoney(linea.impuesto_monto, facturaCategorizacion.moneda)}</td>

                          {/* Categorización - siempre visible */}
                          <td className="px-2 py-1">
                            <select
                              value={linea.tipo_movimiento}
                              onChange={(e) => updateLineaField(linea.id, 'tipo_movimiento', e.target.value)}
                              className="w-full text-xs border rounded px-1 py-1"
                            >
                              {categorias.tiposMovimiento.map(t => (
                                <option key={t.codigo} value={t.codigo}>{t.nombre}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1">
                            <select
                              value={linea.clase}
                              onChange={(e) => updateLineaField(linea.id, 'clase', e.target.value)}
                              className="w-full text-xs border rounded px-1 py-1"
                            >
                              {categorias.clases.map(c => (
                                <option key={c.codigo} value={c.codigo}>{c.nombre}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1">
                            <select
                              value={linea.identificacion}
                              onChange={(e) => updateLineaField(linea.id, 'identificacion', e.target.value)}
                              className="w-full text-xs border rounded px-1 py-1"
                            >
                              {categorias.identificaciones.map(i => (
                                <option key={i.codigo} value={i.codigo}>{i.nombre}</option>
                              ))}
                            </select>
                          </td>
                          <td className="px-2 py-1">
                            <div className="flex items-center gap-1 justify-center">
                              {/* Guardar esta línea */}
                              <button
                                onClick={() => handleSaveLineaCategorizacion(linea)}
                                className={`p-1.5 rounded ${linea.categorizado ? 'bg-green-600 text-white' : 'bg-green-100 text-green-600 hover:bg-green-600 hover:text-white'} transition-colors`}
                                title="Guardar categorización"
                              >
                                <Check size={14} />
                              </button>
                              {/* Aplicar a líneas con mismo CABYS */}
                              {lineasCategorizacion.filter(l => (l.codigo_cabys || l.codigo) === cabysCode).length > 1 && (
                                <button
                                  onClick={async () => {
                                    const similares = lineasCategorizacion.filter(l => (l.codigo_cabys || l.codigo) === cabysCode);
                                    try {
                                      setCategorizacionLoading(true);
                                      // Aplicar a todas las líneas con mismo CABYS
                                      for (const sim of similares) {
                                        await gastosApi.categorizarLinea(facturaCategorizacion.id, sim.id, {
                                          tipo_movimiento: linea.tipo_movimiento,
                                          clase: linea.clase,
                                          identificacion: linea.identificacion,
                                          tarifa_iva_categoria: linea.tarifa_iva_categoria
                                        });
                                      }
                                      toast.success(`Categorización aplicada a ${similares.length} líneas con CABYS ${cabysCode}`);
                                      // Actualizar estado local
                                      setLineasCategorizacion(prev => prev.map(l =>
                                        (l.codigo_cabys || l.codigo) === cabysCode
                                          ? { ...l, tipo_movimiento: linea.tipo_movimiento, clase: linea.clase, identificacion: linea.identificacion, categorizado: true }
                                          : l
                                      ));
                                    } catch (error) {
                                      toast.error('Error al aplicar categorización');
                                    } finally {
                                      setCategorizacionLoading(false);
                                    }
                                  }}
                                  disabled={categorizacionLoading}
                                  className="p-1.5 rounded bg-indigo-100 text-indigo-600 hover:bg-indigo-600 hover:text-white transition-colors disabled:opacity-50"
                                  title={`Aplicar a ${lineasCategorizacion.filter(l => (l.codigo_cabys || l.codigo) === cabysCode).length} líneas con mismo CABYS`}
                                >
                                  <Tags size={14} />
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );})}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="px-6 py-4 border-t bg-gray-50 flex justify-end gap-3">
              <button
                onClick={() => {
                  setShowCategorizacionDetalleModal(false);
                  setFacturaCategorizacion(null);
                  setLineasCategorizacion([]);
                  setEditingLineaId(null);
                  setCategorizacionSelected([]); // Limpiar selección
                  // Refresh the list
                  fetchFacturas();
                }}
                className="px-4 py-2 border border-gray-400 text-gray-600 rounded-lg hover:bg-gray-100 transition-colors flex items-center justify-center gap-2 font-medium"
              >
                <Check size={16} />
                Cerrar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Chip flotante centrado al viewport (fixed) — solo cuando hay highlight activo */}
      <AlertHighlightChip
        highlightKey={highlightKey}
        hasMatches={hasMatches}
        count={matchedIds.size}
        onClose={clearHighlight}
        anchorRef={tableHeaderRef}
      />
    </div>
  );
}

// Upload XML Modal Component - Supports multiple files
function UploadXMLModal({ onClose, onSuccess, onAbrirMultiple }) {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [currentIndex, setCurrentIndex] = useState(0);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (files.length === 0) return;

    setLoading(true);
    setResults([]);
    const newResults = [];

    for (let i = 0; i < files.length; i++) {
      setCurrentIndex(i);
      const file = files[i];

      try {
        const formData = new FormData();
        formData.append('xml', file);

        const response = await gastosApi.uploadXML(formData);
        newResults.push({
          fileName: file.name,
          success: response.data.success,
          proveedor: response.data.proveedor?.nombre,
          consecutivo: response.data.data?.numeroConsecutivo,
          error: null
        });
      } catch (error) {
        newResults.push({
          fileName: file.name,
          success: false,
          error: error.response?.data?.error || error.message
        });
      }

      setResults([...newResults]);
    }

    setLoading(false);

    const exitosos = newResults.filter(r => r.success).length;
    if (exitosos > 0) {
      toast.success(`${exitosos} de ${files.length} facturas importadas`);
      setTimeout(onSuccess, 1500);
    } else {
      toast.error('No se pudo importar ninguna factura');
    }
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Importar Facturas XML</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {onAbrirMultiple && (
            <button type="button" onClick={onAbrirMultiple}
              className="w-full text-left rounded-xl border border-indigo-300 bg-indigo-50/60 hover:bg-indigo-50 px-4 py-3 transition">
              <span className="block text-sm font-semibold text-indigo-800">Importación múltiple</span>
              <span className="block text-xs text-indigo-700/80 mt-0.5">Carpeta completa del proveedor: cada factura con su XML, su respuesta de Hacienda y su PDF</span>
            </button>
          )}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Archivos XML
            </label>
            <input
              type="file"
              accept=".xml"
              multiple
              onChange={(e) => setFiles(Array.from(e.target.files || []))}
              className="w-full border rounded-lg p-2 text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Puedes seleccionar múltiples archivos XML (Ctrl+Click o Shift+Click)
            </p>
            {files.length > 0 && (
              <p className="mt-2 text-sm text-primary-600 font-medium">
                {files.length} archivo{files.length > 1 ? 's' : ''} seleccionado{files.length > 1 ? 's' : ''}
              </p>
            )}
          </div>

          {loading && (
            <div className="p-4 bg-blue-50 rounded-lg">
              <div className="flex items-center gap-2 text-blue-700">
                <Loader className="animate-spin" size={16} />
                <span>Procesando {currentIndex + 1} de {files.length}...</span>
              </div>
            </div>
          )}

          {results.length > 0 && (
            <div className="max-h-48 overflow-y-auto space-y-2">
              {results.map((r, idx) => (
                <div
                  key={idx}
                  className={`p-3 rounded-lg text-sm ${r.success ? 'bg-green-50 text-green-700' : 'bg-red-50 text-red-700'}`}
                >
                  <div className="flex items-center gap-2">
                    {r.success ? <CheckCircle size={16} /> : <XCircle size={16} />}
                    <span className="font-medium truncate">{r.fileName}</span>
                  </div>
                  {r.success ? (
                    <p className="text-xs mt-1 ml-6">{r.proveedor} - {r.consecutivo}</p>
                  ) : (
                    <p className="text-xs mt-1 ml-6">{r.error}</p>
                  )}
                </div>
              ))}
            </div>
          )}

          <div className="flex justify-end gap-3 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
            >
              {results.length > 0 ? 'Cerrar' : 'Cancelar'}
            </button>
            {results.length === 0 && (
              <button
                type="submit"
                disabled={files.length === 0 || loading}
                className="px-4 py-2 border border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50"
              >
                {loading ? 'Importando...' : `Importar ${files.length > 0 ? `(${files.length})` : ''}`}
              </button>
            )}
          </div>
        </form>
      </div>
    </div>
  );
}

// Aplicar NC Modal - Select FE to apply the NC to
function AplicarNCModal({ nc, onClose, onSuccess }) {
  const [facturas, setFacturas] = useState([]);
  const [loading, setLoading] = useState(true);
  const [applying, setApplying] = useState(false);
  const [selectedFacturaIds, setSelectedFacturaIds] = useState([]);

  useEffect(() => {
    loadFacturasProveedor();
  }, []);

  const loadFacturasProveedor = async () => {
    try {
      setLoading(true);
      const res = await gastosApi.listFacturas({
        proveedorId: nc.proveedorId,
        estado: 'por_pagar',
        limit: 200
      });
      const eligible = (res.data.data || []).filter(f =>
        f.id !== nc.id &&
        !['NC', '03'].includes(f.tipoDocumento) &&
        (parseFloat(f.saldoPendiente) || 0) > 0
      );
      setFacturas(eligible);
    } catch (error) {
      toast.error('Error al cargar facturas del proveedor');
    } finally {
      setLoading(false);
    }
  };

  const toggleFactura = (id) => {
    setSelectedFacturaIds(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleAplicar = async () => {
    if (selectedFacturaIds.length === 0) return;
    try {
      setApplying(true);
      const res = await gastosApi.aplicarNC(nc.id, selectedFacturaIds);
      const d = res.data.data;
      toast.success(`NC aplicada: ${fmt(d.totalAplicado)} a ${d.facturasAplicadas} factura(s)${d.creditoRestanteNC > 0.01 ? ` · sobrante ${fmt(d.creditoRestanteNC)}` : ''}`);
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al aplicar NC');
    } finally {
      setApplying(false);
    }
  };

  const mon = nc.moneda || 'CRC';
  const fmt = (v) => {
    const n = parseFloat(v) || 0;
    return mon === 'USD'
      ? `$${n.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
      : `₡${n.toLocaleString('es-CR', { minimumFractionDigits: 2 })}`;
  };

  // Crédito disponible de la NC (saldo si ya se aplicó parcialmente; si no, el total)
  const creditoNC = (parseFloat(nc.saldoPendiente) > 0) ? parseFloat(nc.saldoPendiente) : (parseFloat(nc.totalComprobante) || 0);
  // Distribución: las FE seleccionadas, ordenadas por fecha (más antigua primero),
  // se saldan en orden hasta agotar el crédito de la NC.
  const seleccionadas = facturas
    .filter(f => selectedFacturaIds.includes(f.id))
    .sort((a, b) => new Date(a.fechaEmision || 0) - new Date(b.fechaEmision || 0));
  let _restante = creditoNC;
  let totalAplicar = 0;
  const distMap = {};
  for (const f of seleccionadas) {
    const saldo = parseFloat(f.saldoPendiente) || 0;
    const aplicar = Math.max(0, Math.round(Math.min(_restante, saldo) * 100) / 100);
    distMap[f.id] = aplicar;
    _restante = Math.round((_restante - aplicar) * 100) / 100;
    totalAplicar = Math.round((totalAplicar + aplicar) * 100) / 100;
  }
  const sobranteNC = Math.max(0, Math.round((creditoNC - totalAplicar) * 100) / 100);

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[80vh] flex flex-col">
        {/* Header con info NC */}
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <div className="flex items-center gap-4">
            <div>
              <h3 className="text-base font-semibold">Aplicar NC</h3>
              <p className="text-xs text-gray-500">{nc.proveedorNombre}</p>
            </div>
            <span className="text-sm font-bold text-indigo-700 bg-indigo-50 px-2 py-1 rounded">{fmt(nc.totalComprobante)}</span>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={18} /></button>
        </div>

        {/* Tabla de facturas */}
        <div className="overflow-y-auto flex-1 px-5 py-3">
          {loading ? (
            <div className="text-center py-6">
              <div className="animate-spin w-6 h-6 border-3 border-blue-500 border-t-transparent rounded-full mx-auto" />
            </div>
          ) : facturas.length === 0 ? (
            <div className="text-center py-6 text-gray-500 text-sm">No hay facturas pendientes de este proveedor</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="px-2 py-1.5 w-8"></th>
                  <th className="px-2 py-1.5 text-left">No. Documento</th>
                  <th className="px-2 py-1.5 text-left">Fecha</th>
                  <th className="px-2 py-1.5 text-right">Total</th>
                  <th className="px-2 py-1.5 text-right">Saldo</th>
                  <th className="px-2 py-1.5 text-right">A aplicar</th>
                  <th className="px-2 py-1.5 text-right">Saldo final</th>
                </tr>
              </thead>
              <tbody>
                {facturas.map(f => {
                  const sel = selectedFacturaIds.includes(f.id);
                  const aplicado = distMap[f.id] || 0;
                  const saldoActual = parseFloat(f.saldoPendiente) || 0;
                  const saldoFinal = Math.max(0, Math.round((saldoActual - aplicado) * 100) / 100);
                  const quedaSaldada = sel && aplicado > 0 && saldoFinal <= 0.01;
                  return (
                  <tr
                    key={f.id}
                    onClick={() => toggleFactura(f.id)}
                    className={`border-t cursor-pointer hover:bg-blue-50 ${sel ? 'bg-blue-100' : ''}`}
                  >
                    <td className="px-2 py-1.5">
                      <input type="checkbox" checked={sel} onChange={() => toggleFactura(f.id)} onClick={(e) => e.stopPropagation()} className="accent-indigo-600 w-3.5 h-3.5" />
                    </td>
                    <td className="px-2 py-1.5 font-mono">{f.fecNumeroFacturaProveedor || f.numeroConsecutivo}</td>
                    <td className="px-2 py-1.5">{dayjs(f.fechaEmision).format('DD/MM/YY')}</td>
                    <td className="px-2 py-1.5 text-right">{fmt(f.totalComprobante)}</td>
                    <td className="px-2 py-1.5 text-right font-bold text-red-600">{fmt(f.saldoPendiente)}</td>
                    <td className="px-2 py-1.5 text-right font-semibold text-indigo-600">{sel && aplicado > 0 ? fmt(aplicado) : '-'}</td>
                    <td className={`px-2 py-1.5 text-right font-semibold ${quedaSaldada ? 'text-emerald-600' : sel && aplicado > 0 ? 'text-orange-600' : 'text-gray-300'}`}>
                      {sel && aplicado > 0 ? (quedaSaldada ? '✓ Saldada' : fmt(saldoFinal)) : '-'}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>

        {/* Footer con resumen + botones */}
        <div className="border-t px-5 py-3 bg-gray-50">
          <div className="flex items-center justify-between gap-4">
            {/* Resumen de la distribución de la NC */}
            <div className="flex items-center gap-3 text-xs flex-wrap">
              <div>
                <span className="text-gray-500">Crédito NC: </span>
                <span className="font-semibold text-indigo-600">{fmt(creditoNC)}</span>
              </div>
              <span className="text-gray-400">→</span>
              <div>
                <span className="text-gray-500">Se aplica: </span>
                <span className="font-semibold text-green-600">{fmt(totalAplicar)}</span>
                <span className="text-gray-400"> a {seleccionadas.length} fact.</span>
              </div>
              <span className="text-gray-400">→</span>
              <div>
                <span className="text-gray-500">Sobrante NC: </span>
                <span className={`font-bold ${sobranteNC > 0.01 ? 'text-orange-600' : 'text-green-600'}`}>{fmt(sobranteNC)}</span>
              </div>
              {sobranteNC > 0.01 && (
                <span className="px-2 py-0.5 bg-amber-100 text-amber-700 text-xs font-medium rounded-full">Queda disponible</span>
              )}
            </div>
            {/* Botones */}
            <div className="flex gap-2">
              <button onClick={onClose} className="px-3 py-1.5 border border-gray-300 text-gray-600 rounded-lg hover:bg-gray-100 text-sm">Cancelar</button>
              <button
                onClick={handleAplicar}
                disabled={applying || selectedFacturaIds.length === 0 || totalAplicar <= 0}
                className="px-3 py-1.5 border-2 border-indigo-500 text-indigo-600 rounded-lg hover:bg-indigo-50 disabled:opacity-50 text-sm font-medium"
              >
                {applying ? 'Aplicando...' : `Aplicar NC${seleccionadas.length > 1 ? ` (${seleccionadas.length})` : ''}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

// Upload PDF Modal Component - AI-powered extraction
function UploadPDFModal({ onClose, onSuccess }) {
  const [file, setFile] = useState(null);
  const [step, setStep] = useState('upload'); // 'upload' | 'extracting' | 'review'
  const [extracting, setExtracting] = useState(false);
  const [saving, setSaving] = useState(false);
  const [proveedores, setProveedores] = useState([]);
  const [formData, setFormData] = useState({
    proveedorId: '',
    proveedorNombre: '',
    proveedorIdentificacion: '',
    numeroConsecutivo: '',
    fechaEmision: dayjs().format('YYYY-MM-DD'),
    fechaVencimiento: '',
    condicionVenta: '01',
    moneda: 'USD',
    tipoCambio: '1',
    subtotal: '',
    totalDescuentos: '0',
    totalImpuesto: '0',
    totalComprobante: '',
    notas: ''
  });
  const [lineas, setLineas] = useState([]);

  useEffect(() => {
    gastosApi.listProveedores({ limit: 200 }).then(res => {
      setProveedores(res.data.data || []);
    });
  }, []);

  // Extract data from PDF using AI
  const handleExtract = async () => {
    if (!file) {
      toast.error('Seleccione un archivo PDF');
      return;
    }
    try {
      setExtracting(true);
      setStep('extracting');
      const data = new FormData();
      data.append('pdf', file);
      const res = await gastosApi.extractPDF(data);
      const extracted = res.data.data;

      // Map extracted data to form
      if (extracted.proveedor) {
        const p = extracted.proveedor;
        // Try to find existing proveedor
        let matchedId = '';
        if (p.identificacion) {
          const match = proveedores.find(prov =>
            prov.identificacion === p.identificacion
          );
          if (match) matchedId = match.id;
        }
        if (!matchedId && p.nombre) {
          const match = proveedores.find(prov =>
            prov.nombre?.toLowerCase().includes(p.nombre.toLowerCase()) ||
            p.nombre.toLowerCase().includes(prov.nombre?.toLowerCase())
          );
          if (match) matchedId = match.id;
        }
        setFormData(prev => ({
          ...prev,
          proveedorId: matchedId || '',
          proveedorNombre: p.nombre || '',
          proveedorIdentificacion: p.identificacion || ''
        }));
      }
      if (extracted.factura) {
        const f = extracted.factura;
        setFormData(prev => ({
          ...prev,
          numeroConsecutivo: f.numero || prev.numeroConsecutivo,
          fechaEmision: f.fechaEmision || prev.fechaEmision,
          fechaVencimiento: f.fechaVencimiento || '',
          condicionVenta: f.condicionVenta === 'credito' ? '02' : '01',
          moneda: f.moneda || 'USD',
          notas: f.notas || ''
        }));
      }
      if (extracted.totales) {
        const t = extracted.totales;
        setFormData(prev => ({
          ...prev,
          subtotal: t.subtotal != null ? String(t.subtotal) : '',
          totalDescuentos: t.descuentos != null ? String(t.descuentos) : '0',
          totalImpuesto: t.impuestos != null ? String(t.impuestos) : '0',
          totalComprobante: t.total != null ? String(t.total) : ''
        }));
      }
      if (extracted.lineas && extracted.lineas.length > 0) {
        setLineas(extracted.lineas.map((l, i) => ({
          numeroLinea: i + 1,
          detalle: l.detalle || '',
          cantidad: l.cantidad || 1,
          precioUnitario: l.precioUnitario || 0,
          descuentoMonto: l.descuento || 0,
          impuestoTarifa: l.impuestoPorcentaje || 0,
          impuestoMonto: l.impuestoMonto || 0,
          montoTotalLinea: l.total || 0
        })));
      }
      setStep('review');
      toast.success('Datos extraídos del PDF');
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al extraer datos del PDF');
      setStep('upload');
    } finally {
      setExtracting(false);
    }
  };

  // Skip AI and go to manual entry
  const handleManual = () => {
    setStep('review');
  };

  // Save the invoice
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.numeroConsecutivo || !formData.totalComprobante) {
      toast.error('No. Factura y Total son requeridos');
      return;
    }
    if (!formData.proveedorId && !formData.proveedorNombre) {
      toast.error('Seleccione o ingrese un proveedor');
      return;
    }
    try {
      setSaving(true);
      const data = new FormData();
      if (file) data.append('pdf', file);
      Object.keys(formData).forEach(key => {
        if (formData[key] !== '' && formData[key] != null) data.append(key, formData[key]);
      });
      if (lineas.length > 0) {
        data.append('lineas', JSON.stringify(lineas));
      }
      await gastosApi.uploadPDF(data);
      toast.success('Factura registrada correctamente');
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || error.message);
    } finally {
      setSaving(false);
    }
  };

  const updateLinea = (idx, field, value) => {
    setLineas(prev => prev.map((l, i) => i === idx ? { ...l, [field]: value } : l));
  };

  const removeLinea = (idx) => {
    setLineas(prev => prev.filter((_, i) => i !== idx));
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-3xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b sticky top-0 bg-white z-10">
          <div>
            <h3 className="text-lg font-semibold">Importar Factura PDF</h3>
            <p className="text-xs text-gray-500">
              {step === 'upload' ? 'Suba el PDF para extraer datos con IA' :
               step === 'extracting' ? 'Analizando documento...' :
               'Revise y corrija los datos antes de guardar'}
            </p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* Step 1: Upload */}
        {step === 'upload' && (
          <div className="p-6 space-y-4">
            <div className="border-2 border-dashed border-gray-300 rounded-xl p-8 text-center">
              <Upload size={40} className="mx-auto text-gray-400 mb-3" />
              <p className="text-sm text-gray-600 mb-2">Seleccione la factura en PDF</p>
              <input
                type="file"
                accept=".pdf"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="mx-auto block text-sm"
              />
              {file && (
                <p className="mt-2 text-sm font-medium text-green-600">{file.name}</p>
              )}
            </div>

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="button"
                onClick={handleManual}
                className="px-4 py-2 border-2 border-gray-400 text-gray-600 rounded-lg hover:bg-gray-50"
              >
                Entrada Manual
              </button>
              <button
                type="button"
                onClick={handleExtract}
                disabled={!file}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 flex items-center gap-2"
              >
                <Sparkles size={16} />
                Extraer con IA
              </button>
            </div>
          </div>
        )}

        {/* Step 2: Extracting */}
        {step === 'extracting' && (
          <div className="p-12 text-center">
            <div className="animate-spin w-10 h-10 border-4 border-blue-500 border-t-transparent rounded-full mx-auto mb-4" />
            <p className="text-lg font-medium text-gray-700">Analizando PDF con IA...</p>
            <p className="text-sm text-gray-500 mt-1">Extrayendo proveedor, líneas y totales</p>
          </div>
        )}

        {/* Step 3: Review & Edit */}
        {step === 'review' && (
          <form onSubmit={handleSubmit} className="p-6 space-y-4">
            {/* Proveedor */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Proveedor</label>
                <select
                  value={formData.proveedorId}
                  onChange={(e) => setFormData({ ...formData, proveedorId: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="">-- Crear nuevo --</option>
                  {proveedores.map(p => (
                    <option key={p.id} value={p.id}>{p.nombre}</option>
                  ))}
                </select>
              </div>
              {!formData.proveedorId && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Nombre Proveedor *</label>
                  <input
                    type="text"
                    value={formData.proveedorNombre}
                    onChange={(e) => setFormData({ ...formData, proveedorNombre: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 text-sm"
                    placeholder="Se creará automáticamente"
                  />
                </div>
              )}
            </div>

            {/* Factura info */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">No. Factura *</label>
                <input
                  type="text"
                  value={formData.numeroConsecutivo}
                  onChange={(e) => setFormData({ ...formData, numeroConsecutivo: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Fecha Emisión *</label>
                <input
                  type="date"
                  value={formData.fechaEmision}
                  onChange={(e) => setFormData({ ...formData, fechaEmision: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Vencimiento</label>
                <input
                  type="date"
                  value={formData.fechaVencimiento}
                  onChange={(e) => setFormData({ ...formData, fechaVencimiento: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Condición</label>
                <select
                  value={formData.condicionVenta}
                  onChange={(e) => setFormData({ ...formData, condicionVenta: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="01">Contado</option>
                  <option value="02">Crédito</option>
                </select>
              </div>
            </div>

            {/* Moneda */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Moneda</label>
                <select
                  value={formData.moneda}
                  onChange={(e) => setFormData({ ...formData, moneda: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                >
                  <option value="CRC">CRC</option>
                  <option value="USD">USD</option>
                  <option value="EUR">EUR</option>
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Tipo Cambio</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.tipoCambio}
                  onChange={(e) => setFormData({ ...formData, tipoCambio: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Subtotal</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.subtotal}
                  onChange={(e) => setFormData({ ...formData, subtotal: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Impuestos</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.totalImpuesto}
                  onChange={(e) => setFormData({ ...formData, totalImpuesto: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            {/* Total */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Total Comprobante *</label>
                <input
                  type="number"
                  step="0.01"
                  value={formData.totalComprobante}
                  onChange={(e) => setFormData({ ...formData, totalComprobante: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm font-semibold"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Notas</label>
                <input
                  type="text"
                  value={formData.notas}
                  onChange={(e) => setFormData({ ...formData, notas: e.target.value })}
                  className="w-full border rounded-lg px-3 py-2 text-sm"
                />
              </div>
            </div>

            {/* Líneas */}
            {lineas.length > 0 && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Líneas ({lineas.length})</label>
                <div className="border rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-2 py-1.5 text-left">Detalle</th>
                        <th className="px-2 py-1.5 text-right w-16">Cant</th>
                        <th className="px-2 py-1.5 text-right w-24">Precio</th>
                        <th className="px-2 py-1.5 text-right w-20">Imp%</th>
                        <th className="px-2 py-1.5 text-right w-24">Total</th>
                        <th className="px-2 py-1.5 w-8"></th>
                      </tr>
                    </thead>
                    <tbody>
                      {lineas.map((l, i) => (
                        <tr key={i} className="border-t">
                          <td className="px-2 py-1">
                            <input
                              type="text"
                              value={l.detalle}
                              onChange={(e) => updateLinea(i, 'detalle', e.target.value)}
                              className="w-full border rounded px-1.5 py-1 text-xs"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              value={l.cantidad}
                              onChange={(e) => updateLinea(i, 'cantidad', parseFloat(e.target.value) || 0)}
                              className="w-full border rounded px-1.5 py-1 text-xs text-right"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              step="0.01"
                              value={l.precioUnitario}
                              onChange={(e) => updateLinea(i, 'precioUnitario', parseFloat(e.target.value) || 0)}
                              className="w-full border rounded px-1.5 py-1 text-xs text-right"
                            />
                          </td>
                          <td className="px-2 py-1">
                            <input
                              type="number"
                              value={l.impuestoTarifa}
                              onChange={(e) => updateLinea(i, 'impuestoTarifa', parseFloat(e.target.value) || 0)}
                              className="w-full border rounded px-1.5 py-1 text-xs text-right"
                            />
                          </td>
                          <td className="px-2 py-1 text-right font-medium">
                            {(l.montoTotalLinea || l.total || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
                          </td>
                          <td className="px-1 py-1">
                            <button type="button" onClick={() => removeLinea(i)} className="text-red-400 hover:text-red-600">
                              <X size={14} />
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Archivo */}
            {!file && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Archivo PDF</label>
                <input
                  type="file"
                  accept=".pdf"
                  onChange={(e) => setFile(e.target.files?.[0] || null)}
                  className="w-full border rounded-lg p-2 text-sm"
                />
              </div>
            )}

            <div className="flex justify-end gap-3 pt-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
              >
                {saving ? 'Guardando...' : 'Guardar Factura'}
              </button>
            </div>
          </form>
        )}
      </div>
    </div>
  );
}

// Accept Invoice Modal Component - Total, Parcial, Contado/Credito - Con polling
function AceptarFacturaModal({ factura, onClose, onSuccess, onActualizar }) {
  const [fase, setFase] = useState('seleccion'); // 'seleccion' | 'procesando' | 'completado'
  // La lista sigue el proceso aunque el modal esté minimizado: mientras corre se recarga cada 5 s
  // (lo enviado pasa a «En Hacienda» de una vez) y al terminar una vez más con la respuesta final.
  useEffect(() => {
    if (fase === 'completado') { onActualizar?.(); return undefined; }
    if (fase !== 'procesando') return undefined;
    const t = setInterval(() => onActualizar?.(), 5000);
    return () => clearInterval(t);
  }, [fase]); // eslint-disable-line react-hooks/exhaustive-deps
  const [min, setMin] = useState(false); // minimizado: el proceso sigue corriendo
  useEffect(() => { if (min) onActualizar?.(); }, [min]); // eslint-disable-line react-hooks/exhaustive-deps -- al minimizar, recarga de una vez
  const [tipoAceptacion, setTipoAceptacion] = useState('total'); // 'total' | 'parcial'
  // La condición inicial sale del XML del proveedor (CondicionVenta 01 = contado), igual que la aceptación masiva
  const [tipoPago, setTipoPago] = useState(() =>
    (factura?.condicionVenta || factura?.condicion_venta) === '01' ? 'contado' : 'credito'); // 'credito' | 'contado'
  const [detalleParcial, setDetalleParcial] = useState('');
  const [pagoData, setPagoData] = useState({
    fechaPago: dayjs().format('YYYY-MM-DD'),
    medioPago: 'transferencia',
    numeroComprobante: '',
    banco: '',
    notas: ''
  });
  const [loading, setLoading] = useState(false);
  const [bancos, setBancos] = useState([]);
  const [estadoActual, setEstadoActual] = useState('enviando');
  const [mensajeActual, setMensajeActual] = useState('');
  const pollingRef = useRef(null);

  const hasElectronicKey = !!factura.clave;
  const MAX_DETALLE_LENGTH = 80;

  // Limpiar polling al desmontar
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Polling cuando estamos en fase procesando
  useEffect(() => {
    if (fase !== 'procesando') return;

    const pollEstado = async () => {
      try {
        const response = await gastosApi.getFactura(factura.id);
        // Usar estadoHacienda que tiene valores: 'aceptado', 'rechazado', 'procesando'
        const estadoHacienda = response.data.data.estadoHacienda || response.data.data.estado_hacienda;
        setEstadoActual(estadoHacienda);

        if (['aceptado', 'rechazado'].includes(estadoHacienda)) {
          clearInterval(pollingRef.current);
          setFase('completado');
          setMensajeActual(estadoHacienda === 'rechazado'
            ? 'La confirmación fue rechazada por Hacienda'
            : '¡Factura confirmada exitosamente!'
          );
        }
      } catch (error) {
        console.error('Error polling estado:', error);
      }
    };

    // Primera consulta después de 2 segundos
    const timeout = setTimeout(() => {
      pollEstado();
      // Luego cada 3 segundos
      pollingRef.current = setInterval(pollEstado, 3000);
    }, 2000);

    return () => {
      clearTimeout(timeout);
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [fase, factura.id]);

  // Cargar bancos
  useEffect(() => {
    const loadBancos = async () => {
      try {
        const response = await parametrosApi.listBancosActivos();
        setBancos(response.data.data || []);
      } catch (error) {
        console.error('Error loading bancos:', error);
      }
    };
    loadBancos();
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Validar detalle para aceptación parcial
    if (tipoAceptacion === 'parcial') {
      if (!detalleParcial.trim()) {
        toast.error('Debe indicar el detalle de la aceptación parcial');
        return;
      }
      if (detalleParcial.length > MAX_DETALLE_LENGTH) {
        toast.error(`El detalle no puede exceder ${MAX_DETALLE_LENGTH} caracteres`);
        return;
      }
    }

    try {
      setLoading(true);
      setEstadoActual('enviando');
      setMensajeActual('Enviando confirmación a Hacienda...');

      // Si tiene clave electrónica, mostrar modal de procesamiento
      if (hasElectronicKey) {
        setFase('procesando');
      }

      const payload = {
        estado: 'aceptada',
        tipoPago: tipoAceptacion === 'parcial' ? 'credito' : tipoPago,
        tipoAceptacion,
        ...(tipoAceptacion === 'parcial' && { detalleMensaje: detalleParcial }),
        ...(tipoAceptacion === 'total' && tipoPago === 'contado' && { pago: pagoData })
      };

      await gastosApi.updateFacturaEstado(factura.id, payload);
      onActualizar?.(); // ya salió: la lista la muestra «En Hacienda» de una vez

      // Si no tiene clave electrónica, cerrar inmediatamente
      if (!hasElectronicKey) {
        toast.success(tipoPago === 'contado' ? 'Factura aceptada y marcada como pagada' : 'Factura aceptada');
        onSuccess();
        return;
      }

      // Si tiene clave, actualizar estado a procesando y esperar polling
      setEstadoActual('procesando_hacienda');
      setMensajeActual('Esperando respuesta de Hacienda...');

    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al aceptar factura');
      setFase('seleccion');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (fase === 'completado') {
      onSuccess();
    } else if (fase === 'seleccion') {
      onClose();
    }
    // No permitir cerrar durante procesamiento
  };

  const formatMoney = (amount, currency = 'CRC') => {
    const num = parseFloat(amount) || 0;
    return currency === 'USD'
      ? `$${num.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
      : `₡${num.toLocaleString('es-CR', { minimumFractionDigits: 2 })}`;
  };

  // Fase de procesamiento o completado
  if (fase === 'procesando' || fase === 'completado') {
    const isSuccess = ['aceptado', 'aceptada', 'por_pagar'].includes(estadoActual);
    const isRejected = ['rechazado', 'rechazada'].includes(estadoActual);
    if (min) return <ProcesoMinimizado texto={`Confirmando ${String(factura.numeroConsecutivo || '').slice(-6)}`} estado={fase === 'completado' ? (isSuccess ? 'listo' : 'error') : 'corriendo'} onAbrir={() => setMin(false)} />;

    return (
      <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50">
        <div className="bg-white rounded-xl w-full max-w-md mx-4 shadow-2xl overflow-hidden">
          {/* Header con color según estado */}
          <div className={`px-6 py-5 ${
            fase === 'completado'
              ? (isSuccess ? 'bg-green-500' : 'bg-red-500')
              : 'bg-blue-500'
          }`}>
            <div className="flex items-center gap-4 text-white">
              {fase === 'completado' ? (
                isSuccess ? <CheckCircle size={32} /> : <XCircle size={32} />
              ) : (
                <div className="relative">
                  <div className="w-8 h-8 border-4 border-white/30 border-t-white rounded-full animate-spin" />
                </div>
              )}
              <div>
                <h3 className="text-lg font-bold">
                  {fase === 'completado'
                    ? (isSuccess ? 'Confirmación Exitosa' : 'Confirmación Rechazada')
                    : 'Procesando Confirmación'
                  }
                </h3>
                <p className="text-sm opacity-90">{mensajeActual}</p>
              </div>
              {fase === 'procesando' && (
                <button onClick={() => setMin(true)} title="Minimizar: el proceso sigue corriendo" className="ml-auto px-3 py-1.5 text-xs font-semibold border border-white/70 text-white rounded-lg hover:bg-white/10">Minimizar</button>
              )}
            </div>
          </div>

          {/* Info de la factura */}
          <div className="px-6 py-4 border-b bg-gray-50">
            <div className="flex items-center justify-between">
              <div>
                <p className="font-medium text-gray-900">{factura.proveedorNombre}</p>
                <p className="text-sm text-gray-500">{factura.numeroConsecutivo}</p>
              </div>
              <div className="text-right">
                <p className="font-bold text-gray-900">{formatMoney(factura.totalComprobante, factura.moneda)}</p>
                <p className="text-xs text-gray-500">
                  {tipoAceptacion === 'parcial' ? 'Aceptación Parcial' : 'Aceptación Total'}
                </p>
              </div>
            </div>
          </div>

          {/* Estado actual */}
          <div className="px-6 py-6">
            <div className="flex items-center justify-center gap-3">
              {fase === 'procesando' ? (
                <>
                  <Loader size={20} className="animate-spin text-blue-500" />
                  <span className="text-gray-600">
                    {estadoActual === 'enviando' ? 'Enviando a Hacienda...' : 'Esperando respuesta...'}
                  </span>
                </>
              ) : (
                <span className={`text-lg font-medium ${isSuccess ? 'text-green-600' : 'text-red-600'}`}>
                  {isSuccess ? '✓ Factura Aceptada' : '✗ Confirmación Rechazada'}
                </span>
              )}
            </div>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t bg-gray-50">
            {fase === 'completado' ? (
              <button
                onClick={handleClose}
                className={`w-full py-3 rounded-lg font-medium transition flex items-center justify-center gap-2 ${
                  isSuccess
                    ? 'bg-green-600 text-white hover:bg-green-700'
                    : 'bg-red-600 text-white hover:bg-red-700'
                }`}
              >
                {isSuccess ? <CheckCircle size={20} /> : <XCircle size={20} />}
                Cerrar
              </button>
            ) : (
              <div className="text-center text-sm text-gray-500">
                <p className="flex items-center justify-center gap-2">
                  <Loader size={14} className="animate-spin" />
                  Por favor espere...
                </p>
                <p className="text-xs mt-1 text-gray-400">No cierre esta ventana</p>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl mx-4 max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">Confirmar Factura</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* Invoice Info */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <p className="font-medium text-gray-900">{factura.proveedorNombre}</p>
          <p className="text-sm text-gray-500">Factura: {factura.numeroConsecutivo}</p>
          <div className="flex justify-between mt-2">
            <span className="text-sm text-gray-500">Total:</span>
            <span className="text-sm font-bold">{formatMoney(factura.totalComprobante, factura.moneda)}</span>
          </div>
          {hasElectronicKey && (
            <div className="mt-2 p-2 bg-blue-50 rounded text-xs text-blue-700">
              Se enviara Mensaje Receptor a Hacienda
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          {/* Tipo de Aceptación (solo si tiene clave electrónica) */}
          {hasElectronicKey && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Confirmación
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setTipoAceptacion('total')}
                  className={`p-3 border-2 rounded-lg text-center transition-all ${
                    tipoAceptacion === 'total'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <CheckCircle size={20} className="mx-auto mb-1" />
                  <div className="font-medium text-sm">Aceptar Total</div>
                  <div className="text-xs text-gray-500">Acepto todo el comprobante</div>
                </button>
                <button
                  type="button"
                  onClick={() => setTipoAceptacion('parcial')}
                  className={`p-3 border-2 rounded-lg text-center transition-all ${
                    tipoAceptacion === 'parcial'
                      ? 'border-amber-500 bg-amber-50 text-amber-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <AlertCircle size={20} className="mx-auto mb-1" />
                  <div className="font-medium text-sm">Aceptar Parcial</div>
                  <div className="text-xs text-gray-500">Acepto solo una parte</div>
                </button>
              </div>
            </div>
          )}

          {/* Detalle para Aceptación Parcial */}
          {tipoAceptacion === 'parcial' && (
            <div className="space-y-3 p-4 bg-amber-50 border border-amber-200 rounded-lg">
              <div>
                <label className="block text-sm font-medium text-amber-800 mb-1">
                  Detalle de la Aceptación Parcial *
                </label>
                <textarea
                  value={detalleParcial}
                  onChange={(e) => setDetalleParcial(e.target.value.slice(0, MAX_DETALLE_LENGTH))}
                  placeholder="Ej: Se aceptan 50 de 100 unidades facturadas"
                  className="w-full border border-amber-300 rounded-lg px-3 py-2 text-sm focus:ring-amber-500 focus:border-amber-500"
                  rows={2}
                  required
                />
                <div className="flex justify-between mt-1">
                  <span className="text-xs text-amber-600">Este mensaje se enviará a Hacienda</span>
                  <span className={`text-xs ${detalleParcial.length >= MAX_DETALLE_LENGTH ? 'text-red-600 font-bold' : 'text-amber-600'}`}>
                    {detalleParcial.length}/{MAX_DETALLE_LENGTH}
                  </span>
                </div>
              </div>

              {/* Alerta importante sobre Nota de Crédito */}
              <div className="flex items-start gap-3 p-3 bg-amber-100 rounded-lg">
                <FileWarning size={20} className="text-amber-600 flex-shrink-0 mt-0.5" />
                <div className="text-sm text-amber-800">
                  <p className="font-semibold">Importante:</p>
                  <p>El proveedor deberá emitir una <strong>Nota de Crédito</strong> por la diferencia no aceptada.</p>
                </div>
              </div>
            </div>
          )}

          {/* Tipo de Pago (solo para aceptación total) */}
          {tipoAceptacion === 'total' && (
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Pago
              </label>
              <div className="grid grid-cols-2 gap-3">
                <button
                  type="button"
                  onClick={() => setTipoPago('credito')}
                  className={`p-3 border-2 rounded-lg text-center transition-all ${
                    tipoPago === 'credito'
                      ? 'border-blue-500 bg-blue-50 text-blue-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <Clock size={20} className="mx-auto mb-1" />
                  <div className="font-medium text-sm">A Crédito</div>
                  <div className="text-xs text-gray-500">Pendiente de pago</div>
                </button>
                <button
                  type="button"
                  onClick={() => setTipoPago('contado')}
                  className={`p-3 border-2 rounded-lg text-center transition-all ${
                    tipoPago === 'contado'
                      ? 'border-green-500 bg-green-50 text-green-700'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <CheckCircle size={20} className="mx-auto mb-1" />
                  <div className="font-medium text-sm">De Contado</div>
                  <div className="text-xs text-gray-500">Ya fue pagada</div>
                </button>
              </div>
            </div>
          )}

          {/* Payment Details (only for Contado on Total acceptance) */}
          {tipoAceptacion === 'total' && tipoPago === 'contado' && (
            <div className="pt-4 border-t">
              <h4 className="font-medium text-gray-700 mb-3">Datos del Pago</h4>

              <div className="grid grid-cols-4 gap-3">
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Fecha</label>
                  <input
                    type="date"
                    value={pagoData.fechaPago}
                    onChange={(e) => setPagoData({ ...pagoData, fechaPago: e.target.value })}
                    className="w-full border rounded-lg px-2 py-1.5 text-sm"
                    required
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Medio</label>
                  <select
                    value={pagoData.medioPago}
                    onChange={(e) => setPagoData({ ...pagoData, medioPago: e.target.value })}
                    className="w-full border rounded-lg px-2 py-1.5 text-sm"
                  >
                    <option value="transferencia">Transferencia</option>
                    <option value="efectivo">Efectivo</option>
                    <option value="cheque">Cheque</option>
                    <option value="tarjeta">Tarjeta</option>
                    <option value="sinpe">SINPE</option>
                    <option value="anticipado">Anticipado</option>
                    <option value="otro">Otro</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Comprobante</label>
                  <input
                    type="text"
                    value={pagoData.numeroComprobante}
                    onChange={(e) => setPagoData({ ...pagoData, numeroComprobante: e.target.value })}
                    placeholder="Referencia"
                    className="w-full border rounded-lg px-2 py-1.5 text-sm"
                  />
                </div>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">Banco</label>
                  <select
                    value={pagoData.banco}
                    onChange={(e) => setPagoData({ ...pagoData, banco: e.target.value })}
                    className="w-full border rounded-lg px-2 py-1.5 text-sm"
                  >
                    <option value="">Seleccionar</option>
                    {bancos.map(banco => (
                      <option key={banco.id} value={banco.nombre}>{banco.nombre}</option>
                    ))}
                  </select>
                </div>
              </div>

              <div className="mt-3">
                <label className="block text-xs font-medium text-gray-700 mb-1">Notas</label>
                <input
                  type="text"
                  value={pagoData.notas}
                  onChange={(e) => setPagoData({ ...pagoData, notas: e.target.value })}
                  placeholder="Ej: Retiro de mercaderia, prepago, etc."
                  className="w-full border rounded-lg px-2 py-1.5 text-sm"
                />
              </div>
            </div>
          )}

          {/* Summary */}
          <div className="pt-4 border-t">
            <div className="bg-gray-50 rounded-lg p-4">
              {tipoAceptacion === 'parcial' ? (
                <div className="flex items-center gap-3 text-gray-700">
                  <AlertCircle size={20} className="text-amber-500" />
                  <div>
                    <p className="font-medium">Aceptación Parcial - Crédito</p>
                    <p className="text-sm text-gray-500">Esperando Nota de Crédito del proveedor</p>
                  </div>
                </div>
              ) : tipoPago === 'credito' ? (
                <div className="flex items-center gap-3 text-gray-700">
                  <Clock size={20} className="text-blue-500" />
                  <div>
                    <p className="font-medium">Factura irá a Cuentas por Pagar</p>
                    <p className="text-sm text-gray-500">Quedará pendiente de pago posterior</p>
                  </div>
                </div>
              ) : (
                <div className="flex items-center gap-3 text-gray-700">
                  <CheckCircle size={20} className="text-green-500" />
                  <div>
                    <p className="font-medium">Factura se marcará como Pagada</p>
                    <p className="text-sm text-gray-500">Se registrará el pago automáticamente</p>
                  </div>
                </div>
              )}
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || (tipoAceptacion === 'parcial' && !detalleParcial.trim())}
              className={`flex items-center gap-2 px-4 py-2 border-2 rounded-lg disabled:opacity-50 ${
                tipoAceptacion === 'parcial'
                  ? 'border-amber-600 text-amber-600 hover:bg-amber-50'
                  : 'border-green-600 text-green-600 hover:bg-green-50'
              }`}
            >
              {loading ? (
                <Loader className="animate-spin" size={18} />
              ) : (
                <Check size={18} />
              )}
              {loading ? 'Procesando...' : tipoAceptacion === 'parcial' ? 'Aceptar Parcialmente' : 'Aceptar Factura'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Rechazar Factura Modal - Requiere motivo
function RechazarFacturaModal({ factura, onClose, onSuccess }) {
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);

  const hasElectronicKey = !!factura.clave;
  const MAX_MOTIVO_LENGTH = 80;

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!motivo.trim()) {
      toast.error('Debe indicar el motivo del rechazo');
      return;
    }

    if (motivo.length > MAX_MOTIVO_LENGTH) {
      toast.error(`El motivo no puede exceder ${MAX_MOTIVO_LENGTH} caracteres`);
      return;
    }

    try {
      setLoading(true);
      const response = await gastosApi.updateFacturaEstado(factura.id, {
        estado: 'rechazada',
        motivo
      });

      if (response.data.mensajeReceptor) {
        toast.success(`Mensaje de Rechazo enviado a Hacienda. Estado: ${response.data.mensajeReceptor.status}`);
      } else {
        toast.success('Factura rechazada');
      }
      onSuccess();
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al rechazar factura');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount, currency = 'CRC') => {
    const num = parseFloat(amount) || 0;
    return currency === 'USD'
      ? `$${num.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
      : `₡${num.toLocaleString('es-CR', { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-lg mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-red-600">Rechazar Factura</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        {/* Invoice Info */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <p className="font-medium text-gray-900">{factura.proveedorNombre}</p>
          <p className="text-sm text-gray-500">Factura: {factura.numeroConsecutivo}</p>
          <div className="flex justify-between mt-2">
            <span className="text-sm text-gray-500">Total:</span>
            <span className="text-sm font-bold">{formatMoney(factura.totalComprobante, factura.moneda)}</span>
          </div>
          {hasElectronicKey && (
            <div className="mt-2 p-2 bg-red-50 rounded text-xs text-red-700">
              Se enviará Mensaje de RECHAZO a Hacienda
            </div>
          )}
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo del Rechazo *
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value.slice(0, MAX_MOTIVO_LENGTH))}
              placeholder="Ej: Datos incorrectos, producto no recibido, etc."
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-red-500 focus:border-red-500"
              rows={3}
              required
              autoFocus
            />
            <div className="flex justify-between mt-1">
              <span className="text-xs text-gray-500">
                {hasElectronicKey ? 'Este mensaje se enviará a Hacienda' : 'Motivo interno'}
              </span>
              <span className={`text-xs ${motivo.length >= MAX_MOTIVO_LENGTH ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                {motivo.length}/{MAX_MOTIVO_LENGTH}
              </span>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !motivo.trim()}
              className="flex items-center gap-2 px-4 py-2 border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              {loading ? (
                <Loader className="animate-spin" size={18} />
              ) : (
                <XCircle size={18} />
              )}
              {loading ? 'Procesando...' : 'Rechazar Factura'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Batch Rechazar Modal - Con procesamiento y polling hasta respuesta de Hacienda
function BatchRechazarModal({ count, facturaIds, facturas, onClose, onSuccess, onActualizar }) {
  const [fase, setFase] = useState('seleccion'); // 'seleccion' | 'procesando' | 'completado'
  // La lista sigue el proceso aunque el modal esté minimizado: mientras corre se recarga cada 5 s
  // (lo enviado pasa a «En Hacienda» de una vez) y al terminar una vez más con la respuesta final.
  useEffect(() => {
    if (fase === 'completado') { onActualizar?.(); return undefined; }
    if (fase !== 'procesando') return undefined;
    const t = setInterval(() => onActualizar?.(), 5000);
    return () => clearInterval(t);
  }, [fase]); // eslint-disable-line react-hooks/exhaustive-deps
  const [min, setMin] = useState(false); // minimizado: el proceso sigue corriendo
  useEffect(() => { if (min) onActualizar?.(); }, [min]); // eslint-disable-line react-hooks/exhaustive-deps -- al minimizar, recarga de una vez
  const [motivo, setMotivo] = useState('');
  const [loading, setLoading] = useState(false);
  const [estadoFacturas, setEstadoFacturas] = useState({});
  const pollingRef = useRef(null);
  const [mensajeActual, setMensajeActual] = useState('Preparando envío...');

  const MAX_MOTIVO_LENGTH = 80;

  // Limpiar polling al desmontar
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // Polling cuando estamos en fase procesando
  useEffect(() => {
    if (fase !== 'procesando') return;

    const pollEstados = async () => {
      try {
        const newResults = [];
        let rechazadas = 0;

        for (const id of facturaIds) {
          try {
            const response = await gastosApi.getFactura(id);
            const factura = response.data.data;
            const estadoHacienda = factura.estadoHacienda || factura.estado_hacienda;

            newResults.push({
              id,
              estado: estadoHacienda,
              proveedor: factura.proveedorNombre,
              numero: factura.numeroConsecutivo
            });

            if (['rechazado', 'aceptado'].includes(estadoHacienda)) rechazadas++;
          } catch (err) {
            console.error('Error polling factura', id, err);
          }
        }

        // Actualizar UNA POR UNA con delay
        let completedCount = 0;
        const facturasNuevasCompletadas = newResults.filter(r =>
          ['rechazado', 'aceptado'].includes(r.estado)
        );

        const facturasEnProceso = newResults.filter(r =>
          !['rechazado', 'aceptado'].includes(r.estado)
        );

        if (facturasEnProceso.length > 0) {
          setEstadoFacturas(prev => {
            const updated = { ...prev };
            facturasEnProceso.forEach(f => {
              updated[f.id] = { estado: f.estado, proveedor: f.proveedor, numero: f.numero };
            });
            return updated;
          });
        }

        facturasNuevasCompletadas.forEach((f, index) => {
          setTimeout(() => {
            setEstadoFacturas(prev => {
              if (prev[f.id]?.estado !== f.estado) {
                const updated = { ...prev };
                updated[f.id] = { estado: f.estado, proveedor: f.proveedor, numero: f.numero };
                return updated;
              }
              return prev;
            });

            completedCount++;
            setMensajeActual(`Procesando... ${completedCount}/${facturaIds.length} completadas`);

            if (index === facturasNuevasCompletadas.length - 1 &&
                facturasNuevasCompletadas.length === facturaIds.length) {
              setTimeout(() => {
                clearInterval(pollingRef.current);
                setFase('completado');
                setMensajeActual(`¡Proceso completado! ${rechazadas} facturas procesadas`);
              }, 300);
            }
          }, index * 400);
        });

        if (facturasNuevasCompletadas.length === 0) {
          setMensajeActual(`Esperando respuesta de Hacienda...`);
        }
      } catch (error) {
        console.error('Error en polling:', error);
      }
    };

    // Primera consulta inmediata
    pollEstados();

    // Polling cada 2 segundos para feedback más rápido
    pollingRef.current = setInterval(pollEstados, 2000);

    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, [fase, facturaIds]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!motivo.trim()) {
      toast.error('Debe indicar el motivo del rechazo');
      return;
    }

    if (motivo.length > MAX_MOTIVO_LENGTH) {
      toast.error(`El motivo no puede exceder ${MAX_MOTIVO_LENGTH} caracteres`);
      return;
    }

    try {
      setLoading(true);
      setMensajeActual('Enviando rechazos a Hacienda...');

      // Inicializar estados como "enviando"
      const initialStates = {};
      facturaIds.forEach(id => {
        const fac = facturas?.find(f => f.id === id);
        initialStates[id] = {
          estado: 'enviando',
          proveedor: fac?.proveedorNombre || '',
          numero: fac?.numeroConsecutivo || ''
        };
      });
      setEstadoFacturas(initialStates);

      // Cambiar a fase de procesamiento
      setFase('procesando');

      // Enviar rechazos
      await gastosApi.batchRechazar(facturaIds, motivo);
      onActualizar?.(); // ya salieron: la lista las muestra en proceso de una vez

      // Actualizar estados a "procesando_rechazo"
      setEstadoFacturas(prev => {
        const updated = { ...prev };
        facturaIds.forEach(id => {
          updated[id] = { ...updated[id], estado: 'procesando_rechazo' };
        });
        return updated;
      });

      setMensajeActual('Esperando respuesta de Hacienda...');

    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al enviar rechazos');
      setFase('seleccion');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (fase === 'completado') {
      onSuccess();
    } else if (fase === 'seleccion') {
      onClose();
    }
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'enviando':
      case 'procesando_rechazo':
      case 'procesando':
        return <Loader size={16} className="animate-spin text-red-500" />;
      case 'rechazado':
      case 'rechazada':
      case 'aceptado':
        return <XCircle size={16} className="text-red-500" />;
      default:
        return <Clock size={16} className="text-gray-400" />;
    }
  };

  const getEstadoLabel = (estado) => {
    switch (estado) {
      case 'enviando': return 'Enviando...';
      case 'procesando_rechazo':
      case 'procesando': return 'Esperando Hacienda...';
      case 'rechazado':
      case 'rechazada':
      case 'aceptado': return 'Procesado ✓';
      default: return 'Pendiente';
    }
  };

  // Fase de procesamiento o completado
  if (fase === 'procesando' || fase === 'completado') {
    const completadas = Object.values(estadoFacturas).filter(f =>
      ['rechazado', 'rechazada', 'aceptado'].includes(f.estado)
    ).length;
    const porcentaje = Math.round((completadas / facturaIds.length) * 100);

    // Determinar etapa actual
    const enviando = Object.values(estadoFacturas).some(f => f.estado === 'enviando');
    const esperando = Object.values(estadoFacturas).some(f => f.estado === 'procesando_rechazo' || f.estado === 'procesando');
    const etapaActual = enviando ? 'enviando' : esperando ? 'esperando' : 'completado';
    if (min) return <ProcesoMinimizado texto={`Rechazando ${facturaIds.length} factura${facturaIds.length === 1 ? '' : 's'}`} avance={`${completadas}/${facturaIds.length}`} estado={fase === 'completado' ? 'listo' : 'corriendo'} onAbrir={() => setMin(false)} />;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 py-8">
          <div className="fixed inset-0 bg-gray-900/60" />
          <div className="relative w-full max-w-5xl bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden">
            {/* Header - outline style */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg border ${fase === 'completado' ? 'border-red-300 bg-red-50' : 'border-orange-300 bg-orange-50'}`}>
                  {fase === 'completado' ? (
                    <XCircle className="w-6 h-6 text-red-600" />
                  ) : (
                    <Loader className="w-6 h-6 text-orange-600 animate-spin" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {fase === 'completado' ? 'Rechazos Completados' : 'Rechazando Facturas'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {fase === 'completado'
                      ? `${completadas} facturas procesadas`
                      : mensajeActual
                    }
                  </p>
                </div>
                {fase === 'procesando' && <BotonMinimizar onClick={() => setMin(true)} />}
                {fase === 'completado' && (
                  <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                )}
              </div>
            </div>

            <div className="p-6">
              {/* Barra de progreso */}
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-medium text-gray-700">Progreso</span>
                  <span className="font-semibold text-gray-900">{porcentaje}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                  <div
                    className={`h-full transition-all duration-500 ease-out rounded-full ${
                      fase === 'completado' ? 'bg-red-500' : 'bg-orange-500'
                    }`}
                    style={{ width: `${porcentaje}%` }}
                  />
                </div>
              </div>

              {/* Estado actual con etapas */}
              {fase !== 'completado' && (
                <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg border border-gray-200 ${etapaActual === 'enviando' ? 'text-orange-600' : 'text-red-600'}`}>
                      {etapaActual === 'enviando' ? <Send className="w-5 h-5" /> : <RefreshCw className="w-5 h-5 animate-spin" />}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {etapaActual === 'enviando' ? 'Enviando rechazo a Hacienda' : 'Esperando respuesta'}
                      </p>
                      <p className="text-sm text-gray-500">{mensajeActual}</p>
                    </div>
                  </div>

                  {/* Mini etapas */}
                  <div className="flex items-center gap-1.5 mt-3">
                    <div className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${etapaActual === 'enviando' || etapaActual === 'esperando' ? 'bg-orange-500' : 'bg-gray-200'}`} />
                    <div className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${etapaActual === 'esperando' ? 'bg-red-500' : 'bg-gray-200'}`} />
                  </div>
                  <div className="flex justify-between mt-1.5 text-[10px] text-gray-400">
                    <span>Enviando</span>
                    <span>Esperando Hacienda</span>
                  </div>
                </div>
              )}

              {/* Tabla de resultados */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-xs table-fixed">
                    <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                      <tr className="text-left text-[10px] font-semibold text-gray-600 tracking-wider">
                        <th className="px-3 py-2.5 w-44">Consecutivo</th>
                        <th className="px-3 py-2.5">Proveedor</th>
                        <th className="px-3 py-2.5 text-center w-40">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {Object.entries(estadoFacturas).map(([id, data], idx) => {
                        const isComplete = ['rechazado', 'rechazada', 'aceptado'].includes(data.estado);

                        return (
                          <tr
                            key={id}
                            className={`transition-colors ${isComplete ? 'hover:bg-red-50/50' : 'hover:bg-gray-50'}`}
                          >
                            <td className="px-3 py-2">
                              <code className="px-1.5 py-0.5 border border-gray-200 rounded text-[10px] font-mono text-gray-700">
                                {data.numero || '—'}
                              </code>
                            </td>
                            <td className="px-3 py-2 text-gray-900 font-medium">
                              {data.proveedor}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {isComplete ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border border-red-300 text-red-700">
                                  <XCircle className="w-3 h-3" />
                                  Rechazada
                                </span>
                              ) : data.estado === 'enviando' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border border-orange-300 text-orange-700">
                                  <Send className="w-3 h-3" />
                                  Enviando...
                                </span>
                              ) : data.estado === 'procesando_rechazo' || data.estado === 'procesando' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border border-red-300 text-red-700">
                                  <Loader className="w-3 h-3 animate-spin" />
                                  Esperando...
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border border-gray-300 text-gray-500">
                                  <Clock className="w-3 h-3" />
                                  En cola
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Botón cerrar */}
              {fase === 'completado' && (
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleClose}
                    className="px-6 py-2.5 text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fase de selección inicial
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-2xl mx-4 border-2 border-gray-200">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold text-red-600">Rechazar {count} Facturas</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="px-6 py-4 bg-red-50 border-b">
          <div className="flex items-center gap-3 text-red-700">
            <AlertTriangle size={20} />
            <p className="text-sm">
              Se enviará mensaje de RECHAZO a Hacienda para las {count} facturas seleccionadas
            </p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="p-6 space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Motivo del Rechazo (aplica a todas) *
            </label>
            <textarea
              value={motivo}
              onChange={(e) => setMotivo(e.target.value.slice(0, MAX_MOTIVO_LENGTH))}
              placeholder="Ej: Rechazado por lote - verificar detalles"
              className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:ring-red-500 focus:border-red-500"
              rows={3}
              required
              autoFocus
            />
            <div className="flex justify-between mt-1">
              <span className="text-xs text-gray-500">Este mensaje se enviará a Hacienda</span>
              <span className={`text-xs ${motivo.length >= MAX_MOTIVO_LENGTH ? 'text-red-600 font-bold' : 'text-gray-500'}`}>
                {motivo.length}/{MAX_MOTIVO_LENGTH}
              </span>
            </div>
          </div>

          <div className="flex justify-end gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !motivo.trim()}
              className="flex items-center gap-2 px-4 py-2 border-2 border-red-600 text-red-600 rounded-lg hover:bg-red-50 disabled:opacity-50"
            >
              {loading ? (
                <Loader className="animate-spin" size={18} />
              ) : (
                <XCircle size={18} />
              )}
              {loading ? 'Procesando...' : `Rechazar ${count} Facturas`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Batch Aceptar Modal - Con procesamiento y polling hasta respuesta de Hacienda
// Condición de venta del XML de una factura de proveedor: 01 contado; el resto se acepta a crédito
const condicionXml = (f) => String(f?.condicionVenta || f?.condicion_venta || '');
const tipoPagoSegunXml = (f) => (condicionXml(f) === '01' ? 'contado' : 'credito');
const textoCondicionXml = (f) => {
  const cv = condicionXml(f);
  if (!cv) return '—';
  if (cv === '01') return 'Contado';
  const plazo = Number(f?.plazoCredito || f?.plazo_credito);
  if (cv === '02') return plazo > 0 ? `Crédito ${plazo} días` : 'Crédito';
  return `Crédito (cond. ${cv})`;
};

function BatchAceptarModal({ count, facturaIds, facturas, onClose, onSuccess, onActualizar }) {
  const [fase, setFase] = useState('seleccion'); // 'seleccion' | 'procesando' | 'completado'
  // La lista sigue el proceso aunque el modal esté minimizado: mientras corre se recarga cada 5 s
  // (lo enviado pasa a «En Hacienda» de una vez) y al terminar una vez más con la respuesta final.
  useEffect(() => {
    if (fase === 'completado') { onActualizar?.(); return undefined; }
    if (fase !== 'procesando') return undefined;
    const t = setInterval(() => onActualizar?.(), 5000);
    return () => clearInterval(t);
  }, [fase]); // eslint-disable-line react-hooks/exhaustive-deps
  const [min, setMin] = useState(false); // minimizado: el proceso sigue corriendo
  useEffect(() => { if (min) onActualizar?.(); }, [min]); // eslint-disable-line react-hooks/exhaustive-deps -- al minimizar, recarga de una vez
  // Por factura: el término viene de su XML (se cambia solo si no es correcto) y, si es contado,
  // quién la pagó. Las líneas se categorizan al preparar; lo que falte se categoriza aquí mismo.
  const [prep, setPrep] = useState(null); // { prorrata, facturas: [...] } del backend
  const [items, setItems] = useState({}); // id → { tipoPago, pagadoPor, empleadoNombre, pct }
  const [abierta, setAbierta] = useState(null); // factura con las líneas desplegadas
  const [pagadorAbierto, setPagadorAbierto] = useState(null); // factura con el recuadro «quién la pagó» abierto
  const [pagadorPos, setPagadorPos] = useState(null); // dónde se dibuja ese recuadro (fuera del scroll de la tabla)
  const [guardandoLinea, setGuardandoLinea] = useState(null);
  const [loading, setLoading] = useState(false);

  const [errorPrep, setErrorPrep] = useState(null);
  const idsKey = (facturaIds || []).join(',');
  const cargarPreparacion = useCallback(async () => {
    const res = await gastosApi.prepararConfirmacion(idsKey.split(',').filter(Boolean));
    const data = res.data?.data;
    setPrep(data);
    setItems(prev => {
      const next = { ...prev };
      for (const f of data?.facturas || []) {
        if (!next[f.id]) next[f.id] = {
          tipoPago: f.condicionVentaXml === '01' ? 'contado' : 'credito',
          pagadoPor: f.pagadaPorEmpleado ? 'empleado' : 'empresa',
          empleadoNombre: f.empleadoNombre || '',
          pct: f.porcentajeEmpresa ?? 100
        };
      }
      return next;
    });
  }, [idsKey]);

  useEffect(() => {
    setErrorPrep(null);
    cargarPreparacion().catch(err => {
      setErrorPrep(err.response?.data?.error || 'No se pudieron preparar las facturas');
      if (!err.response) toast.error('No se pudieron preparar las facturas');
    });
  }, [cargarPreparacion]);
  const [estadoFacturas, setEstadoFacturas] = useState({});
  const pollingRef = useRef(null);
  const [mensajeActual, setMensajeActual] = useState('Preparando envío...');

  // Limpiar polling al desmontar
  useEffect(() => {
    return () => {
      if (pollingRef.current) {
        clearInterval(pollingRef.current);
      }
    };
  }, []);

  // El seguimiento de Hacienda lo hace UN solo ciclo, el de handleSubmit. Había
  // otro aquí que cada 2 s reescribía cada fila con el estado del servidor
  // ('pendiente' = «En cola»): borraba el «Error» de un envío fallido y el modal
  // terminaba en «Confirmación Completada · 0 aceptadas, 0 con error».

  const handleSubmit = async (e) => {
    e.preventDefault();
    const sinEmpleado = facturaIds.filter(id => items[id]?.tipoPago === 'contado' && items[id]?.pagadoPor === 'empleado' && !String(items[id]?.empleadoNombre || '').trim());
    if (sinEmpleado.length) { toast.error('Indique el empleado que pagó cada factura marcada «Empleado»'); return; }

    try {
      setLoading(true);

      // Inicializar estados como "pendiente"
      const initialStates = {};
      facturaIds.forEach(id => {
        const fac = facturas?.find(f => f.id === id);
        initialStates[id] = {
          estado: 'pendiente',
          proveedor: fac?.proveedorNombre || '',
          numero: fac?.numeroConsecutivo || '',
          pago: items[id]?.tipoPago === 'contado' ? 'Contado' : 'Crédito'
        };
      });
      setEstadoFacturas(initialStates);

      // Cambiar a fase de procesamiento
      setFase('procesando');

      // Enviar confirmaciones UNA POR UNA con progreso visual
      let enviadas = 0;
      for (const id of facturaIds) {
        enviadas++;
        // Las que incumplen una regla que bloquea no se envían (el backend tampoco las acepta)
        if (prep?.facturas?.find(f => f.id === id)?.cumplimiento === 'incumple') {
          setEstadoFacturas(prev => ({ ...prev, [id]: { ...prev[id], estado: 'error', errorMsg: 'Incumple una regla que bloquea: no se confirmó' } }));
          continue;
        }
        setMensajeActual(`Enviando ${enviadas} de ${facturaIds.length} a Hacienda...`);

        // Marcar como "enviando"
        setEstadoFacturas(prev => ({
          ...prev,
          [id]: { ...prev[id], estado: 'enviando' }
        }));

        try {
          const it = items[id] || {};
          await gastosApi.updateFacturaEstado(id, {
            estado: 'aceptada',
            tipoPago: it.tipoPago || tipoPagoSegunXml(facturas?.find(f => f.id === id)),
            tipoAceptacion: 'total',
            ...(it.tipoPago === 'contado' && {
              pagadoPor: it.pagadoPor || 'empresa',
              ...(it.pagadoPor === 'empleado' && { empleadoNombre: it.empleadoNombre, porcentajeEmpresa: it.pct })
            })
          });

          // Marcar como "procesando_hacienda" (esperando respuesta)
          setEstadoFacturas(prev => ({
            ...prev,
            [id]: { ...prev[id], estado: 'procesando' }
          }));
          onActualizar?.(); // ya salió: la lista la muestra «En Hacienda» de una vez
        } catch (err) {
          console.error(`Error enviando factura ${id}:`, err);
          const errorMsg = err.response?.data?.error || err.message || 'Error desconocido';
          setEstadoFacturas(prev => ({
            ...prev,
            [id]: { ...prev[id], estado: 'error', errorMsg }
          }));
        }
      }

      // Todas enviadas, ahora polling para las que quedan en "procesando"
      setMensajeActual('Esperando respuesta de Hacienda...');

      // Poll every 5 seconds until all are resolved
      // En pollingRef para que el efecto de desmontaje lo detenga si se cierra el modal.
      const pollInterval = pollingRef.current = setInterval(async () => {
        let allResolved = true;
        setEstadoFacturas(prev => {
          const pendingIds = Object.entries(prev).filter(([, d]) => d.estado === 'procesando').map(([id]) => id);
          if (pendingIds.length === 0) {
            clearInterval(pollInterval);
            setFase('completado');
            return prev;
          }
          allResolved = false;
          return prev;
        });
        if (allResolved) return;

        // Check status for pending ones
        for (const id of facturaIds) {
          try {
            const currentState = await new Promise(resolve => {
              setEstadoFacturas(prev => { resolve(prev[id]?.estado); return prev; });
            });
            if (currentState !== 'procesando') continue;

            const res = await gastosApi.getFactura(id);
            const fac = res.data?.data;
            if (fac && fac.estadoHacienda && fac.estadoHacienda !== 'pendiente') {
              const isAccepted = ['aceptado'].includes(fac.estadoHacienda);
              const isRejected = ['rechazado'].includes(fac.estadoHacienda);
              if (isAccepted || isRejected) {
                setEstadoFacturas(prev => ({
                  ...prev,
                  [id]: {
                    ...prev[id],
                    estado: isAccepted ? 'aceptada' : 'rechazado',
                    errorMsg: isRejected ? (fac.haciendaMensaje || 'Rechazado por Hacienda') : null
                  }
                }));
              }
            }
          } catch {}
        }

        // Check if all resolved now
        setEstadoFacturas(prev => {
          const stillPending = Object.values(prev).some(d => d.estado === 'procesando' || d.estado === 'enviando');
          if (!stillPending) {
            clearInterval(pollInterval);
            setFase('completado');
          }
          return prev;
        });
      }, 5000);

      // Seguro: a los 2 minutos se deja de consultar. Lo que siga esperando se
      // queda «Esperando»: Hacienda no respondió todavía, y darlo por aceptado
      // sería afirmar algo que no pasó. El estado real se ve después en la lista.
      setTimeout(() => {
        clearInterval(pollInterval);
        setMensajeActual('Hacienda no respondió todavía; el estado se actualizará en la lista.');
        setFase('completado');
      }, 120000);

    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al enviar confirmaciones');
      setFase('seleccion');
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (fase === 'completado') {
      onSuccess();
    } else if (fase === 'seleccion') {
      onClose();
    }
    // No permitir cerrar durante procesamiento
  };

  const getEstadoIcon = (estado) => {
    switch (estado) {
      case 'enviando':
        return <Loader size={16} className="animate-spin text-amber-500" />;
      case 'procesando_hacienda':
      case 'procesando':
        return <Loader size={16} className="animate-spin text-blue-500" />;
      case 'aceptado':
      case 'aceptada':
      case 'por_pagar':
        return <CheckCircle size={16} className="text-green-500" />;
      case 'rechazado':
      case 'rechazada':
      case 'error':
        return <XCircle size={16} className="text-red-500" />;
      case 'pendiente':
        return <Clock size={16} className="text-gray-300" />;
      default:
        return <Clock size={16} className="text-gray-400" />;
    }
  };

  const getEstadoLabel = (estado) => {
    switch (estado) {
      case 'pendiente': return 'En cola...';
      case 'enviando': return 'Enviando...';
      case 'procesando_hacienda':
      case 'procesando': return 'Esperando Hacienda...';
      case 'aceptado':
      case 'aceptada':
      case 'por_pagar': return 'Aceptada ✓';
      case 'rechazado':
      case 'rechazada': return 'Rechazada ✗';
      case 'error': return 'Error ✗';
      default: return 'Pendiente';
    }
  };

  // Fase de procesamiento o completado
  if (fase === 'procesando' || fase === 'completado') {
    const completadas = Object.values(estadoFacturas).filter(f =>
      ['aceptado', 'aceptada', 'por_pagar', 'rechazado', 'rechazada'].includes(f.estado)
    ).length;
    const exitosos = Object.values(estadoFacturas).filter(f =>
      ['aceptado', 'aceptada', 'por_pagar'].includes(f.estado)
    ).length;
    const fallidos = Object.values(estadoFacturas).filter(f =>
      ['rechazado', 'rechazada', 'error'].includes(f.estado)
    ).length;
    const porcentaje = Math.round((completadas / facturaIds.length) * 100);

    // Determinar etapa actual basado en estados
    const enviando = Object.values(estadoFacturas).some(f => f.estado === 'enviando');
    const esperando = Object.values(estadoFacturas).some(f => f.estado === 'procesando');
    const etapaActual = enviando ? 'enviando' : esperando ? 'esperando' : 'completado';
    if (min) return <ProcesoMinimizado texto={`Confirmando ${facturaIds.length} factura${facturaIds.length === 1 ? '' : 's'}`} avance={`${completadas}/${facturaIds.length}`} estado={fase === 'completado' ? (fallidos ? 'error' : 'listo') : 'corriendo'} onAbrir={() => setMin(false)} />;

    return (
      <div className="fixed inset-0 z-50 overflow-y-auto">
        <div className="flex items-center justify-center min-h-screen px-4 py-8">
          <div className="fixed inset-0 bg-gray-900/60" />
          <div className="relative w-full max-w-5xl bg-white rounded-xl border border-gray-200 shadow-xl overflow-hidden">
            {/* Header - outline style */}
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center gap-3">
                <div className={`p-2 rounded-lg border ${fase === 'completado' ? 'border-green-300 bg-green-50' : 'border-blue-300 bg-blue-50'}`}>
                  {fase === 'completado' ? (
                    <CheckCircle className="w-6 h-6 text-green-600" />
                  ) : (
                    <Loader className="w-6 h-6 text-blue-600 animate-spin" />
                  )}
                </div>
                <div className="flex-1">
                  <h3 className="text-lg font-semibold text-gray-900">
                    {fase === 'completado' ? 'Confirmación Completada' : 'Confirmando Facturas'}
                  </h3>
                  <p className="text-sm text-gray-500">
                    {fase === 'completado'
                      ? `${exitosos} aceptadas, ${fallidos} con error`
                      : mensajeActual
                    }
                  </p>
                </div>
                {fase === 'procesando' && <BotonMinimizar onClick={() => setMin(true)} />}
                {fase === 'completado' && (
                  <button onClick={handleClose} className="p-2 hover:bg-gray-100 rounded-lg transition-colors border border-gray-200">
                    <X className="w-5 h-5 text-gray-500" />
                  </button>
                )}
              </div>
            </div>

            <div className="p-6">
              {/* Barra de progreso */}
              <div className="mb-6">
                <div className="flex justify-between text-sm mb-2">
                  <span className="font-medium text-gray-700">Progreso</span>
                  <span className="font-semibold text-gray-900">{porcentaje}%</span>
                </div>
                <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden border border-gray-200">
                  <div
                    className={`h-full transition-all duration-500 ease-out rounded-full ${
                      fase === 'completado' ? 'bg-green-500' : 'bg-blue-500'
                    }`}
                    style={{ width: `${porcentaje}%` }}
                  />
                </div>
              </div>

              {/* Estado actual con etapas */}
              {fase !== 'completado' && (
                <div className="mb-6 p-4 bg-white rounded-lg border border-gray-200">
                  <div className="flex items-center gap-3 mb-3">
                    <div className={`p-2 rounded-lg border border-gray-200 ${etapaActual === 'enviando' ? 'text-orange-600' : 'text-blue-600'}`}>
                      {etapaActual === 'enviando' ? <Send className="w-5 h-5" /> : <RefreshCw className="w-5 h-5 animate-spin" />}
                    </div>
                    <div>
                      <p className="font-semibold text-gray-900">
                        {etapaActual === 'enviando' ? 'Enviando a Hacienda' : 'Esperando respuesta'}
                      </p>
                      <p className="text-sm text-gray-500">{mensajeActual}</p>
                    </div>
                  </div>

                  {/* Mini etapas */}
                  <div className="flex items-center gap-1.5 mt-3">
                    <div className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${etapaActual === 'enviando' || etapaActual === 'esperando' ? 'bg-green-500' : 'bg-gray-200'}`} />
                    <div className={`h-1.5 flex-1 rounded-full transition-all duration-300 ${etapaActual === 'esperando' ? 'bg-blue-500' : 'bg-gray-200'}`} />
                  </div>
                  <div className="flex justify-between mt-1.5 text-[10px] text-gray-400">
                    <span>Enviando</span>
                    <span>Esperando Hacienda</span>
                  </div>
                </div>
              )}

              {/* Tabla de resultados */}
              <div className="border border-gray-200 rounded-lg overflow-hidden">
                <div className="max-h-96 overflow-y-auto">
                  <table className="w-full text-xs">
                    <thead className="sticky top-0 bg-gray-50 border-b border-gray-200">
                      <tr className="text-left text-[10px] font-semibold text-gray-600 tracking-wider">
                        <th className="px-3 py-2.5 w-44">Consecutivo</th>
                        <th className="px-3 py-2.5">Proveedor</th>
                        <th className="px-3 py-2.5 w-36">Condición</th>
                        <th className="px-3 py-2.5 text-center w-40">Estado</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-100">
                      {Object.entries(estadoFacturas).map(([id, data], idx) => {
                        const isComplete = ['aceptado', 'aceptada', 'por_pagar', 'rechazado', 'rechazada', 'error'].includes(data.estado);
                        const isSuccess = ['aceptado', 'aceptada', 'por_pagar'].includes(data.estado);
                        const isError = ['rechazado', 'rechazada', 'error'].includes(data.estado);

                        return (
                          <tr
                            key={id}
                            className={`transition-colors ${
                              isSuccess ? 'hover:bg-green-50/50' : isError ? 'hover:bg-red-50/50' : 'hover:bg-gray-50'
                            }`}
                          >
                            <td className="px-3 py-2">
                              <code className="px-1.5 py-0.5 border border-gray-200 rounded text-[10px] font-mono text-gray-700">
                                {data.numero || '—'}
                              </code>
                            </td>
                            <td className="px-3 py-2">
                              <div className="text-gray-900 font-medium">{data.proveedor}</div>
                              {/* El error va debajo, en varias líneas y solo con lo que dice Hacienda: antes el texto
                                  crudo no se partía, ensanchaba la tabla y tapaba las columnas y los botones */}
                              {isError && data.errorMsg && (
                                <div className="text-[11px] text-red-600 mt-1 whitespace-normal break-words leading-snug">{mensajeLegible(data.errorMsg)}</div>
                              )}
                            </td>
                            <td className="px-3 py-2">
                              {data.pago && (
                                <span className={`inline-flex px-2 py-0.5 rounded-full border text-[10px] font-semibold whitespace-nowrap ${data.pago === 'Contado' ? 'border-sky-300 text-sky-700' : 'border-amber-300 text-amber-800'}`}>{data.pago}</span>
                              )}
                            </td>
                            <td className="px-3 py-2 text-center">
                              {isSuccess ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border border-green-300 text-green-700">
                                  <CheckCircle className="w-3 h-3" />
                                  Aceptada
                                </span>
                              ) : isError ? (
                                <button
                                  onClick={() => toast.error(mensajeLegible(data.errorMsg) || 'Error desconocido', { duration: 15000, style: { maxWidth: '500px' } })}
                                  className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border border-red-300 text-red-700 cursor-pointer hover:bg-red-50"
                                  title="Click para ver detalle"
                                >
                                  <XCircle className="w-3 h-3" />
                                  Error
                                </button>
                              ) : data.estado === 'enviando' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border border-orange-300 text-orange-700">
                                  <Send className="w-3 h-3" />
                                  Enviando...
                                </span>
                              ) : data.estado === 'procesando' ? (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border border-blue-300 text-blue-700">
                                  <Loader className="w-3 h-3 animate-spin" />
                                  Esperando...
                                </span>
                              ) : (
                                <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-semibold border border-gray-300 text-gray-500">
                                  <Clock className="w-3 h-3" />
                                  En cola
                                </span>
                              )}
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              </div>

              {/* Botón cerrar */}
              {fase === 'completado' && (
                <div className="mt-6 flex justify-end">
                  <button
                    onClick={handleClose}
                    className="px-6 py-2.5 text-gray-700 font-medium rounded-lg border border-gray-300 hover:bg-gray-50 transition-colors"
                  >
                    Cerrar
                  </button>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Fase de selección: una fila por factura (término del XML, quién pagó, categorización y lo que se declara)
  const CATS_IVA = [
    { k: '01E', tm: '01', id: 'E', t: 'Acreditable (identificado)' },
    { k: '01N', tm: '01', id: 'N', t: 'Acreditable proporcional (prorrata)' },
    { k: '03E', tm: '03', id: 'E', t: 'Sin crédito (IVA como gasto)' },
  ];
  const catDe = (l) => !l.categorizado ? '' : (l.tipoMovimiento === '02' || l.tipoMovimiento === '03') ? '03E' : `01${l.identificacion === 'N' ? 'N' : 'E'}`;
  const prorrata = prep?.prorrata || 100;
  // Igual que lo que calcula el backend al enviar (ivaLineaService); aquí solo para mostrarlo antes
  const declaracionDe = (f) => {
    const it = items[f.id] || {};
    // % vacío = 100 (igual que el backend)
    const pctNum = it.pct === '' || it.pct === null || it.pct === undefined ? 100 : Number(it.pct);
    const pct = it.tipoPago === 'contado' && it.pagadoPor === 'empleado' ? Math.min(100, Math.max(0, Number.isFinite(pctNum) ? pctNum : 100)) : 100;
    let acrL = 0, ivaL = 0, conN = false, todasCapital = f.lineas.length > 0;
    for (const l of f.lineas) {
      const k = catDe(l) || '01E';
      const base = k === '03E' ? 0 : k === '01N' ? Math.round(l.iva * prorrata) / 100 : l.iva;
      if (k === '01N' && l.iva > 0) conN = true;
      if (l.clase !== 'K') todasCapital = false;
      ivaL += l.iva;
      acrL += base * pct / 100;
    }
    if (!(f.iva > 0)) return { cond: null, acr: 0 };
    // Lo declarado cuadra con el IVA del comprobante: las líneas dan la proporción
    const prop = ivaL > 0 ? Math.min(1, Math.max(0, acrL / ivaL)) : pct / 100;
    const todo = prop >= 0.9999;
    const acr = todo ? f.iva : Math.round(f.iva * prop * 100) / 100;
    const cond = conN ? '05' : acr <= 0 ? '04' : todo && todasCapital ? '03' : todo ? '01' : '02';
    return { cond, acr };
  };
  const COND = {
    '01': ['01 · Genera crédito', 'border-green-400 text-green-700'],
    '02': ['02 · Crédito parcial', 'border-amber-400 text-amber-700'],
    '03': ['03 · Bienes de capital', 'border-green-400 text-green-700'],
    '04': ['04 · Sin crédito', 'border-gray-400 text-gray-700'],
    '05': ['05 · Proporcionalidad', 'border-indigo-400 text-indigo-700'],
  };
  const setItem = (id, cambios) => setItems(prev => ({ ...prev, [id]: { ...prev[id], ...cambios } }));
  const categorizar = async (f, l, k) => {
    const c = CATS_IVA.find(x => x.k === k);
    if (!c) return;
    setGuardandoLinea(l.id);
    try {
      const tarifa = l.tarifaCategoria || (l.tarifa >= 13 ? '13' : l.tarifa >= 4 ? '04' : l.tarifa >= 2 ? '02' : l.tarifa >= 1 ? '01' : 'NS');
      await gastosApi.categorizarLinea(f.id, l.id, { tipo_movimiento: c.tm, identificacion: c.id, clase: l.clase || 'S', tarifa_iva_categoria: tarifa });
      await cargarPreparacion();
    } catch (err) {
      if (!err.response) toast.error('No se pudo categorizar la línea');
    } finally {
      setGuardandoLinea(null);
    }
  };
  const lista = prep?.facturas || [];
  const enviables = lista.filter(f => f.cumplimiento !== 'incumple');
  // «Pagada por» solo aplica a las de contado: sin ninguna de contado, la columna no se muestra
  // 28-oct-2026: «Pagada por» ya no es una columna con dos botones; va debajo de «Contado» como
  // «pagada por la empresa · cambiar». Se decide igual ANTES de enviar: el % que asume la empresa
  // cambia el IVA a acreditar que se declara en el mensaje a Hacienda.
  const hayContado = false;
  const sinCat = enviables.reduce((t, f) => t + f.lineas.filter(l => !l.categorizado).length, 0);
  const facSinCat = enviables.filter(f => f.lineas.some(l => !l.categorizado)).length;
  // Configuración → Facturas recibidas: con líneas sin categorizar no se confirma (o se declaran 01)
  const bloqueaSinCat = (prep?.sinCategorizar || 'bloquea') === 'bloquea';
  const fmt = (n, m) => `${m === 'USD' ? '$' : '₡'}${(Number(n) || 0).toLocaleString(m === 'USD' ? 'en-US' : 'es-CR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg w-full max-w-6xl border-2 border-gray-200 max-h-[90vh] flex flex-col">
        <div className="flex items-center justify-between px-6 py-4 border-b border-green-300">
          <h3 className="text-lg font-semibold text-green-600">Confirmar {count} {count === 1 ? 'Factura' : 'Facturas'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="px-6 py-3 border-b flex items-center gap-3 text-green-700 text-sm">
          <CheckCircle size={18} className="shrink-0" />
          <p>Se enviará mensaje de ACEPTACIÓN a Hacienda. El término viene de cada factura; cámbielo solo si no es correcto.</p>
        </div>

        <form onSubmit={handleSubmit} className="flex flex-col min-h-0">
          <div className="overflow-auto min-h-0">
            {!prep && errorPrep ? (
              <div className="py-10 text-center text-sm text-red-700 space-y-3">
                <p>{errorPrep}</p>
                <button type="button" onClick={() => { setErrorPrep(null); cargarPreparacion().catch(err => setErrorPrep(err.response?.data?.error || 'No se pudieron preparar las facturas')); }}
                  className="px-3 py-1.5 border-2 border-gray-300 text-gray-700 rounded-lg hover:bg-gray-50">Reintentar</button>
              </div>
            ) : !prep ? (
              <div className="py-10 text-center text-sm text-gray-500 flex items-center justify-center gap-2"><Loader size={16} className="animate-spin" /> Preparando facturas...</div>
            ) : (
              <table className="w-full text-sm">
                <thead className="sticky top-0 bg-white">
                  <tr className="border-b text-left text-[11px] font-semibold text-gray-500 uppercase tracking-wider">
                    <th className="px-4 py-2">Factura</th>
                    <th className="px-3 py-2 text-right">Total</th>
                    <th className="px-3 py-2">Término</th>
                    {hayContado && <th className="px-3 py-2">Pagada por</th>}
                    <th className="px-3 py-2">Categoría IVA</th>
                    <th className="px-3 py-2">IVA a Hacienda</th>
                  </tr>
                </thead>
                <tbody>
                  {lista.map(f => {
                    const it = items[f.id] || {};
                    const esNC = ['03', 'NC'].includes(String(f.tipoDocumento));
                    const pend = f.lineas.filter(l => !l.categorizado).length;
                    const d = declaracionDe(f);
                    const bloqueada = f.cumplimiento === 'incumple';
                    const seg = (campo, valor, texto) => (
                      <button type="button" onClick={() => setItem(f.id, { [campo]: valor })}
                        className={`px-2 py-0.5 rounded-md border-2 text-xs ${it[campo] === valor ? 'border-indigo-500 text-indigo-700' : 'border-gray-200 text-gray-500 hover:border-gray-300'}`}>{texto}</button>
                    );
                    return (
                      <Fragment key={f.id}>
                        <tr className="border-b align-top">
                          <td className="px-4 py-2">
                            <div className="font-medium text-gray-900 whitespace-nowrap">{f.proveedor}</div>
                            <div className="font-mono text-[11px] text-gray-500">{f.numero}{esNC && ' · NC'}</div>
                            {bloqueada && <div className="text-[11px] text-red-700 mt-0.5">✕ Incumple una regla: no se confirma</div>}
                          </td>
                          <td className="px-3 py-2 text-right whitespace-nowrap font-medium">{fmt(f.total, f.moneda)}</td>
                          <td className="px-3 py-2">
                            {/* Una sola línea siempre (28-oct-2026): término y quién la pagó. Los datos del empleado se llenan
                                en un recuadro flotante, para que la fila no crezca. «Según la factura» solo si se cambió. */}
                            {(() => {
                              const r2 = (n) => Math.round(n * 100) / 100;
                              const pctEmp = Number.isFinite(Number(it.pct)) ? Number(it.pct) : 100;
                              const esEmpleado = it.tipoPago === 'contado' && !esNC && it.pagadoPor === 'empleado';
                              return (
                                <div className="relative">
                                  <div className="flex items-center gap-1 whitespace-nowrap">
                                    {seg('tipoPago', 'credito', 'Crédito')}{seg('tipoPago', 'contado', 'Contado')}
                                    {it.tipoPago === 'contado' && !esNC && (
                                      <span className="text-[11px] text-gray-500 ml-1.5">
                                        {esEmpleado
                                          ? <>pagó {it.empleadoNombre?.trim() || <span className="text-red-600">un empleado (falta el nombre)</span>} · asume {r2(100 - pctEmp)} %</>
                                          : 'pagó la empresa'}
                                        {' · '}<button type="button" onClick={(e) => { const r = e.currentTarget.getBoundingClientRect(); setPagadorPos({ top: r.bottom + 6, left: Math.max(8, Math.min(r.left - 120, window.innerWidth - 370)) }); setPagadorAbierto(pagadorAbierto === f.id ? null : f.id); }} className="text-indigo-600 hover:underline">cambiar</button>
                                      </span>
                                    )}
                                  </div>
                                  {(it.tipoPago === 'contado') !== (f.condicionVentaXml === '01') && (
                                    <div className="text-[10px] text-amber-700 mt-0.5 whitespace-nowrap">según la factura: {f.condicionVentaXml === '01' ? 'contado' : 'crédito'}</div>
                                  )}
                                  {pagadorAbierto === f.id && (
                                    <div className="fixed z-[60] w-[22rem] bg-white border border-gray-300 rounded-lg shadow-lg p-3 space-y-2" style={pagadorPos || undefined} onClick={e => e.stopPropagation()}>
                                      <div className="flex gap-1">
                                        <button type="button" onClick={() => setItem(f.id, { pagadoPor: 'empresa' })}
                                          className={`px-2 py-0.5 rounded-md border-2 text-xs ${!esEmpleado ? 'border-indigo-500 text-indigo-700' : 'border-gray-200 text-gray-500'}`}>La pagó la empresa</button>
                                        <button type="button" onClick={() => setItem(f.id, { pagadoPor: 'empleado' })}
                                          className={`px-2 py-0.5 rounded-md border-2 text-xs ${esEmpleado ? 'border-indigo-500 text-indigo-700' : 'border-gray-200 text-gray-500'}`}>La pagó un empleado</button>
                                      </div>
                                      {esEmpleado && (<>
                                        <input value={it.empleadoNombre || ''} onChange={e => setItem(f.id, { empleadoNombre: e.target.value })} placeholder="Nombre del empleado" maxLength={150} autoFocus
                                          className="w-full border border-gray-300 rounded px-2 py-1 text-xs" />
                                        {/* Se anota lo que asume EL EMPLEADO; por dentro se guarda lo de la empresa (pct = 100 − empleado),
                                            que es lo que se reintegra y cuyo IVA se acredita en el mensaje a Hacienda. */}
                                        <div className="flex items-center gap-1 whitespace-nowrap">
                                          <span className="text-[11px] text-gray-600">el empleado asume</span>
                                          <input type="number" min="0" max="100" step="0.01" value={it.empleadoPctTexto ?? String(r2(100 - pctEmp))} title="% que asume el empleado"
                                            onChange={e => { const v = e.target.value, n = Number(v);
                                              setItem(f.id, { empleadoPctTexto: v, empleadoMontoTexto: undefined,
                                                ...(v !== '' && Number.isFinite(n) && n >= 0 && n <= 100 ? { pct: String(r2(100 - n)), monto: String(r2(f.total * (100 - n) / 100)) } : {}) }); }}
                                            className="w-14 border border-gray-300 rounded px-1.5 py-0.5 text-xs" />
                                          <span className="text-xs text-gray-500">% =</span>
                                          <input type="number" min="0" step="0.01" value={it.empleadoMontoTexto ?? String(r2(f.total * (100 - pctEmp) / 100))} title="Monto que asume el empleado"
                                            onChange={e => { const v = e.target.value, n = Number(v);
                                              setItem(f.id, { empleadoMontoTexto: v, empleadoPctTexto: undefined,
                                                ...(v !== '' && Number.isFinite(n) && f.total > 0 && n >= 0 && n <= f.total ? { pct: String(r2(100 - n / f.total * 100)), monto: String(r2(f.total - n)) } : {}) }); }}
                                            className="w-24 border border-gray-300 rounded px-1.5 py-0.5 text-xs" />
                                        </div>
                                        <div className="text-[11px] text-gray-500">La empresa reintegra el {r2(pctEmp)} % y acredita ese IVA.</div>
                                      </>)}
                                      <div className="flex justify-end">
                                        <button type="button" onClick={() => setPagadorAbierto(null)} className="px-3 py-0.5 border border-gray-300 rounded-md text-xs text-gray-700 hover:bg-gray-50">Listo</button>
                                      </div>
                                    </div>
                                  )}
                                </div>
                              );
                            })()}
                          </td>

                          <td className="px-3 py-2">
                            {f.lineas.length === 0 ? <span className="text-xs text-gray-400">Sin líneas</span> : (
                              <button type="button" onClick={() => setAbierta(abierta === f.id ? null : f.id)}
                                className={`px-2 py-0.5 rounded-full border text-xs whitespace-nowrap ${pend ? 'border-amber-400 text-amber-700' : 'border-green-400 text-green-700'}`}>
                                {pend ? `${pend} sin categorizar · Categorizar` : `✓ ${f.lineas.length} de ${f.lineas.length} · Ver`} {abierta === f.id ? '▴' : '▾'}
                              </button>
                            )}
                          </td>
                          <td className="px-3 py-2">
                            {d.cond ? (
                              <>
                                <span className={`px-2 py-0.5 rounded-full border text-xs whitespace-nowrap ${COND[d.cond][1]}`}>{COND[d.cond][0]}</span>
                                <div className="text-[11px] text-gray-500 mt-0.5 whitespace-nowrap">IVA a acreditar {fmt(d.acr, f.moneda)}</div>
                              </>
                            ) : <span className="text-xs text-gray-500">Sin IVA</span>}
                          </td>
                        </tr>
                        {abierta === f.id && (
                          <tr className="border-b">
                            <td colSpan={hayContado ? 6 : 5} className="px-4 pb-3">
                              <div className="border rounded-lg divide-y">
                                {f.lineas.map(l => (
                                  <div key={l.id} className="flex flex-wrap items-center justify-between gap-2 px-3 py-2">
                                    <span className="text-sm text-gray-800 min-w-0">{l.detalle} <span className="text-gray-500">· IVA {fmt(l.iva, f.moneda)}{l.tarifa ? ` (${l.tarifa}%)` : ''}</span></span>
                                    <select value={catDe(l)} disabled={guardandoLinea === l.id} onChange={e => categorizar(f, l, e.target.value)}
                                      className={`border rounded-md px-2 py-1 text-xs ${l.categorizado ? 'border-gray-300' : 'border-amber-400'}`}>
                                      <option value="" disabled>Sin categorizar</option>
                                      {CATS_IVA.map(c => <option key={c.k} value={c.k}>{c.t}</option>)}
                                    </select>
                                  </div>
                                ))}
                              </div>
                            </td>
                          </tr>
                        )}
                      </Fragment>
                    );
                  })}
                </tbody>
              </table>
            )}
          </div>

          {sinCat > 0 && (
            <div className={`mx-6 mt-3 border rounded-lg px-4 py-2.5 text-sm ${bloqueaSinCat ? 'border-red-400 text-red-700' : 'border-amber-400 text-amber-800'}`}>
              <strong>Hay {sinCat} {sinCat === 1 ? 'línea' : 'líneas'} sin categorizar en {facSinCat} {facSinCat === 1 ? 'factura' : 'facturas'}.</strong>{' '}
              {bloqueaSinCat
                ? 'Categorícelas aquí mismo para poder confirmar.'
                : <>Según la configuración de la empresa se declaran como <strong>01 · crédito total del IVA</strong>. Categorícelas aquí mismo si no es así.</>}
            </div>
          )}

          <div className="flex justify-end gap-3 px-6 py-4">
            <button type="button" onClick={onClose} className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50">Cancelar</button>
            <button type="submit" disabled={loading || !prep || enviables.length === 0 || (bloqueaSinCat && sinCat > 0)}
              className="flex items-center gap-2 px-4 py-2 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50">
              {loading ? <Loader className="animate-spin" size={18} /> : <Check size={18} />}
              {loading ? 'Procesando...' : `Confirmar ${count} ${count === 1 ? 'Factura' : 'Facturas'}`}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Detalle de la revisión de una factura recibida con las reglas de la empresa (lo guardado en la factura)
function CumplimientoModal({ factura, onClose, onConfirmar, onPasado }) {
  const [data, setData] = useState(null);
  // F4: cerrar sin volver a cargar la revisión cada vez que el padre se redibuja
  const cerrarRef = useRef(onClose);
  cerrarRef.current = onClose;
  const [pasar, setPasar] = useState(false);
  useEffect(() => {
    gastosApi.getCumplimiento(factura.id)
      .then(r => setData(r.data?.data || { resultados: [] }))
      .catch(err => { if (!err.response) toast.error('No se pudo cargar la revisión'); cerrarRef.current(); });
  }, [factura.id]);
  const resultados = data?.resultados || [];
  const bloquea = resultados.some(r => !r.ok && r.nivel === 'bloquea');
  const icono = (r) => r.ok ? ['✓', 'text-green-600'] : r.nivel === 'bloquea' ? ['✕', 'text-red-600'] : ['!', 'text-amber-600'];
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div className={`bg-white rounded-lg w-full max-w-3xl border-2 ${bloquea ? 'border-red-300' : 'border-gray-200'}`} onClick={e => e.stopPropagation()}>
        <div className="flex items-start justify-between px-6 py-4 border-b">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-gray-900">Revisión de la factura</h3>
            <p className="text-sm text-gray-500 truncate">{factura.proveedorNombre} · <span className="font-mono">{factura.numeroConsecutivo}</span></p>
          </div>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600"><X size={20} /></button>
        </div>
        <div className="px-6 py-4">
          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-2">Lo que debe traer según Hacienda y las reglas de la empresa</p>
          {!data ? (
            <div className="py-6 text-center text-sm text-gray-500 flex items-center justify-center gap-2"><Loader size={16} className="animate-spin" /> Revisando...</div>
          ) : (
            <ul className="divide-y border rounded-lg">
              {resultados.map(r => {
                const [ic, cl] = icono(r);
                return (
                  <li key={r.codigo} className="flex items-start gap-3 px-3 py-2">
                    <span className={`font-bold w-4 text-center ${cl}`}>{ic}</span>
                    <div className="min-w-0 flex-1">
                      <div className={`text-sm ${!r.ok && r.nivel === 'bloquea' ? 'text-red-700 font-medium' : 'text-gray-900'}`}>{r.nombre}</div>
                      <div className="text-xs text-gray-500">{r.detalle}</div>
                    </div>
                    {!r.ok && <span className={`text-[11px] px-2 py-0.5 rounded-full border whitespace-nowrap ${r.nivel === 'bloquea' ? 'border-red-300 text-red-700' : 'border-amber-300 text-amber-700'}`}>{r.nivel === 'bloquea' ? 'Bloquea' : 'Advierte'}</span>}
                  </li>
                );
              })}
              {resultados.length === 0 && <li className="px-3 py-3 text-sm text-gray-500">No hay reglas activas.</li>}
            </ul>
          )}
          {bloquea && <p className="text-xs text-red-700 mt-3">Esta factura incumple una regla que bloquea: no se puede confirmar así. Hay que pedirle al proveedor la nota de crédito y la factura corregida.</p>}
        </div>
        <div className="flex justify-end gap-3 px-6 py-4 border-t">
          <button onClick={onClose} className="px-4 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50">Cerrar</button>
          {data && bloquea && (
            <button onClick={() => setPasar(true)} className="flex items-center gap-2 px-4 py-2 border-2 border-red-500 text-red-600 rounded-lg hover:bg-red-50"><Send size={16} /> Pasar a pendientes de rechazo</button>
          )}
          {data && !bloquea && (
            <button onClick={onConfirmar} className="flex items-center gap-2 px-4 py-2 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50"><Check size={18} /> Confirmar</button>
          )}
        </div>
        {pasar && <CorreoProveedorModal facturaId={factura.id} modo="pasar" onClose={() => setPasar(false)} onListo={() => { setPasar(false); onPasado?.(); }} />}
      </div>
    </div>
  );
}

// Menu Pago Modal - Choose single or multiple payment
function MenuPagoModal({ factura, onClose, onPagarSolo, onPagarMultiple }) {
  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg w-full max-w-md mx-4">
        <div className="flex items-center justify-between px-6 py-4 border-b">
          <h3 className="text-lg font-semibold">¿Cómo deseas registrar el pago?</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <X size={20} />
          </button>
        </div>

        <div className="p-6 space-y-4">
          <p className="text-sm text-gray-600">
            Puedes pagar solo esta factura o varias del mismo emisor a la vez.
          </p>

          <div className="space-y-3">
            <button
              onClick={onPagarSolo}
              className="w-full px-4 py-3 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 font-medium transition"
            >
              Pagar esta factura
            </button>
            <button
              onClick={onPagarMultiple}
              className="w-full px-4 py-3 border-2 border-blue-600 text-blue-600 rounded-lg hover:bg-blue-50 font-medium transition"
            >
              Pagar múltiples facturas del mismo emisor
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

// Apply Payment Modal Component - Supports single and multiple invoices
function PagoModal({ factura, facturas = [], isMultiple = false, bancos = [], onClose, onSuccess }) {
  // Use facturas array if multiple, otherwise single factura
  const invoices = isMultiple ? facturas : [factura];
  const totalPendiente = invoices.reduce((sum, f) => sum + (f.saldoPendiente || f.totalComprobante || 0), 0);
  const moneda = invoices[0]?.moneda || 'CRC';
  const proveedorNombre = invoices[0]?.proveedorNombre || '';

  const [formData, setFormData] = useState({
    fechaPago: dayjs().format('YYYY-MM-DD'),
    monto: isMultiple ? '' : (factura?.saldoPendiente?.toFixed(2) || ''),
    medioPago: 'transferencia',
    numeroComprobante: '',
    banco: ''
  });
  const [file, setFile] = useState(null);
  const [loading, setLoading] = useState(false);

  // Montos individuales por FE (solo en modo múltiple) — default = saldo completo
  const [montosPorFE, setMontosPorFE] = useState(() => {
    if (!isMultiple) return {};
    const m = {};
    invoices.forEach(f => { m[f.id] = (parseFloat(f.saldoPendiente) || parseFloat(f.totalComprobante) || 0).toFixed(2); });
    return m;
  });
  const setMontoFE = (id, val) => setMontosPorFE(prev => ({ ...prev, [id]: val }));
  const montoTotalCalc = isMultiple
    ? invoices.reduce((s, f) => s + (parseFloat(montosPorFE[f.id]) || 0), 0)
    : (parseFloat(formData.monto) || 0);

  const handleSubmit = async (e) => {
    e.preventDefault();

    try {
      setLoading(true);

      let notifInfo = null;
      if (isMultiple) {
        // Validar montos individuales antes de enviar
        for (const f of invoices) {
          const m = parseFloat(montosPorFE[f.id]) || 0;
          const saldo = parseFloat(f.saldoPendiente) || parseFloat(f.totalComprobante) || 0;
          if (m < 0) {
            toast.error(`Monto negativo en factura ${f.numeroConsecutivo}`);
            setLoading(false);
            return;
          }
          if (m > saldo + 0.01) {
            toast.error(`El monto excede el saldo de la factura ${f.numeroConsecutivo}`);
            setLoading(false);
            return;
          }
        }
        if (montoTotalCalc <= 0) {
          toast.error('Ingrese al menos un monto válido por factura');
          setLoading(false);
          return;
        }

        // Solo FE con monto > 0
        const montosArr = invoices
          .map(f => ({ facturaId: f.id, monto: parseFloat(montosPorFE[f.id]) || 0 }))
          .filter(x => x.monto > 0);

        // Multiple payment
        const data = new FormData();
        data.append('facturaIds', JSON.stringify(montosArr.map(x => x.facturaId)));
        data.append('montosPorFactura', JSON.stringify(montosArr));
        data.append('fechaPago', formData.fechaPago);
        data.append('montoTotal', montoTotalCalc.toFixed(2));
        data.append('moneda', moneda);
        data.append('medioPago', formData.medioPago);
        data.append('numeroComprobante', formData.numeroComprobante);
        data.append('banco', formData.banco);
        if (file) data.append('comprobante', file);

        const res = await gastosApi.aplicarPagoMultiple(data);
        toast.success(`Pago aplicado a ${montosArr.length} factura${montosArr.length !== 1 ? 's' : ''}`);
        const d = res?.data?.data || {};
        notifInfo = {
          parentId: d.parentId || null,
          pagoId: null,
          proveedorId: d.proveedorId || invoices[0]?.proveedorId,
          proveedorNombre: invoices[0]?.proveedorNombre || '',
          moneda,
          montoTotal: montoTotalCalc,
          fechaPago: formData.fechaPago,
          medioPago: formData.medioPago,
          banco: formData.banco,
          numeroComprobante: formData.numeroComprobante,
          comprobanteFilename: file?.name || null,
          facturasCount: montosArr.length
        };
      } else {
        // Single payment
        const data = new FormData();
        data.append('fechaPago', formData.fechaPago);
        data.append('monto', formData.monto);
        data.append('moneda', moneda);
        data.append('medioPago', formData.medioPago);
        data.append('numeroComprobante', formData.numeroComprobante);
        data.append('banco', formData.banco);
        if (file) data.append('comprobante', file);

        const res = await gastosApi.aplicarPago(factura.id, data);
        toast.success('Pago aplicado correctamente');
        const d = res?.data?.data || {};
        notifInfo = {
          parentId: null,
          pagoId: d.pago?.id || null,
          proveedorId: factura?.proveedorId,
          proveedorNombre: factura?.proveedorNombre || '',
          moneda,
          montoTotal: parseFloat(formData.monto) || 0,
          fechaPago: formData.fechaPago,
          medioPago: formData.medioPago,
          banco: formData.banco,
          numeroComprobante: formData.numeroComprobante,
          comprobanteFilename: file?.name || null,
          facturasCount: 1
        };
      }

      onSuccess(notifInfo);
    } catch (error) {
      toast.error(error.response?.data?.error || 'Error al aplicar pago');
    } finally {
      setLoading(false);
    }
  };

  const formatMoney = (amount, currency = 'CRC') => {
    const num = parseFloat(amount) || 0;
    return currency === 'USD'
      ? `$${num.toLocaleString('en-US', { minimumFractionDigits: 2 })}`
      : `₡${num.toLocaleString('es-CR', { minimumFractionDigits: 2 })}`;
  };

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
      <div className="bg-white rounded-xl w-full max-w-2xl mx-4 shadow-2xl">
        {/* Header */}
        <div className="flex items-center justify-between px-6 py-4 border-b bg-gradient-to-r from-green-600 to-green-700 rounded-t-xl">
          <h3 className="text-lg font-semibold text-white">
            {isMultiple ? 'Pago a Proveedor (Múltiple)' : 'Pago a Proveedor'}
          </h3>
          <button onClick={onClose} className="text-white/80 hover:text-white">
            <X size={20} />
          </button>
        </div>

        {/* Invoice Summary */}
        <div className="px-6 py-4 bg-gray-50 border-b">
          <div className="flex items-start justify-between">
            <div>
              <p className="font-semibold text-gray-900">{proveedorNombre}</p>
              {!isMultiple && (
                <p className="text-sm text-gray-500 font-mono mt-0.5">{factura?.numeroConsecutivo}</p>
              )}
            </div>
            <div className="text-right">
              <p className="text-xs text-gray-500">Total a Pagar</p>
              <p className="text-2xl font-bold text-green-700 ">
                {formatMoney(isMultiple ? montoTotalCalc : factura?.saldoPendiente, moneda)}
              </p>
              {isMultiple && Math.abs(montoTotalCalc - totalPendiente) > 0.01 && (
                <p className="text-[11px] text-gray-500 mt-0.5">
                  de {formatMoney(totalPendiente, moneda)} pendiente
                </p>
              )}
            </div>
          </div>

          {/* Multiple invoices table — montos editables por FE */}
          {isMultiple && invoices.length > 0 && (
            <div className="mt-3 border rounded-lg overflow-hidden">
              <div className="px-3 py-1.5 bg-gray-50 border-b text-[11px] text-gray-500">
                Editá el monto de cada FE si pagás solo parte
              </div>
              <table className="w-full text-sm">
                <thead className="bg-gray-100">
                  <tr>
                    <th className="text-left px-3 py-2 font-medium text-gray-600">Factura</th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600">Saldo</th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600 w-40">A pagar</th>
                    <th className="text-right px-3 py-2 font-medium text-gray-600 w-28">Saldo final</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {invoices.map(f => {
                    const saldo = parseFloat(f.saldoPendiente) || parseFloat(f.totalComprobante) || 0;
                    const aplicado = parseFloat(montosPorFE[f.id]) || 0;
                    const saldoFinal = Math.max(0, Math.round((saldo - aplicado) * 100) / 100);
                    const quedaSaldada = aplicado > 0 && saldoFinal <= 0.01;
                    const excede = aplicado > saldo + 0.01;
                    return (
                      <tr key={f.id} className="hover:bg-gray-50">
                        <td className="px-3 py-2 font-mono text-xs">{f.numeroConsecutivo}</td>
                        <td className="px-3 py-2 text-right text-gray-600">{formatMoney(saldo, moneda)}</td>
                        <td className="px-3 py-2 text-right">
                          <div className="flex items-center justify-end gap-1">
                            <input
                              type="number"
                              step="0.01"
                              min="0"
                              max={saldo}
                              value={montosPorFE[f.id]}
                              onChange={(e) => setMontoFE(f.id, e.target.value)}
                              className={`w-28 border rounded px-2 py-1 text-right text-sm font-mono focus:ring-2 focus:ring-green-500 focus:border-green-500 ${excede ? 'border-red-400 bg-red-50' : 'border-gray-300'}`}
                            />
                            <button
                              type="button"
                              onClick={() => setMontoFE(f.id, saldo.toFixed(2))}
                              className="text-[10px] px-1.5 py-1 border border-gray-300 rounded text-gray-600 hover:bg-green-50 hover:border-green-400 hover:text-green-700"
                              title="Pagar total"
                            >
                              100%
                            </button>
                          </div>
                        </td>
                        <td className={`px-3 py-2 text-right font-semibold ${quedaSaldada ? 'text-emerald-600' : aplicado > 0 ? 'text-orange-600' : 'text-gray-300'}`}>
                          {aplicado > 0 ? (quedaSaldada ? '✓ Saldada' : formatMoney(saldoFinal, moneda)) : '-'}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
                <tfoot className="bg-green-50">
                  <tr>
                    <td colSpan="2" className="px-3 py-2 font-medium text-gray-700">
                      {invoices.length} facturas · {invoices.filter(f => (parseFloat(montosPorFE[f.id]) || 0) > 0).length} con monto
                    </td>
                    <td className="px-3 py-2 text-right font-bold text-green-700 ">
                      {formatMoney(montoTotalCalc, moneda)}
                    </td>
                    <td></td>
                  </tr>
                </tfoot>
              </table>
            </div>
          )}

          {/* Single invoice details */}
          {!isMultiple && factura && (
            <div className="mt-3 flex gap-6 text-sm">
              <div>
                <span className="text-gray-500">Total factura:</span>
                <span className="ml-2 text-sm font-medium">{formatMoney(factura.totalComprobante, moneda)}</span>
              </div>
              <div>
                <span className="text-gray-500">Ya pagado:</span>
                <span className="ml-2 text-sm font-medium">{formatMoney(factura.totalPagado || 0, moneda)}</span>
              </div>
            </div>
          )}
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Fecha de Pago *
              </label>
              <input
                type="date"
                value={formData.fechaPago}
                onChange={(e) => setFormData({ ...formData, fechaPago: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                required
              />
            </div>
            {!isMultiple && (
              <>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Monto a Pagar *
                  </label>
                  <input
                    type="number"
                    step="0.01"
                    max={factura?.saldoPendiente}
                    value={formData.monto}
                    onChange={(e) => setFormData({ ...formData, monto: e.target.value })}
                    className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
                    required
                  />
                  <div className="flex gap-1 mt-1">
                    {[25, 50, 75, 100].map(pct => (
                      <button
                        key={pct}
                        type="button"
                        onClick={() => setFormData({ ...formData, monto: ((factura?.saldoPendiente || 0) * pct / 100).toFixed(2) })}
                        className="flex-1 text-xs py-1 border border-gray-300 rounded hover:bg-green-50 hover:border-green-400 hover:text-green-700 transition-colors font-medium text-gray-600"
                      >
                        {pct}%
                      </button>
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Saldo Restante
                  </label>
                  <div className={`w-full border rounded-lg px-3 py-2  font-semibold ${
                    (factura?.saldoPendiente - (parseFloat(formData.monto) || 0)) <= 0.01
                      ? 'bg-green-50 text-green-700 border-green-200'
                      : 'bg-yellow-50 text-yellow-700 border-yellow-200'
                  }`}>
                    {formatMoney(Math.max(0, (factura?.saldoPendiente || 0) - (parseFloat(formData.monto) || 0)), moneda)}
                  </div>
                </div>
              </>
            )}
            <div className={isMultiple ? 'col-span-2' : ''}>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Medio de Pago
              </label>
              <select
                value={formData.medioPago}
                onChange={(e) => setFormData({ ...formData, medioPago: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="transferencia">Transferencia</option>
                <option value="efectivo">Efectivo</option>
                <option value="cheque">Cheque</option>
                <option value="tarjeta">Tarjeta</option>
                <option value="otro">Otro</option>
              </select>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-4 mt-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                No. Comprobante
              </label>
              <input
                type="text"
                value={formData.numeroComprobante}
                onChange={(e) => setFormData({ ...formData, numeroComprobante: e.target.value })}
                placeholder="Referencia"
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Banco
              </label>
              <select
                value={formData.banco}
                onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                className="w-full border rounded-lg px-3 py-2 focus:ring-2 focus:ring-green-500 focus:border-green-500"
              >
                <option value="">Seleccionar banco</option>
                {bancos.map(banco => (
                  <option key={banco.id} value={banco.nombre}>
                    {banco.nombre}
                  </option>
                ))}
              </select>
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">
                Comprobante *
              </label>
              <input
                type="file"
                accept=".pdf,.jpg,.jpeg,.png"
                onChange={(e) => setFile(e.target.files?.[0] || null)}
                className="w-full border rounded-lg px-2 py-1.5 text-sm file:mr-2 file:py-1 file:px-2 file:rounded file:border-0 file:text-xs file:bg-gray-100 file:text-gray-700 hover:file:bg-gray-200"
                required
              />
              {!file && (
                <p className="text-xs text-amber-600 mt-1">Debe adjuntar el comprobante de pago</p>
              )}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3 mt-6 pt-4 border-t">
            <button
              type="button"
              onClick={onClose}
              className="px-5 py-2 border-2 border-gray-300 text-gray-600 rounded-lg hover:bg-gray-50 font-medium"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading || !file || (isMultiple && montoTotalCalc <= 0)}
              className="px-6 py-2 border-2 border-green-600 text-green-600 rounded-lg hover:bg-green-50 disabled:opacity-50 font-medium flex items-center gap-2"
            >
              {loading ? (
                <>
                  <span className="animate-spin h-4 w-4 border-2 border-green-600 border-t-transparent rounded-full"></span>
                  Aplicando...
                </>
              ) : (
                <>
                  <Check size={18} />
                  {isMultiple
                    ? `Pagar ${formatMoney(montoTotalCalc, moneda)}`
                    : 'Pagar Factura'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

// Factura Preview Modal - Shows actual PDF from email attachment
export function FacturaPreviewModal({ factura, loading, onClose }) {
  const [pdfUrl, setPdfUrl] = useState(null);
  const [pdfLoading, setPdfLoading] = useState(false);
  const [pdfError, setPdfError] = useState(false);

  // Load PDF when factura changes and has PDF
  useEffect(() => {
    const loadPdf = async () => {
      if (!factura?.hasPdf || !factura?.id) {
        setPdfUrl(null);
        return;
      }

      try {
        setPdfLoading(true);
        setPdfError(false);

        // Fetch PDF as blob (includes auth headers via axios)
        const response = await gastosApi.downloadPDF(factura.id);
        const blob = new Blob([response.data], { type: 'application/pdf' });
        const url = URL.createObjectURL(blob);
        setPdfUrl(url);
      } catch (error) {
        console.error('Error loading PDF:', error);
        setPdfError(true);
      } finally {
        setPdfLoading(false);
      }
    };

    loadPdf();

    // Cleanup blob URL on unmount
    return () => {
      if (pdfUrl) {
        URL.revokeObjectURL(pdfUrl);
      }
    };
  }, [factura?.id, factura?.hasPdf]);

  const isLoading = loading || pdfLoading;

  const displayNum = factura?.fecNumeroFacturaProveedor || factura?.numeroConsecutivo;

  return (
    <DraggableModal
      title={factura ? `${factura.proveedorNombre} - ${displayNum}` : 'Factura'}
      onClose={onClose}
    >
      <div className="w-full h-full bg-gray-200">
        {isLoading ? (
          <div className="flex items-center justify-center h-full">
            <div className="text-center">
              <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-primary-600 mx-auto mb-3"></div>
              <p className="text-gray-600">Cargando factura...</p>
            </div>
          </div>
        ) : pdfUrl && !pdfError ? (
          <iframe
            src={pdfUrl}
            className="w-full h-full"
            title={`Factura ${displayNum}`}
          />
        ) : (
          <div className="flex items-center justify-center h-full">
            <div className="text-center p-8 bg-white rounded-lg shadow-md max-w-md">
              <FileText size={48} className="mx-auto text-gray-400 mb-4" />
              <h3 className="text-lg font-medium text-gray-900 mb-2">PDF no disponible</h3>
              <p className="text-sm text-gray-500 mb-4">
                Esta factura no tiene un PDF asociado.
                {factura?.clave && ' Solo se importó el XML de la factura electrónica.'}
              </p>
              {factura && (
                <div className="text-left bg-gray-50 rounded-lg p-4 text-sm">
                  <div className="grid grid-cols-2 gap-2">
                    <span className="text-gray-500">Proveedor:</span>
                    <span className="font-medium">{factura.proveedorNombre}</span>
                    <span className="text-gray-500">Factura:</span>
                    <span className="font-medium">{displayNum}</span>
                    <span className="text-gray-500">Fecha:</span>
                    <span className="font-medium">{dayjs(factura.fechaEmision).format('DD/MM/YYYY')}</span>
                    <span className="text-gray-500">Total:</span>
                    <span className="font-medium">
                      {factura.moneda === 'USD' ? '$' : '₡'}
                      {parseFloat(factura.totalComprobante).toLocaleString('es-CR', { minimumFractionDigits: 2 })}
                    </span>
                  </div>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
    </DraggableModal>
  );
}

// Mensaje de Hacienda sin el ruido del formato («codigo, mensaje, fila, columna», comillas dobles, códigos):
// «-29, ""El comprobante … clave."", 0, 0» → «El comprobante … clave.»
function mensajeLegible(msg) {
  const t = String(msg || '');
  const partes = [...t.matchAll(/""(.+?)""/gs)].map(m => m[1].trim());
  return partes.length ? partes.join(' ') : t.replace(/\s+/g, ' ').trim();
}

// La FE del proveedor la rechazó Hacienda (respuesta que vino con el correo o consultada): mientras siga por
// confirmar no se puede aceptar — Hacienda no la tiene registrada y el Mensaje Receptor rebota (-29).
/** Las facturas cuyo mensaje ya salió a Hacienda y esperan respuesta (se reabre desde la píldora). */
function EnHaciendaModal({ facturas, onMinimizar }) {
  const dinero = (n, m) => `${m === 'USD' ? '$' : '₡'}${(parseFloat(n) || 0).toLocaleString('es-CR', { minimumFractionDigits: 2 })}`;
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center px-4 py-8 bg-gray-900/60" onMouseDown={(e) => { if (e.target === e.currentTarget) onMinimizar(); }}>
      <div className="w-full max-w-4xl bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center gap-3">
          <div className="p-2 rounded-lg border border-blue-300"><Loader className="w-6 h-6 text-blue-600 animate-spin" /></div>
          <div className="flex-1">
            <h3 className="text-lg font-semibold text-gray-900">En Hacienda</h3>
            <p className="text-sm text-gray-500">El mensaje ya se envió; se actualizan solas cuando Hacienda conteste, aunque cierre o refresque la pantalla.</p>
          </div>
          <BotonMinimizar onClick={onMinimizar} />
        </div>
        <div className="max-h-[60vh] overflow-auto">
          <table className="w-full text-sm">
            <thead><tr className="text-left text-[11px] uppercase tracking-wider text-gray-500 border-b border-gray-200">
              <th className="px-5 py-2.5 font-semibold">Consecutivo</th><th className="px-5 py-2.5 font-semibold">Proveedor</th><th className="px-5 py-2.5 font-semibold text-right">Total</th><th className="px-5 py-2.5 font-semibold text-right">Estado</th>
            </tr></thead>
            <tbody>{facturas.map(f => (
              <tr key={f.id} className="border-b border-gray-100">
                <td className="px-5 py-2 font-mono text-xs">{f.numeroConsecutivo}</td>
                <td className="px-5 py-2">{f.proveedorNombre}</td>
                <td className="px-5 py-2 text-right tabular-nums">{dinero(f.totalComprobante, f.moneda)}</td>
                <td className="px-5 py-2 text-right"><span className="inline-flex items-center gap-1.5 text-xs font-semibold border border-blue-300 text-blue-700 rounded-full px-2.5 py-0.5"><Loader className="w-3 h-3 animate-spin" /> Esperando a Hacienda</span></td>
              </tr>
            ))}</tbody>
          </table>
        </div>
      </div>
    </div>
  );
}

/** Nuestro mensaje (confirmación o rechazo) ya salió y Hacienda todavía no contesta. */
function enHacienda(f) {
  return f?.estado === 'por_confirmar' && f?.estadoHacienda === 'procesando';
}

function rechazadaPorHacienda(f) {
  return !!f && f.estado === 'por_confirmar' && f.haciendaEstadoOriginal === 'rechazado';
}

// Motivos de Hacienda uno por uno, con su código: los del formato de Hacienda (-53, ""texto"") o los ya
// guardados («[-53] texto | [-37] texto»). Advertencia = Hacienda acepta la factura igual: -37 (dirección
// del emisor) y lo que redacta como aviso («Estimado obligado tributario… favor actualizar sus datos»).
function motivosHacienda(msg) {
  const t = String(msg || '').replace(/&#13;/g, '');
  let items = [...t.matchAll(/(-?\d+)\s*,\s*""(.+?)""/gs)].map(m => ({ codigo: m[1], texto: m[2].replace(/\s+/g, ' ').trim() }));
  if (!items.length) {
    items = t.split(' | ').map(x => x.trim()).filter(Boolean).map(x => {
      const m = x.match(/^\[(-?\d+)\]\s*(.*)$/s);
      return m ? { codigo: m[1], texto: m[2] } : { codigo: null, texto: x };
    });
  }
  if (!items.length) items = [{ codigo: null, texto: 'Sin detalle' }];
  return items.map(m => ({ ...m, advertencia: m.codigo === '-37' || /^Estimado obligado tributario/i.test(m.texto) }));
}
